import React from "react";
import { buildFleetPerspectiveIcons } from "../utils/fleetTimelinePerspective.js";
import { getPlanningMutationActivityMessage } from "../utils/planningMutationActivity.js";
import "./FleetContext.css";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { AlertTriangle, ArrowRightLeft, Ban, ChevronDown, CircleCheck, ClipboardList, Expand, Fuel, Keyboard, Lock, MapPin, Minimize2, Moon, Pause, RotateCcw, Route, Sailboat, ShieldCheck, Smartphone, Sun } from "lucide-react";
import { DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME } from "../utils/driverPortalPublicationStyles.js";
import {
  ActionButton,
  EmptyState,
  HoverCard,
  Pill,
  StatusBadge,
  Tooltip,
  joinClasses,
  DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
  useToast,
} from "./ResultsPrimitives.jsx";
import RouteMetaChipRow, {
  getRouteMetaChipDisplayLabel,
  resolveRouteChipHoverCardContent,
  resolveRouteChipHoverCardTitle,
  resolveRouteMetaChipPresentation,
} from "./RouteMetaChipRow.jsx";
import {
  ASPIRATOR_SELECT_OPTIONS,
  ORDER_LOADING_PRIORITY_OPTIONS,
  PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS,
  buildLoadingClientCorrespondenceIndex,
  buildUpdatePayload,
  canEditSingleDeliveryWindowFields,
  createEditableOrderDraft,
  formatQuantityTons,
  getOrderDraftErrors,
  hasDraftErrors,
  hasOrderDraftChanges,
  resolveEndOfDayLoadHoverCardDetailsFromSegment,
} from "../hooks/useDataManagerOrdersUtils.js";
import { EndOfDayLoadHoverCardDetails } from "./EndOfDayLoadHoverCardDetails.jsx";
import { TruckLoadPreview, useTruckLoadMatch } from "./TruckLoadView.jsx";
import { isLunchBreakSegment } from "../utils/lunchBreakTimeline.js";
import { getLocalTodayDispatchDate, isShortcutTypingTarget } from "../utils/dispatchDayCalendarUtils.js";
import EndOfDayLoadSunsetIcon from "./EndOfDayLoadSunsetIcon.jsx";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { ACCESS_PERMISSION_KEYS } from "../lib/accessControl.js";
import {
  buildCompartmentFillImpact,
  buildCompartmentFillIndicatorLabel,
  normalizeCompartmentFillPercent,
  summarizeCompartmentFillSegments,
} from "../lib/productCompartmentFillRules.js";
import {
  applyFleetSegmentComparisonDurations,
  buildAspiratorMissionDisplaySegments,
  buildExpandedTimelineGroupMemberSegments,
  buildFleetDisplayedSegmentTimings,
  buildFleetGroupedComparisonSummary,
  buildFleetSegmentComparisonCacheKey,
  buildFleetSequenceDisplayItems,
  buildSequenceTruckLoadByKey,
  buildSameSiteSequenceHoverMetaChips,
  buildFleetTimelineDisplaySegments,
  buildSegmentPositionData,
  buildSequenceTableRenderItems,
  buildSameSiteSequenceDisplayOverrides,
  buildTimelineRenderItems,
  formatTimelineClockLabel,
  getTimelineTickLabelStyle,
  getSameSiteSequenceDisplayDistanceLabel,
  getSameSiteSequenceLegacyTravelDurationMinutes,
  getSameSiteSequenceDisplayTravelDurationMinutes,
  getRowActiveTrip,
  getRouteSequencePrimaryTitle,
  getTruckFilterMetaLabel,
  getTruckHoverCardKpiItems,
  isMergedOperationalMissionSegment,
  isPreviousDayPreloadDisplaySegment,
  resolveAssignmentBackendDurationBreakdown,
  resolveAssignmentBackendTotalDurationMinutes,
  resolveDisplayedTimelineFrontendDurationBreakdown,
  resolveDisplayedTimelineFrontendTotalDurationMinutes,
  resolveFrontendCanonicalTotalDurationMinutes,
  resolveDisplayedTimelineSpanLabel,
  resolveDisplayedTimelineSpanMinutes,
  resolveGroupedHoverRouteSegmentIndex,
  resolveMergedSameAddressQuantityTons,
  joinGroupedSegmentCompartmentLabel,
  resolveTerminalMergedComparisonSourceSegments,
  resolveSameSiteSequenceDisplayedTravelDuration,
  resolveTimelineGroupSyncTarget,
  resolveTimelineHourTicks,
  resolveTimelineBounds,
  resolveAssignmentDurationLabel,
  buildGroupedSegmentOrderHoverEntries,
  buildGroupedSegmentOrdersHoverCardDetails,
  buildGroupedSegmentQuantityHoverCardDetails,
  buildMergedSameAddressQuantityHoverCard,
  buildFleetSequenceTruckIncidentChip,
  resolveFleetRowTruckIncidents,
  resolveGroupedSegmentQuantityTons,
  shouldUseFleetSegmentComparisonCurrentDuration,
  shouldSyncDisplayedTravelDurationChip,
  shouldDisplaySameSiteDurationChips,
} from "../utils/fleetTimelinePresentation.js";
import { withPendingTruckSourceValues } from "../utils/truckSourceChangePresentation.js";
import {
  buildFleetSegmentComparisonDelta,
  resolveFleetGroupedServiceDurationMinutes,
  formatFleetSegmentComparisonDurationLabel,
  resolveFleetSegmentCurrentHereDurationMinutes,
  resolveFleetSegmentDisplayedTotalDurationMinutes,
  resolveFleetSegmentDurationMinutesFromChips,
  resolveFleetSegmentComparisonLabel,
} from "../utils/fleetSegmentComparisonPresentation.js";
import {
  resolveGroupedSegmentProductLabels,
  buildDeliveryOrderLocationInfoItems,
  buildSegmentActionFeedbackLines,
  buildSegmentHoverCardTitle,
  buildSegmentInfoItems,
  formatAssignableTargetCount,
  getSegmentActionPopoverWidthClass,
  shouldHideSupersededSegmentConfirmButton,
  getSegmentActionSurfaceClassName,
  getSegmentApprovalBlockingReason,
  getSegmentClassName,
  getSegmentHoverCardAccentColor,
  getSegmentHoverSurfaceClassName,
  getSequenceTableMarkerStyle,
  isLinkedCommandCompanionSegment,
  normalizeSegmentMetaValue,
  resolveEndOfDayLoadDisplayMode,
  resolveEndOfDayLoadVisualMode,
  formatSegmentHoverCardAddress,
  resolveSegmentPrimaryAddress,
  resolveSegmentPrimaryTitle,
  resolveSegmentRouteSummary,
  resolveSegmentActionMetaLabel,
  resolveSegmentWorkOrderLabel,
  resolvePmSegmentContext,
} from "../utils/fleetSegmentPresentation.js";
import {
  getEndOfDayLoadModeIconName,
  getEndOfDayLoadModeLongLabel,
  getEndOfDayLoadModeShortLabel,
  normalizeEndOfDayLoadDateValue,
  normalizeEndOfDayLoadModeForDisplay,
} from "../utils/endOfDayLoadModeLabels.js";
import { getFleetProductBandsBackground, getFleetProductTone } from "../utils/cropColors.js";
import { buildFleetTimelineData, formatDurationShort, formatManualBufferReasonLabel, MANUAL_BUFFER_REASON_OPTIONS, resolveVisibleRouteSequenceItems } from "../utils/routeModelsData.js";
import { readCurrentDispatchDate, resolveNextDispatchDateValue } from "../utils/dispatchDateUrls.js";
import {
  buildGroupedRouteSequenceDurationHoverCard,
  buildRouteSequenceDisplayedTravelTitle,
  enrichRouteSequenceDurationChipMetadata,
  buildRouteSequenceOpeningIndicator,
  buildRouteSequenceOpeningVisibilityByItemKey,
  buildRouteSequenceClosingVisibilityByItemKey,
  filterRouteSequenceOpeningIndicators,
  formatRouteSequenceHoursIndicatorTime,
  formatSequenceGroupDistanceLabel as buildRouteSequenceGroupDistanceLabel,
  resolveRouteSequenceDisplayedTravelLabel,
  shouldDisplayRouteSequenceOpeningForItem,
  shouldDisplayRouteSequenceOpeningForSegment,
  shouldDisplayRouteSequenceClosingForSegment,
} from "../utils/routeSequenceHoverCards.js";

const FLEET_TIMELINE_TRUCK_COLUMN_GRID = "min-[1120px]:grid-cols-[155px_minmax(0,1fr)]";

const EMBEDDED_SEGMENT_META_GRID_CLASS_NAME = "lg:max-w-full lg:w-[24.75rem] lg:grid lg:grid-cols-[6.75rem_3.75rem_5.75rem_4.25rem_3.25rem] lg:items-center lg:gap-x-1 lg:gap-y-0";

const FLEET_COMPARISON_DURATION_CHIP_GRID_CLASS_NAME = "grid auto-cols-max grid-flow-col items-center justify-end gap-1";

const FLEET_SEGMENT_HOVER_CARD_MAX_WIDTH = 420;

const FLEET_SEGMENT_HOVER_CARD_SURFACE_CLASS_NAME = "fleet-context-surface fleet-context-hover-surface grid w-full min-w-0 max-w-full gap-1 rounded-[4px] border px-2.5 py-1.75 overflow-hidden";

const FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS = {
  compactTitle: true,
  maxWidth: "calc(100vw - 1.5rem)",
  tight: true,
  flat: true,
  width: FLEET_SEGMENT_HOVER_CARD_MAX_WIDTH,
};

const GROUPED_SEGMENT_METRICS_PRIMARY_GRID_CLASS_NAME = "grid w-full min-w-0 max-w-full grid-cols-2 grid-rows-2 gap-x-1 gap-y-1";

const GROUPED_SEGMENT_METRICS_PRIMARY_CHIP_COLUMN_CLASS_NAME_MAP = {
  dist: "",
  dur: "",
  qty: "",
  svc: "",
};

function resolveFleetHoverCardPrimaryMetricsGridClassName(chipCount = 0) {
  const normalizedCount = Math.max(0, Number(chipCount) || 0);

  if (normalizedCount <= 1) {
    return "grid w-full min-w-0 max-w-full grid-cols-1 gap-x-1";
  }

  if (normalizedCount === 2) {
    return "grid w-full min-w-0 max-w-full grid-cols-2 grid-rows-1 gap-x-1";
  }

  return GROUPED_SEGMENT_METRICS_PRIMARY_GRID_CLASS_NAME;
}

const EMBEDDED_SEGMENT_META_CHIP_COLUMN_CLASS_NAME_MAP = {
  grain: "lg:col-start-1",
  qty: "lg:col-start-2",
  dist: "lg:col-start-3",
  meunerie: "lg:col-start-1 lg:row-start-2",
  svc: "lg:col-start-2 lg:row-start-2",
  dur: "lg:col-start-3 lg:row-start-2",
  manual_buffer: "lg:col-start-4 lg:row-start-2",
  aspirator_time: "lg:col-start-5 lg:row-start-2",
};

const FLEET_SEQUENCE_RELATION_PLURAL_LABELS = Object.freeze({
  Client: "Clients",
  Fournisseur: "Fournisseurs",
});

const FLEET_TIMELINE_ZOOM_OPTIONS = [
  { key: "100", label: "100 %", factor: 1 },
  { key: "125", label: "125 %", factor: 1.25 },
  { key: "150", label: "150 %", factor: 1.5 },
  { key: "200", label: "200 %", factor: 2 },
];

const DEFAULT_FLEET_TIMELINE_ZOOM_KEY = "100";

const INITIAL_PRELOAD_FLEET_RIBBON_LABEL = "Veille";

const INITIAL_PRELOAD_FLEET_RIBBON_TITLE = "Chargement réalisé la veille, hors plage chauffeur.";

function resolveEndOfDayLoadLabelSource(source = null) {
  if (!source) {
    return null;
  }

  if (
    source?.endOfDayLoadTargetDispatchDate
    || source?.target_dispatch_date
    || source?.targetDispatchDate
    || source?.end_of_day_load_target_dispatch_date
  ) {
    return source;
  }

  const memberSegments = Array.isArray(source?.memberSegments) ? source.memberSegments.filter(Boolean) : [];
  return memberSegments.find((segment) => (
    segment?.endOfDayLoadTargetDispatchDate
    || segment?.target_dispatch_date
    || segment?.targetDispatchDate
    || segment?.end_of_day_load_target_dispatch_date
  )) || source;
}

function resolveEndOfDayLoadLabelOptions(source = null) {
  const labelSource = resolveEndOfDayLoadLabelSource(source);
  return {
    sourceDispatchDate: labelSource?.endOfDayLoadSourceDispatchDate
      || labelSource?.source_dispatch_date
      || labelSource?.sourceDispatchDate
      || labelSource?.end_of_day_load_source_dispatch_date
      || "",
    targetDispatchDate: labelSource?.endOfDayLoadTargetDispatchDate
      || labelSource?.target_dispatch_date
      || labelSource?.targetDispatchDate
      || labelSource?.end_of_day_load_target_dispatch_date
      || "",
  };
}

function getEndOfDayLoadFleetRibbonTitle(visualMode = "next_day", labelOptions = {}) {
  return getEndOfDayLoadModeLongLabel(visualMode, labelOptions);
}

function parseSegmentTimestampClockMinutes(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return (parsedDate.getHours() * 60) + parsedDate.getMinutes() + (parsedDate.getSeconds() / 60) + (parsedDate.getMilliseconds() / 60000);
}

function normalizeSegmentClockMinutes(value) {
  if (value == null || value === "") {
    return null;
  }

  const minutes = Number(value);
  return Number.isFinite(minutes) ? minutes : null;
}

function resolveSegmentBoundaryMinutes(segment, timestampKey, minutesKey) {
  return normalizeSegmentClockMinutes(segment?.[minutesKey])
    ?? parseSegmentTimestampClockMinutes(segment?.[timestampKey]);
}

function formatSegmentBoundaryLabel(segment, timestampKey, minutesKey) {
  const continuousMinutes = normalizeSegmentClockMinutes(segment?.[minutesKey]);
  if (continuousMinutes != null) {
    return formatTimelineClockLabel(continuousMinutes);
  }

  const timestampMinutes = parseSegmentTimestampClockMinutes(segment?.[timestampKey]);
  return timestampMinutes != null ? formatTimelineClockLabel(timestampMinutes) : "";
}

function isEndOfDayLoadTimelineSegment(segment) {
  // Operational PM loads keep their clocks even when presented with the PM context.
  return !segment?.isPmOperationalLoading
    && Boolean(segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload);
}

function formatPreviousDaySequenceStartLabel(segment) {
  if (isEndOfDayLoadTimelineSegment(segment)) {
    return getEndOfDayLoadModeShortLabel(
      resolveEndOfDayLoadDisplayMode(segment),
      resolveEndOfDayLoadLabelOptions(segment),
    );
  }

  return isPreviousDayPreloadDisplaySegment(segment) ? "Veille" : "";
}

function formatSegmentBusinessStartLabel(segment) {
  return formatPreviousDaySequenceStartLabel(segment)
    || formatSegmentBoundaryLabel(segment, "displayedSegmentStartAt", "displayedSegmentStartMinutes")
    || formatSegmentBoundaryLabel(segment, "segmentStartAt", "segmentStartMinutes")
    || formatSegmentBoundaryLabel(segment, "visualSegmentStartAt", "visualSegmentStartMinutes")
    || formatSegmentBoundaryLabel(segment, "scheduledAt", "scheduledAtMinutes");
}

function resolveSegmentBusinessStartMinutes(segment) {
  return resolveSegmentBoundaryMinutes(segment, "displayedSegmentStartAt", "displayedSegmentStartMinutes")
    ?? resolveSegmentBoundaryMinutes(segment, "segmentStartAt", "segmentStartMinutes")
    ?? resolveSegmentBoundaryMinutes(segment, "visualSegmentStartAt", "visualSegmentStartMinutes")
    ?? resolveSegmentBoundaryMinutes(segment, "scheduledAt", "scheduledAtMinutes");
}

function resolveSegmentBusinessEndMinutes(segment) {
  if (segment?.isTerminalRouteMerged === true) {
    const endCandidates = [
      resolveSegmentBoundaryMinutes(segment, "terminalMergedStopEndAt", "terminalMergedStopEndMinutes"),
      resolveSegmentBoundaryMinutes(segment, "displayedSegmentEndAt", "displayedSegmentEndMinutes"),
      resolveSegmentBoundaryMinutes(segment, "visualSegmentEndAt", "visualSegmentEndMinutes"),
      resolveSegmentBoundaryMinutes(segment, "segmentEndAt", "segmentEndMinutes"),
    ].filter((value) => value != null);

    return endCandidates.length ? Math.max(...endCandidates) : null;
  }

  return resolveSegmentBoundaryMinutes(segment, "displayedSegmentEndAt", "displayedSegmentEndMinutes")
    ?? resolveSegmentBoundaryMinutes(segment, "visualSegmentEndAt", "visualSegmentEndMinutes")
    ?? resolveSegmentBoundaryMinutes(segment, "segmentEndAt", "segmentEndMinutes");
}

function formatDisplayedSegmentEndLabel(segment) {
  const displayedEndMinutes = normalizeSegmentClockMinutes(segment?.displayedSegmentEndMinutes);
  if (displayedEndMinutes != null) {
    return formatTimelineClockLabel(displayedEndMinutes);
  }

  return formatSegmentBoundaryLabel(segment, "displayedSegmentEndAt", "displayedSegmentEndMinutes");
}

function formatSegmentBusinessEndLabel(segment) {
  if (isEndOfDayLoadTimelineSegment(segment)) {
    return "";
  }

  if (segment?.isTerminalRouteMerged === true) {
    const businessEndMinutes = resolveSegmentBusinessEndMinutes(segment);
    return businessEndMinutes != null ? formatTimelineClockLabel(businessEndMinutes) : "";
  }

  return formatDisplayedSegmentEndLabel(segment)
    || formatSegmentBoundaryLabel(segment, "visualSegmentEndAt", "visualSegmentEndMinutes")
    || formatSegmentBoundaryLabel(segment, "segmentEndAt", "segmentEndMinutes");
}

function formatGroupedSegmentBusinessEndLabel(groupItem, memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  if (isEndOfDayLoadTimelineSegment(groupItem) || resolvedMemberSegments.some(isEndOfDayLoadTimelineSegment)) {
    return "";
  }

  const firstMemberSegment = resolvedMemberSegments[0] || null;
  const lastMemberSegment = resolvedMemberSegments[resolvedMemberSegments.length - 1] || firstMemberSegment;
  const segmentWithLatestBusinessEnd = resolvedMemberSegments.reduce((latestSegment, currentSegment) => {
    const currentEndMinutes = resolveSegmentBusinessEndMinutes(currentSegment);
    const latestEndMinutes = resolveSegmentBusinessEndMinutes(latestSegment);

    if (currentEndMinutes == null) {
      return latestSegment;
    }

    if (latestEndMinutes == null || currentEndMinutes >= latestEndMinutes) {
      return currentSegment;
    }

    return latestSegment;
  }, null);

  if (!firstMemberSegment) {
    return "";
  }

  return formatSegmentBusinessEndLabel(segmentWithLatestBusinessEnd)
    || formatSegmentBusinessEndLabel(lastMemberSegment)
    || formatSegmentBusinessEndLabel(firstMemberSegment);
}

function getSegmentStartLabel() {
  return "Début";
}

const SEGMENT_STATUS_TONE_CLASS_NAMES = {
  manual: "border-[color-mix(in_srgb,rgb(29,78,216)_92%,white)] bg-white text-[color-mix(in_srgb,rgb(29,78,216)_92%,white)]",
  "protection-reordered": "border-[color-mix(in_srgb,rgb(109,40,217)_92%,white)] bg-white text-[color-mix(in_srgb,rgb(109,40,217)_92%,white)]",
  success: "border-[color-mix(in_srgb,var(--color-accent-strong)_92%,white)] bg-white text-[color-mix(in_srgb,var(--color-accent-strong)_92%,white)]",
  warning: "border-[color-mix(in_srgb,var(--color-warning-ink)_92%,white)] bg-white text-[color-mix(in_srgb,var(--color-warning-ink)_92%,white)]",
};

/** Fond opaque et contour noir fin : les statuts restent lisibles sur les deux couleurs de segment. */
const SEGMENT_STATUS_TONE_EMPHASIS_CLASS_NAMES = {
  manual: "border-white bg-[color-mix(in_srgb,rgb(29,78,216)_92%,white)] text-white",
  "protection-reordered": "border-white bg-[color-mix(in_srgb,rgb(109,40,217)_92%,white)] text-white",
  // Mêmes couleurs que l'étiquette « PM » du Gantt.
  pm: "border-white bg-gray-800 text-yellow-400",
  success: "border-white bg-[color-mix(in_srgb,var(--color-accent)_76%,white)] text-white",
  warning: "border-white bg-[color-mix(in_srgb,var(--color-warning-ink)_92%,white)] text-white",
};

const TIMELINE_SEQUENCE_BADGE_TONE_CLASS_NAMES = {
  default: "fleet-timeline-sequence-badge border-transparent bg-transparent text-current",
  manual: "border-[rgba(37,99,235,0.18)] bg-[rgba(239,246,255,0.96)] text-[rgb(29,78,216)]",
  "protection-reordered": "border-[rgba(109,40,217,0.2)] bg-[rgba(245,243,255,0.98)] text-[rgb(109,40,217)]",
  success: "border-[rgba(15,92,87,0.16)] bg-[rgba(238,247,245,0.96)] text-(--color-legacy-teal)",
  warning: "border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)]",
};

const TIMELINE_PRIORITY_BADGE_TONE_CLASS_NAMES = {
  P1: "border-[rgba(194,65,12,0.2)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)]",
  P2: "border-[rgba(180,83,9,0.2)] bg-[rgba(255,251,235,0.96)] text-[rgb(180,83,9)]",
  P3: "border-[rgba(202,138,4,0.18)] bg-[rgba(254,252,232,0.96)] text-[rgb(133,77,14)]",
};

const TIMELINE_CONTEXT_BADGE_TONE_CLASS_NAMES = {
  aspiration: "border-[rgba(234,88,12,0.22)] bg-[rgba(255,247,237,0.98)] text-[rgb(194,65,12)]",
  expansion: "border-[rgba(192,38,211,0.24)] bg-[rgba(253,244,255,0.98)] text-[rgb(162,28,175)]",
  buffer: "border-[rgba(217,119,6,0.28)] bg-[rgba(254,243,199,0.98)] text-[rgb(146,64,14)] shadow-[0_2px_6px_rgba(217,119,6,0.18)]",
  ferry: "border-[rgba(15,118,110,0.24)] bg-[rgba(240,253,250,0.98)] text-[rgb(15,118,110)] shadow-[0_2px_6px_rgba(13,148,136,0.16)]",
  info: "border-[rgba(15,92,87,0.18)] bg-[rgba(238,247,245,0.96)] text-(--color-legacy-teal)",
  neutral: "border-[rgba(148,163,184,0.2)] bg-white/94 text-content-copy",
  "product-change": "border-[rgba(13,148,136,0.24)] bg-[rgba(240,253,250,0.98)] text-[rgb(15,118,110)] shadow-[0_2px_6px_rgba(13,148,136,0.16)]",
  schedule: "border-[rgba(220,38,38,0.22)] bg-[rgba(254,242,242,0.98)] text-[rgb(185,28,28)]",
  warning: "border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)]",
};

function normalizeFleetSearch(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getMergedSameAddressHoverSurfaceClassName() {
  return "border-[rgba(126,136,107,0.28)] bg-[linear-gradient(135deg,rgba(244,248,246,0.996)_0%,rgba(228,237,233,0.982)_42%,rgba(247,243,227,0.982)_58%,rgba(252,249,240,0.996)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]";
}

function getGroupedSegmentSurfaceClassName(stopType, isInitialPreload = false, isEndOfDayLoad = false, endOfDayLoadVisualMode = "") {
  return stopType === "mixed"
    ? "border-[rgba(148,163,184,0.16)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.94))]"
    : getSegmentHoverSurfaceClassName(stopType, isInitialPreload, isEndOfDayLoad, endOfDayLoadVisualMode);
}

function buildGoogleMapsDirectionsUrl(originAddress = "", destinationAddress = "") {
  const normalizedOriginAddress = normalizeSegmentMetaValue(originAddress);
  const normalizedDestinationAddress = normalizeSegmentMetaValue(destinationAddress);

  if (!normalizedOriginAddress || !normalizedDestinationAddress) {
    return "";
  }

  if (normalizeFleetSearch(normalizedOriginAddress) === normalizeFleetSearch(normalizedDestinationAddress)) {
    return "";
  }

  const searchParams = new URLSearchParams({
    api: "1",
    origin: normalizedOriginAddress,
    destination: normalizedDestinationAddress,
    travelmode: "driving",
  });

  return `https://www.google.com/maps/dir/?${searchParams.toString()}`;
}

function buildGoogleMapsSearchUrl(address = "") {
  const normalizedAddress = normalizeSegmentMetaValue(address);

  if (!normalizedAddress) {
    return "";
  }

  const searchParams = new URLSearchParams({
    api: "1",
    query: normalizedAddress,
  });

  return `https://www.google.com/maps/search/?${searchParams.toString()}`;
}

function resolveSegmentAddressMapsUrl(segment, address = "") {
  const normalizedAddress = normalizeSegmentMetaValue(address);

  if (!normalizedAddress) {
    return "";
  }

  return buildSegmentRouteDirectionsUrl(segment) || buildGoogleMapsSearchUrl(normalizedAddress);
}

function buildSegmentRouteDirectionsUrl(segment) {
  const originAddress = resolveSegmentPrimaryAddress(segment);
  const destinationAddress = resolveSegmentDestinationAddress(segment);

  return buildGoogleMapsDirectionsUrl(originAddress, destinationAddress);
}

function buildGroupedRouteDirectionsUrl(groupItem) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  const firstMemberSegment = memberSegments[0] || null;
  const originAddress = resolveSegmentPrimaryAddress(firstMemberSegment)
    || normalizeSegmentMetaValue(groupItem?.toAddress)
    || normalizeSegmentMetaValue(firstMemberSegment?.fromAddress);
  const destinationAddress = resolveGroupedSegmentDestinationAddress(groupItem);

  return buildGoogleMapsDirectionsUrl(originAddress, destinationAddress);
}

function resolveGroupedSegmentDestinationAddress(groupItem) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  const firstMemberSegment = memberSegments[0] || null;
  const lastMemberSegment = memberSegments[memberSegments.length - 1] || firstMemberSegment;
  const primaryAddress = resolveSegmentPrimaryAddress(firstMemberSegment) || normalizeSegmentMetaValue(groupItem?.toAddress);
  const outboundDestinationAddress = normalizeSegmentMetaValue(lastMemberSegment?.routeToAddress || firstMemberSegment?.routeToAddress);

  if (outboundDestinationAddress && normalizeFleetSearch(outboundDestinationAddress) !== normalizeFleetSearch(primaryAddress)) {
    return outboundDestinationAddress;
  }

  const fallbackDestinationAddress = resolveSegmentDestinationAddress(lastMemberSegment) || resolveSegmentDestinationAddress(firstMemberSegment);
  return fallbackDestinationAddress && normalizeFleetSearch(fallbackDestinationAddress) !== normalizeFleetSearch(primaryAddress)
    ? fallbackDestinationAddress
    : "";
}

function isSelfReferentialRouteSummary(summary = "", fromLabel = "") {
  const normalizedSummary = normalizeFleetSearch(summary);
  const normalizedFrom = normalizeFleetSearch(fromLabel);
  if (!normalizedSummary) {
    return true;
  }
  if (normalizedFrom && normalizedSummary === normalizedFrom) {
    return true;
  }

  const summaryParts = String(summary)
    .split("→")
    .map((part) => normalizeFleetSearch(part));
  return summaryParts.length >= 2
    && Boolean(normalizedFrom)
    && summaryParts.every((part) => part === normalizedFrom);
}

function resolveGroupedSegmentRouteSummary(groupItem) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  const firstMemberSegment = memberSegments[0] || null;
  const lastMemberSegment = memberSegments[memberSegments.length - 1] || firstMemberSegment;
  const fromLabel = normalizeSegmentMetaValue(groupItem?.toLabel)
    || normalizeSegmentMetaValue(firstMemberSegment?.toLabel)
    || normalizeSegmentMetaValue(resolveSegmentPrimaryTitle(firstMemberSegment));
  const toLabel = normalizeSegmentMetaValue(lastMemberSegment?.routeToLabel)
    || normalizeSegmentMetaValue(firstMemberSegment?.routeToLabel)
    || normalizeSegmentMetaValue(lastMemberSegment?.toLabel);
  const fallbackRouteSummary = normalizeSegmentMetaValue(resolveSegmentRouteSummary(firstMemberSegment))
    || normalizeSegmentMetaValue(resolveSegmentRouteSummary(lastMemberSegment));

  if (fromLabel && toLabel && normalizeFleetSearch(fromLabel) !== normalizeFleetSearch(toLabel)) {
    return `${fromLabel} → ${toLabel}`;
  }

  return fallbackRouteSummary && !isSelfReferentialRouteSummary(fallbackRouteSummary, fromLabel)
    ? fallbackRouteSummary
    : "";
}

function getFleetSequenceDisplayKey(segment, fallbackIndex = 0) {
  return `${segment?.assignmentId || "assignment"}::${segment?.tripId || "trip"}::${segment?.globalSequence || fallbackIndex + 1}`;
}

function getSequenceOperationLabel(item, fallbackItem = null) {
  const itemPrimaryTitle = item ? getRouteSequencePrimaryTitle(item) : "";
  if (itemPrimaryTitle && itemPrimaryTitle !== "Arrêt") {
    return itemPrimaryTitle;
  }

  const fallbackPrimaryTitle = fallbackItem ? getRouteSequencePrimaryTitle(fallbackItem) : "";
  if (fallbackPrimaryTitle && fallbackPrimaryTitle !== "Arrêt") {
    return fallbackPrimaryTitle;
  }

  return "Arrêt";
}

function normalizeSequenceRelationValue(value = "") {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function resolveSequenceCounterparty(item = null, fallbackItem = null) {
  const segment = item?.segment || item;
  const fallbackSegment = fallbackItem?.segment || fallbackItem;
  const orderDetails = segment?.businessStep?.orderDetails || fallbackSegment?.businessStep?.orderDetails || null;
  const stopType = normalizeSequenceRelationValue(segment?.stopType || fallbackSegment?.stopType || "");
  const operationLabelKey = normalizeSequenceRelationValue(getSequenceOperationLabel(item, fallbackItem));

  if (stopType === "delivery") {
    const supplierLabel = normalizeSegmentMetaValue(orderDetails?.loadingClientName || "");
    if (!supplierLabel || normalizeSequenceRelationValue(supplierLabel) === operationLabelKey) {
      return null;
    }

    return {
      label: "Fournisseur",
      value: supplierLabel,
    };
  }

  const clientLabel = normalizeSegmentMetaValue(
    orderDetails?.deliveryClientName
    || segment?.operationOwnerLabel
    || fallbackSegment?.operationOwnerLabel
    || "",
  );

  if (!clientLabel || normalizeSequenceRelationValue(clientLabel) === operationLabelKey) {
    return null;
  }

  return {
    label: "Client",
    value: clientLabel,
  };
}

function buildSequenceCounterpartyInfoItems(items = [], fallbackItem = null) {
  const valuesByLabel = new Map();

  items.forEach((item) => {
    const counterparty = resolveSequenceCounterparty(item, fallbackItem);
    if (!counterparty) {
      return;
    }

    const valueKey = normalizeSequenceRelationValue(counterparty.value);
    if (!valueKey) {
      return;
    }

    if (!valuesByLabel.has(counterparty.label)) {
      valuesByLabel.set(counterparty.label, new Map());
    }

    const values = valuesByLabel.get(counterparty.label);
    if (!values.has(valueKey)) {
      values.set(valueKey, counterparty.value);
    }
  });

  return Array.from(valuesByLabel.entries()).map(([label, values]) => {
    const labels = Array.from(values.values());
    const pluralLabel = FLEET_SEQUENCE_RELATION_PLURAL_LABELS[label] || `${label}s`;
    const value = labels.length > 2
      ? `${labels.slice(0, 2).join(" · ")} · +${labels.length - 2}`
      : labels.join(" · ");

    return {
      key: `${label}:${Array.from(values.keys()).join("|")}`,
      label: labels.length === 1 ? label : pluralLabel,
      value,
    };
  });
}

function isFleetSequenceLoadingStop(segment = null) {
  return normalizeSequenceRelationValue(segment?.stopType) === "loading";
}

function isFleetSequenceDeliveryStop(segment = null) {
  return normalizeSequenceRelationValue(segment?.stopType) === "delivery";
}

function shouldInlineGroupedCounterpartyByOrder(segment = null) {
  return isFleetSequenceLoadingStop(segment) || isFleetSequenceDeliveryStop(segment);
}

function resolveSequenceInlineCounterpartyLabel(segment = null, fallbackSegment = null) {
  const counterparty = resolveSequenceCounterparty(segment, fallbackSegment);
  if (!counterparty?.value) {
    return "";
  }

  if (counterparty.label === "Client" || counterparty.label === "Fournisseur") {
    return counterparty.value;
  }

  return "";
}

// Camion sans compartiments (ex. dompeur) : l'optimiseur le traite comme un seul C1 de la capacité du camion.
function withVirtualTruckCompartment(compartments = [], capacityTons = null) {
  if (Array.isArray(compartments) && compartments.length) {
    return compartments;
  }
  return Number(capacityTons) > 0 ? [{ position: 1, capacity_tons: Number(capacityTons) }] : [];
}

// Quantité par compartiment de l'étape (chargée, ou livrée si l'étape ne charge rien) et part de la capacité.
function buildCompartmentQuantityRows(segments = [], compartments = [], activePositions = new Set(), { includeEmpty = false } = {}) {
  const allocationsOf = (segment) => segment?.compartmentAllocations || segment?.compartment_allocations || [];
  const hasLoading = segments.some((segment) => segment?.stopType === "loading" && allocationsOf(segment).length);
  const quantityByPosition = new Map();
  segments
    .filter((segment) => !hasLoading || segment?.stopType === "loading")
    .forEach((segment) => allocationsOf(segment).forEach((allocation) => {
      const position = Number(allocation?.position);
      const quantityTons = Number(allocation?.quantity_tons ?? allocation?.quantityTons);
      if (position > 0 && quantityTons > 0) {
        quantityByPosition.set(position, (quantityByPosition.get(position) || 0) + quantityTons);
      }
    }));

  return compartments
    .map(({ capacityTons, position }) => {
      const quantityTons = quantityByPosition.get(position);
      return {
        capacityTons,
        isUsed: activePositions.has(position) || quantityByPosition.has(position),
        occupiedPercent: quantityTons > 0 && capacityTons > 0 ? Math.round((quantityTons / capacityTons) * 100) : null,
        position,
        quantityTons,
      };
    })
    .filter((row) => includeEmpty || row.isUsed);
}

function CompartmentQuantityHoverCardContent({ rows = [] }) {
  const formatTons = (value) => (value > 0 ? `${formatQuantityTons(value)} t` : "—");
  const usedRows = rows.filter((row) => row.isUsed);
  const totalQuantityTons = usedRows.reduce((total, row) => total + (row.quantityTons || 0), 0);
  const totalCapacityTons = usedRows.reduce((total, row) => total + (row.capacityTons || 0), 0);
  const cells = [
    ...rows.map((row) => [`C${row.position}`, formatTons(row.quantityTons), formatTons(row.capacityTons), row.isUsed ? (row.occupiedPercent == null ? "—" : `${row.occupiedPercent} %`) : "Vide"]),
    [usedRows.length < rows.length ? "Total utilisé" : "Total", formatTons(totalQuantityTons), formatTons(totalCapacityTons), totalQuantityTons > 0 && totalCapacityTons > 0 ? `${Math.round((totalQuantityTons / totalCapacityTons) * 100)} %` : "—"],
  ];

  return (
    <div className="grid grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-0.5 text-[0.68rem] leading-4 text-content-copy tabular-nums">
      {["Compartiment", "Quantité", "Capacité", "Occupé"].map((column) => (
        <span key={column} className="text-[0.6rem] font-semibold uppercase tracking-[0.04em] text-content-muted">{column}</span>
      ))}
      {cells.map((cellValues, rowIndex) => cellValues.map((value, columnIndex) => (
        <span
          key={`${rowIndex}:${columnIndex}`}
          className={joinClasses([
            columnIndex ? "text-right" : "",
            rows[rowIndex]?.isUsed === false ? "text-content-muted" : "",
            rowIndex === cells.length - 1 ? "border-t border-[rgba(148,163,184,0.24)] pt-0.5 font-semibold" : "",
          ])}
        >
          {value}
        </span>
      )))}
    </div>
  );
}

function FleetSequenceCompartmentPill({ chip = null, compartmentProfile = null, segments = [], showEmptyCompartments = false }) {
  if (!chip) {
    return <span className="text-[0.73rem] text-content-muted">—</span>;
  }

  const chipPresentation = resolveRouteMetaChipPresentation(chip);
  const displayLabel = getRouteMetaChipDisplayLabel(chip);

  if (Array.isArray(compartmentProfile)) {
    const labels = String(displayLabel || "").split(/[,\s]+/).filter(Boolean);
    const activePositions = new Set(labels.map((label) => /^C0*(\d+)$/i.exec(label)?.[1]).filter(Boolean).map(Number));
    const compartments = compartmentProfile
      .map((compartment, index) => ({
        key: compartment.compartment_id || index,
        position: Number(compartment.position) || index + 1,
        capacityTons: Number(compartment.capacity_tons) || 0,
      }))
      .sort((left, right) => left.position - right.position);
    const activeColor = chip.stopType === "loading" ? "#5985b7" : "#b68a34";
    const quantityRows = buildCompartmentQuantityRows((Array.isArray(segments) ? segments : []).filter(Boolean), compartments, activePositions, { includeEmpty: showEmptyCompartments });

    return (
      <HoverCard
        title="Compartiments"
        content={<CompartmentQuantityHoverCardContent rows={quantityRows} />}
        disabled={!quantityRows.length}
        placement="top"
        delay={120}
        accentColor={activeColor}
        referenceAsChild
        flat
        compactTitle
        tight
      >
        <div className="flex w-fit min-w-0 max-w-full flex-col gap-1">
          {compartments.length ? (
            <div className="flex max-w-full flex-wrap gap-x-[3px] gap-y-1">
              {compartments.map(({ key, position }) => (
                <span key={key} className="flex w-[12px] shrink-0 flex-col items-center gap-1">
                  <span
                    aria-hidden="true"
                    className="h-[15px] w-[12px] rounded-none"
                    style={{ backgroundColor: activePositions.has(position) ? activeColor : "#ebe8e2" }}
                  />
                  <span className="min-h-[12px] whitespace-nowrap text-center text-[10px] leading-[12px] font-normal text-content-copy">
                    {activePositions.has(position) ? position : ""}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[0.73rem] leading-tight font-normal text-content-copy [overflow-wrap:anywhere]">{labels.join(" ")}</span>
          )}
        </div>
      </HoverCard>
    );
  }

  return (
    <Pill
      tone={chipPresentation.tone}
      className={joinClasses([
        "min-h-5 w-full max-w-full min-w-0 gap-0.5 overflow-hidden px-0.5 py-px text-[0.55rem] leading-none font-semibold shadow-none",
        DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
        chipPresentation.className || "",
      ])}
      style={chipPresentation.style}
      title={chip.title || undefined}
    >
      <span className="truncate">{displayLabel}</span>
    </Pill>
  );
}

function resolveFleetSequenceMeunerieClientKey(segment) {
  return (segment?.loading_client_name || segment?.clientName || segment?.client_name || segment?.order_id || "").trim();
}

function resolveFleetSequenceMeunerieExtraMinutes(segment) {
  if (!segment || segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
    return 0;
  }

  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  if (stopType !== "loading") {
    return 0;
  }

  const directExtraMinutes = Number(segment?.meunerie_loading_extra_minutes);
  if (Number.isFinite(directExtraMinutes) && directExtraMinutes > 0) {
    return Math.round(directExtraMinutes);
  }

  const meunerieChip = (Array.isArray(segment?.chips) ? segment.chips : []).find(
    (chip) => chip?.kind === "meunerie" || chip?.key === "meunerie",
  );
  if (typeof meunerieChip?.label === "string") {
    const parsedExtraMinutes = Number(meunerieChip.label.replace(/[^\d]/g, ""));
    if (Number.isFinite(parsedExtraMinutes) && parsedExtraMinutes > 0) {
      return Math.round(parsedExtraMinutes);
    }
  }

  return 0;
}

function resolveFleetSequenceAspiratorServiceKey(segment) {
  return (segment?.operationOrderId || segment?.orderLabel || segment?.order_id || segment?.operationDeliveryId || segment?.delivery_id || segment?.key || "").trim();
}

function resolveFleetSequenceAspiratorServiceMinutes(segment) {
  if (!segment) {
    return 0;
  }

  if (segment?.isAspiratorMissionMerged === true) {
    const missionMinutes = Number(segment?.aspiratorServiceMinutes ?? segment?.aspirator_service_minutes);
    return Number.isFinite(missionMinutes) && missionMinutes > 0 ? Math.round(missionMinutes) : 0;
  }

  if (isMergedOperationalMissionSegment(segment) && Array.isArray(segment?.mergedMemberSegments)) {
    return resolveFleetSequenceGroupAspiratorServiceMinutes(segment.mergedMemberSegments);
  }

  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  if (stopType !== "delivery") {
    return 0;
  }

  const directMinutes = Number(segment?.aspiratorServiceMinutes ?? segment?.aspirator_service_minutes);
  if (Number.isFinite(directMinutes) && directMinutes > 0) {
    return Math.round(directMinutes);
  }

  const aspiratorChip = (Array.isArray(segment?.chips) ? segment.chips : []).find(
    (chip) => chip?.kind === "aspirator_time" || chip?.key === "aspirator_time",
  );
  if (typeof aspiratorChip?.label === "string") {
    const parsedMinutes = Number(aspiratorChip.label.replace(/[^\d]/g, ""));
    if (Number.isFinite(parsedMinutes) && parsedMinutes > 0) {
      return Math.round(parsedMinutes);
    }
  }

  return 0;
}

function resolveFleetSequenceGroupAspiratorServiceMinutes(memberSegments = []) {
  const seenKeys = new Set();
  let totalMinutes = 0;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    const minutes = resolveFleetSequenceAspiratorServiceMinutes(segment);
    if (minutes <= 0) {
      return;
    }

    const serviceKey = resolveFleetSequenceAspiratorServiceKey(segment);
    if (serviceKey && seenKeys.has(serviceKey)) {
      return;
    }

    if (serviceKey) {
      seenKeys.add(serviceKey);
    }

    totalMinutes += minutes;
  });

  return totalMinutes;
}

// Temps fixes par livraison et par chargement (Paramètres système) : sur le premier arrêt de la visite, comme le backend.
const FLEET_FIXED_MINUTES_FIELD_BY_STOP_TYPE = { delivery: "delivery_fixed_minutes", loading: "loading_fixed_minutes" };

function resolveFleetSequenceFixedMinutes(segment) {
  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  const field = FLEET_FIXED_MINUTES_FIELD_BY_STOP_TYPE[stopType];
  const minutes = Number(field && !segment?.isPreloadedPreviousDay ? segment?.[field] : 0);
  return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 0;
}

// Une visite regroupée : le temps fixe du camion, une fois, si elle compte une opération ordinaire (ni aspiration ni préchargement).
function resolveFleetSequenceGroupFixedMinutes(memberSegments = [], assignment = null, stopType = "") {
  const field = FLEET_FIXED_MINUTES_FIELD_BY_STOP_TYPE[stopType];
  const hasOrdinaryStep = Boolean(field) && (Array.isArray(memberSegments) ? memberSegments : [])
    .some((segment) => !segment?.requiresAspirator && !segment?.isPreloadedPreviousDay && Number(segment?.quantityTons) > 0);
  return hasOrdinaryStep ? Math.max(0, Math.round(Number(assignment?.[field]) || 0)) : 0;
}

function resolveFleetSequenceDisplayedServiceDurationMinutes(rawServiceMinutes, meunerieExtraMinutes = 0, aspiratorServiceMinutes = 0) {
  const normalizedRaw = normalizeFleetSegmentDurationMinutes(rawServiceMinutes);
  if (normalizedRaw == null) {
    return null;
  }

  const normalizedMeunerieExtra = Math.max(0, Math.round(Number(meunerieExtraMinutes) || 0));
  const normalizedAspiratorExtra = Math.max(0, Math.round(Number(aspiratorServiceMinutes) || 0));
  const normalizedExtra = normalizedMeunerieExtra + normalizedAspiratorExtra;
  if (normalizedExtra <= 0 || normalizedRaw < normalizedExtra) {
    return normalizedRaw;
  }

  return normalizedRaw - normalizedExtra;
}

function resolveFleetSequenceGroupDisplayedServiceDurationMinutes(
  memberSegments = [],
  totalServiceDurationMinutes = 0,
  { assignment = null, stopType = "" } = {},
) {
  const normalizedTotal = normalizeFleetSegmentDurationMinutes(totalServiceDurationMinutes);
  if (normalizedTotal == null) {
    return null;
  }

  const resolvedStopType = typeof stopType === "string" && stopType.trim()
    ? stopType.trim()
    : typeof memberSegments[0]?.stopType === "string"
      ? memberSegments[0].stopType.trim()
      : "";
  const totalMeunerieExtraMinutes = resolveFleetSequenceGroupMeunerieExtraMinutes(memberSegments);
  const totalAspiratorServiceMinutes = resolveFleetSequenceGroupAspiratorServiceMinutes(memberSegments);
  const totalExtraMinutes = totalMeunerieExtraMinutes + totalAspiratorServiceMinutes;

  if (totalExtraMinutes <= 0) {
    return normalizedTotal;
  }

  const totalQuantityTons = memberSegments.reduce((total, segment) => {
    const quantityTons = Number(segment?.quantityTons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);
  const serviceRateTonsPerHour = resolveFleetServiceRateTonsPerHour(assignment, resolvedStopType);
  const calculatedBaseMinutes = resolveFleetServiceMinutesForQuantityTons(totalQuantityTons, serviceRateTonsPerHour);

  if (calculatedBaseMinutes > 0) {
    return calculatedBaseMinutes + resolveFleetSequenceGroupFixedMinutes(memberSegments, assignment, resolvedStopType);
  }

  if (normalizedTotal <= totalExtraMinutes) {
    return normalizedTotal;
  }

  const withoutExtraMinutes = normalizedTotal - totalExtraMinutes;
  if (withoutExtraMinutes >= 15 || normalizedTotal > (totalExtraMinutes * 1.5)) {
    return withoutExtraMinutes;
  }

  return normalizedTotal;
}

function resolveFleetSequenceGroupMeunerieExtraMinutes(memberSegments = []) {
  const seenClients = new Set();
  let totalMeunerieExtra = 0;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    if (segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
      return;
    }

    const extraMinutes = resolveFleetSequenceMeunerieExtraMinutes(segment);
    if (extraMinutes <= 0) {
      return;
    }

    const clientKey = resolveFleetSequenceMeunerieClientKey(segment);
    if (clientKey && seenClients.has(clientKey)) {
      return;
    }

    if (clientKey) {
      seenClients.add(clientKey);
    }

    totalMeunerieExtra += extraMinutes;
  });

  return totalMeunerieExtra;
}

function resolveFleetSequenceGroupOpeningTime(memberSegments = []) {
  let latestOpeningTime = null;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    if (segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
      return;
    }

    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType !== "loading") {
      return;
    }

    const openingTime = segment?.client_loading_opening_time;
    if (typeof openingTime !== "string" || !openingTime) {
      return;
    }

    if (latestOpeningTime === null || openingTime > latestOpeningTime) {
      latestOpeningTime = openingTime;
    }
  });

  return latestOpeningTime;
}

function resolveFleetSequenceGroupClosingTime(memberSegments = []) {
  let earliestClosingTime = null;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    if (segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day) {
      return;
    }

    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType !== "loading") {
      return;
    }

    const closingTime = segment?.client_loading_closing_time;
    if (typeof closingTime !== "string" || !closingTime) {
      return;
    }

    if (earliestClosingTime === null || closingTime < earliestClosingTime) {
      earliestClosingTime = closingTime;
    }
  });

  return earliestClosingTime;
}

function buildFleetSequenceMeunerieChip(extraMinutes, keyPrefix = "segment") {
  const normalizedExtraMinutes = Math.max(0, Math.round(Number(extraMinutes) || 0));
  if (normalizedExtraMinutes <= 0) {
    return null;
  }

  return {
    key: `${keyPrefix}:meunerie`,
    kind: "meunerie",
    label: `+${normalizedExtraMinutes} min`,
    tone: "amber",
    hoverCardTitle: "Surtemps meunerie",
    title: `Surtemps meunerie : +${normalizedExtraMinutes} min. Il se règle dans Data Manager > Clients. Il s'ajoute au temps de chargement calculé à partir du tonnage et du débit camion.`,
  };
}

function buildFleetSequenceAspiratorTimeChip(extraMinutes, keyPrefix = "segment") {
  const normalizedExtraMinutes = Math.max(0, Math.round(Number(extraMinutes) || 0));
  if (normalizedExtraMinutes <= 0) {
    return null;
  }

  return {
    key: `${keyPrefix}:aspirator_time`,
    kind: "aspirator_time",
    label: `+${normalizedExtraMinutes} min`,
    tone: "aspiration",
    hoverCardTitle: "Temps aspirateur",
    title: `Temps aspirateur : +${normalizedExtraMinutes} min. Temps du camion (la base) plus les tonnes au débit d'aspiration (Paramètres système), quand la commande exige l'aspirateur.`,
  };
}

const MANUAL_MIX_INTER_PRODUCT_BUFFER_NOTE = "Délai inter-produit mix produit";

function buildFleetSequenceManualBufferChip(segment, keyPrefix = "segment") {
  const normalizedBufferMinutes = normalizeFleetSegmentDurationMinutes(segment?.manualBufferMinutes);
  // La pause dîner a sa propre ligne « Dîner » (séquence) et son bloc (Gantt).
  if (normalizedBufferMinutes == null || normalizedBufferMinutes <= 0 || isLunchBreakSegment(segment)) {
    return null;
  }

  const roundedBufferMinutes = Math.round(normalizedBufferMinutes);
  const bufferNote = typeof segment?.manualBufferNote === "string" ? segment.manualBufferNote.trim() : "";
  const reasonLabel = typeof segment?.manualBufferReasonLabel === "string" && segment.manualBufferReasonLabel.trim()
    ? segment.manualBufferReasonLabel.trim()
    : formatManualBufferReasonLabel(segment?.manualBufferReason) || "";
  const isSystemMixProductBuffer = bufferNote === MANUAL_MIX_INTER_PRODUCT_BUFFER_NOTE;
  const hoverCardTitle = isSystemMixProductBuffer ? "Délai mix produit" : "Buffer manuel";
  const hoverCardDetails = {
    lines: [
      { label: "Durée", value: `+${roundedBufferMinutes} min` },
      bufferNote ? { label: "Paramètre", value: bufferNote } : null,
      reasonLabel ? { label: "Catégorie", value: reasonLabel } : null,
    ].filter(Boolean),
    note: "Ce temps s'ajoute à la durée affichée du segment sur le Gantt, après le trajet routier et le temps de service. Il n'est pas inclus dans le kilométrage ni dans le temps de chargement ou de déchargement.",
    total: `+${roundedBufferMinutes} min`,
    totalLabel: "Buffer segment",
  };

  return {
    key: `${keyPrefix}:manual_buffer`,
    kind: "manual_buffer",
    label: `+${roundedBufferMinutes} min`,
    tone: "buffer",
    hoverCardDetails,
    hoverCardTitle,
    title: `${isSystemMixProductBuffer ? MANUAL_MIX_INTER_PRODUCT_BUFFER_NOTE : hoverCardTitle} : +${roundedBufferMinutes} min`,
  };
}

function buildFleetSequenceGroupManualBufferChip(memberSegments, groupKey = "group") {
  const segmentsWithBuffer = (Array.isArray(memberSegments) ? memberSegments : [])
    .filter((segment) => (normalizeFleetSegmentDurationMinutes(segment?.manualBufferMinutes) || 0) > 0 && !isLunchBreakSegment(segment));
  if (!segmentsWithBuffer.length) {
    return null;
  }

  const totalBufferMinutes = sumFleetSegmentDurationMinutes(segmentsWithBuffer.map((segment) => segment?.manualBufferMinutes));
  const primaryBufferSegment = segmentsWithBuffer.find((segment) => (
    typeof segment?.manualBufferNote === "string" && segment.manualBufferNote.trim()
  )) || segmentsWithBuffer[0];

  return buildFleetSequenceManualBufferChip({
    ...primaryBufferSegment,
    manualBufferMinutes: totalBufferMinutes,
  }, groupKey);
}

function getSequenceGroupCompartmentLabel(memberSegments) {
  return joinGroupedSegmentCompartmentLabel(memberSegments) || "—";
}

function parseSequenceDistanceKm(distanceLabel = "") {
  if (typeof distanceLabel !== "string") {
    return null;
  }

  const match = distanceLabel.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) {
    return null;
  }

  const parsedDistance = Number.parseFloat(match[0].replace(",", "."));
  return Number.isFinite(parsedDistance) ? parsedDistance : null;
}

function compactFleetSequenceDurationLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  return label.replace(/(\d+\s*h\s+\d+)\s*min\b/giu, "$1").trim();
}

function compactHoverCardFleetSequenceDurationLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  return compactFleetSequenceDurationLabel(label).trim();
}

function resolveSegmentDestinationAddress(segment) {
  const destinationAddress = normalizeSegmentMetaValue(segment?.routeToAddress || segment?.to?.address || segment?.toAddress);
  const primaryAddress = resolveSegmentPrimaryAddress(segment);

  if (!destinationAddress) {
    return "";
  }

  return normalizeFleetSearch(destinationAddress) === normalizeFleetSearch(primaryAddress)
    ? ""
    : destinationAddress;
}

function isInformationalSequenceLoadItem(item = null) {
  const items = Array.isArray(item) ? item.filter(Boolean) : [item].filter(Boolean);
  return items.some((entry) => entry?.isInitialPreload || entry?.isEndOfDayLoadSourcePreload || entry?.isEndOfDayLoad);
}

function isInformationalInitialPreloadItem(item = null) {
  const items = Array.isArray(item) ? item.filter(Boolean) : [item].filter(Boolean);
  return items.some((entry) => entry?.isInitialPreload);
}

function isInformationalEndOfDayLoadItem(item = null) {
  const items = Array.isArray(item) ? item.filter(Boolean) : [item].filter(Boolean);
  return items.some((entry) => entry?.isEndOfDayLoad || entry?.isEndOfDayLoadSourcePreload);
}

function shouldHideInformationalLoadDurationMetrics(segment = null) {
  return isInformationalSequenceLoadItem(segment);
}

function formatFleetSequenceDurationChipLabel(value, { approximate = false } = {}) {
  const durationMinutes = Number(value);
  if (!Number.isFinite(durationMinutes) || durationMinutes < 0) {
    return "—";
  }

  if (durationMinutes === 0) {
    return "0 min";
  }

  const formattedLabel = compactFleetSequenceDurationLabel(formatDurationShort(durationMinutes) || "0 min");
  return approximate ? `≈ ${formattedLabel}` : formattedLabel;
}

function resolveFleetSequenceManualDurationSpeedLabel(segment) {
  const routeSpeedKmh = Number(segment?.routeSpeedKmh ?? segment?.speedKmh);
  return Number.isFinite(routeSpeedKmh) && routeSpeedKmh > 0
    ? `${Math.round(routeSpeedKmh)} km/h`
    : "vitesse camion";
}

function resolveFleetSequenceManualDurationLabel(segment) {
  return `Durée manuelle (${resolveFleetSequenceManualDurationSpeedLabel(segment)})`;
}

function resolveFleetSequenceDisplayedDurationRowLabel() {
  return "Durée affichée";
}

function resolveFleetSequenceDurationChipTitle(
  segment,
  displayLabel,
  { legacyDurationVisibleWhenHereEnabled = false } = {},
) {
  if (segment?.fleetDisplayedTravelUsesHereTraffic === true) {
    return `Temps HERE avec trafic affiché : ${displayLabel}`;
  }

  if (legacyDurationVisibleWhenHereEnabled) {
    return `${resolveFleetSequenceManualDurationLabel(segment)} : ${displayLabel}`;
  }

  return `Temps de conduite affiché : ${displayLabel}`;
}

function formatFleetServiceRateLabel(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(2).replace(/\.0+$/, "").replace(/(\.[1-9]*)0+$/, "$1");
}

function resolveFleetServiceRateTonsPerHour(assignment = null, stopType = "") {
  const rawRate = stopType === "loading"
    ? assignment?.loading_rate_tons_per_hour ?? assignment?.loading_time_minutes
    : stopType === "delivery"
      ? assignment?.unloading_rate_tons_per_hour ?? assignment?.unloading_time_minutes
      : null;
  const tonsPerHour = Number(rawRate);

  return Number.isFinite(tonsPerHour) && tonsPerHour > 0 ? tonsPerHour : null;
}

function resolveFleetServiceMinutesForQuantityTons(quantityTons, tonsPerHour) {
  const normalizedQuantityTons = Number(quantityTons);
  const normalizedTonsPerHour = Number(tonsPerHour);

  if (!Number.isFinite(normalizedQuantityTons) || normalizedQuantityTons <= 0) {
    return 0;
  }

  if (!Number.isFinite(normalizedTonsPerHour) || normalizedTonsPerHour <= 0) {
    return 0;
  }

  return Math.ceil((((normalizedQuantityTons / normalizedTonsPerHour) * 60) - 1e-9));
}

// Sans le droit de lecture des débits (Data Manager), le débit et son calcul cèdent la place
// aux minutes qu'il produit : la carte reste une addition lisible jusqu'au total.
function withFleetServiceRateRestriction(card, { serviceLabel, serviceMinutes }) {
  if (!card.lines.some((line) => line.label === "Débit camion")) {
    return card;
  }

  return {
    ...card,
    restricted: {
      permission: ACCESS_PERMISSION_KEYS.dataManagerTruckLoadRatesRead,
      lines: card.lines.flatMap((line) => {
        if (line.label === "Débit camion") {
          return serviceMinutes > 0 ? [{ label: serviceLabel, value: `${serviceMinutes} min` }] : [];
        }

        return line.label === "Calcul" || line.label === "Arrondi" ? [] : [line];
      }),
    },
  };
}

function resolveFleetServiceRateRestrictedLabel(stopType) {
  return stopType === "delivery" ? "Déchargement au débit camion" : "Chargement au débit camion";
}

function buildSequenceGroupServiceDurationHoverCard(memberSegments = [], totalServiceDurationMinutes = 0, { assignment = null } = {}) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const firstMemberSegment = resolvedMemberSegments[0] || null;
  const stopType = typeof firstMemberSegment?.stopType === "string" ? firstMemberSegment.stopType.trim() : "";
  const totalQuantityTons = resolvedMemberSegments.reduce((total, segment) => {
    const quantityTons = Number(segment?.quantityTons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);
  const serviceRateTonsPerHour = resolveFleetServiceRateTonsPerHour(assignment, stopType);
  const calculatedServiceMinutes = resolveFleetServiceMinutesForQuantityTons(totalQuantityTons, serviceRateTonsPerHour);
  const fixedMinutes = resolveFleetSequenceGroupFixedMinutes(resolvedMemberSegments, assignment, stopType);
  const quantityLabel = formatQuantityTons(totalQuantityTons);
  const serviceRateLabel = formatFleetServiceRateLabel(serviceRateTonsPerHour);
  const resolvedTotalServiceDurationMinutes = resolveSequenceGroupServiceDurationMinutes(memberSegments, { assignment });
  const displayedServiceDurationMinutes = resolveFleetSequenceGroupDisplayedServiceDurationMinutes(
    memberSegments,
    resolvedTotalServiceDurationMinutes,
    { assignment, stopType },
  ) ?? resolvedTotalServiceDurationMinutes;
  const totalLabel = quantityLabel && serviceRateLabel && calculatedServiceMinutes > 0
    ? formatFleetSequenceDurationChipLabel(calculatedServiceMinutes + fixedMinutes)
    : formatFleetSequenceDurationChipLabel(displayedServiceDurationMinutes) || "0 min";

  return withFleetServiceRateRestriction({
    type: "formula",
    lines: [
      quantityLabel
        ? {
            label: "Quantité cumulée",
            value: `${quantityLabel} t`,
          }
        : null,
      serviceRateLabel
        ? {
            label: "Débit camion",
            value: `${serviceRateLabel} t/h`,
          }
        : null,
      quantityLabel && serviceRateLabel
        ? {
            label: "Calcul",
            value: `${quantityLabel} ÷ ${serviceRateLabel} × 60`,
          }
        : null,
      quantityLabel && serviceRateLabel && calculatedServiceMinutes > 0
        ? {
            label: "Arrondi",
            value: `${calculatedServiceMinutes} min`,
          }
        : null,
      fixedMinutes > 0
        ? {
            label: "Temps fixe",
            value: `+${fixedMinutes} min`,
          }
        : null,
    ].filter(Boolean),
    note: quantityLabel && serviceRateLabel
      ? "Le temps affiché reprend le service cumulé des étapes regroupées sur ce même arrêt, calculé à partir du tonnage cumulé et du débit camion actif."
      : "Le temps affiché cumule les temps de service des étapes regroupées sur ce même arrêt.",
    total: totalLabel,
    totalLabel: "Temps de service",
  }, {
    serviceLabel: resolveFleetServiceRateRestrictedLabel(stopType),
    serviceMinutes: calculatedServiceMinutes,
  });
}

function buildMergedSameAddressServiceDurationHoverCard(segment, { assignment = null } = {}) {
  const memberSegments = Array.isArray(segment?.mergedMemberSegments)
    ? segment.mergedMemberSegments.filter(Boolean)
    : [];
  const serviceStepCount = memberSegments.filter((memberSegment) => {
    const stopType = typeof memberSegment?.stopType === "string" ? memberSegment.stopType.trim() : "";
    return stopType === "loading" || stopType === "delivery";
  }).length;
  const stopType = typeof segment?.stopType === "string" && segment.stopType.trim()
    ? segment.stopType.trim()
    : "loading";
  const quantityTons = resolveMergedSameAddressQuantityTons(memberSegments);
  const quantityLabel = Number.isFinite(quantityTons) && quantityTons > 0 ? formatQuantityTons(quantityTons) : null;
  const serviceRateTonsPerHour = resolveFleetServiceRateTonsPerHour(assignment, stopType);
  const serviceRateLabel = formatFleetServiceRateLabel(serviceRateTonsPerHour);
  const baseStepMinutes = resolveFleetServiceMinutesForQuantityTons(quantityTons, serviceRateTonsPerHour);
  const groupedServiceDurationMinutes = resolveSequenceGroupServiceDurationMinutes(memberSegments, { assignment });
  const fixedMinutes = memberSegments.reduce((total, memberSegment) => total + resolveFleetSequenceFixedMinutes(memberSegment), 0);
  const totalMinutes = baseStepMinutes > 0 && serviceStepCount > 0
    ? baseStepMinutes * serviceStepCount + fixedMinutes
    : groupedServiceDurationMinutes;
  const totalLabel = formatFleetSequenceDurationChipLabel(totalMinutes) || "0 min";
  const totalCardLabel = resolveFleetSequenceServiceHoverCardTitle(segment);

  return withFleetServiceRateRestriction({
    type: "formula",
    lines: [
      quantityLabel
        ? {
            label: "Quantité",
            value: `${quantityLabel} t`,
          }
        : null,
      serviceRateLabel
        ? {
            label: "Débit camion",
            value: `${serviceRateLabel} t/h`,
          }
        : null,
      quantityLabel && serviceRateLabel
        ? {
            label: "Calcul",
            value: `${quantityLabel} ÷ ${serviceRateLabel} × 60`,
          }
        : null,
      quantityLabel && serviceRateLabel && baseStepMinutes > 0
        ? {
            label: "Arrondi",
            value: `${baseStepMinutes} min`,
          }
        : null,
      baseStepMinutes > 0 && serviceStepCount > 1
        ? {
            label: "Étapes fusionnées",
            value: `${serviceStepCount} × ${baseStepMinutes} min`,
          }
        : null,
      fixedMinutes > 0
        ? {
            label: "Temps fixe",
            value: `+${fixedMinutes} min`,
          }
        : null,
    ].filter(Boolean),
    note: quantityLabel && serviceRateLabel
      ? "Le temps affiché compte le chargement et le déchargement fusionnés. Le temps aspirateur demeure affiché séparément."
      : "Le temps affiché cumule seulement le chargement et le déchargement fusionnés.",
    total: totalLabel,
    totalLabel: totalCardLabel,
  }, {
    serviceLabel: serviceStepCount > 1 ? "Étape au débit camion" : resolveFleetServiceRateRestrictedLabel(stopType),
    serviceMinutes: baseStepMinutes,
  });
}

function resolveSequenceGroupServiceDurationMinutes(memberSegments = [], { assignment = null } = {}) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const firstMemberSegment = resolvedMemberSegments[0] || null;
  const stopType = typeof firstMemberSegment?.stopType === "string" ? firstMemberSegment.stopType.trim() : "";
  const serviceRateTonsPerHour = resolveFleetServiceRateTonsPerHour(assignment, stopType);

  return resolveFleetGroupedServiceDurationMinutes({
    memberSegments: resolvedMemberSegments,
    serviceRateTonsPerHour,
    fixedMinutes: stopType === "delivery" ? assignment?.delivery_fixed_minutes : stopType === "loading" ? assignment?.loading_fixed_minutes : 0,
  });
}

function resolveFleetSequenceServiceHoverCardTitle(segment, fallbackTitle = "Temps de service") {
  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";

  if (stopType === "loading") {
    return "Temps de chargement";
  }

  if (stopType === "delivery") {
    return "Temps de déchargement";
  }

  if (stopType === "depot_start") {
    return "Temps d'inspection";
  }

  return fallbackTitle;
}

function buildSequenceSegmentServiceDurationHoverCard(segment, { assignment = null } = {}) {
  if (segment?.isAspiratorMissionMerged === true) {
    const aspiratorServiceMinutes = resolveFleetSequenceAspiratorServiceMinutes(segment);
    const totalLabel = formatFleetSequenceDurationChipLabel(aspiratorServiceMinutes) || "0 min";
    return {
      type: "formula",
      lines: [
        {
          label: "Temps aspirateur",
          value: `+${aspiratorServiceMinutes} min`,
        },
      ],
      note: "Le service de la mission correspond au temps fixe d'Aspiration, appliqué une seule fois.",
      total: totalLabel,
      totalLabel: "Temps de service",
    };
  }

  if (segment?.isSameAddressLoadingDeliveryMerged === true && Array.isArray(segment?.mergedMemberSegments) && segment.mergedMemberSegments.length) {
    return buildMergedSameAddressServiceDurationHoverCard(segment, { assignment });
  }

  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  const quantityTons = Number(segment?.quantityTons);
  const quantityLabel = Number.isFinite(quantityTons) && quantityTons > 0 ? formatQuantityTons(quantityTons) : null;
  const serviceRateTonsPerHour = resolveFleetServiceRateTonsPerHour(assignment, stopType);
  const serviceRateLabel = formatFleetServiceRateLabel(serviceRateTonsPerHour);
  const calculatedServiceMinutes = resolveFleetServiceMinutesForQuantityTons(quantityTons, serviceRateTonsPerHour);
  const totalMinutes = normalizeFleetSegmentDurationMinutes(segment?.serviceDurationMinutes);
  const meunerieExtraMinutes = stopType === "loading"
    ? resolveFleetSequenceMeunerieExtraMinutes(segment)
    : 0;
  const aspiratorServiceMinutes = stopType === "delivery"
    ? resolveFleetSequenceAspiratorServiceMinutes(segment)
    : 0;
  const fixedMinutes = resolveFleetSequenceFixedMinutes(segment);
  const displayedTotalMinutes = resolveFleetSequenceDisplayedServiceDurationMinutes(totalMinutes, meunerieExtraMinutes, aspiratorServiceMinutes);
  const baseServiceLabel = quantityLabel && serviceRateLabel && calculatedServiceMinutes > 0
    ? formatFleetSequenceDurationChipLabel(calculatedServiceMinutes)
    : formatFleetSequenceDurationChipLabel(displayedTotalMinutes) || "0 min";
  const totalServiceMinutes = totalMinutes != null
    ? totalMinutes
    : (displayedTotalMinutes || 0) + meunerieExtraMinutes + aspiratorServiceMinutes;
  const totalLabel = formatFleetSequenceDurationChipLabel(totalServiceMinutes) || baseServiceLabel;
  const totalCardLabel = resolveFleetSequenceServiceHoverCardTitle(segment);

  if (stopType !== "loading" && stopType !== "delivery") {
    return {
      type: "formula",
      lines: [
        {
          label: stopType === "depot_start" ? "Inspection camion" : "Service",
          value: totalLabel,
        },
      ],
      note: stopType === "depot_start"
        ? "Le temps affiché correspond à l'inspection camion avant le départ."
        : "Le temps affiché correspond au service de cette étape.",
      total: totalLabel,
      totalLabel: totalCardLabel,
    };
  }

  return withFleetServiceRateRestriction({
    type: "formula",
    lines: [
      quantityLabel
        ? {
            label: "Quantité",
            value: `${quantityLabel} t`,
          }
        : null,
      serviceRateLabel
        ? {
            label: "Débit camion",
            value: `${serviceRateLabel} t/h`,
          }
        : null,
      quantityLabel && serviceRateLabel
        ? {
            label: "Calcul",
            value: `${quantityLabel} ÷ ${serviceRateLabel} × 60`,
          }
        : null,
      quantityLabel && serviceRateLabel && calculatedServiceMinutes > 0
        ? {
            label: "Arrondi",
            value: `${calculatedServiceMinutes} min`,
          }
        : null,
      meunerieExtraMinutes > 0
        ? {
            label: "Surtemps meunerie",
            value: `+${meunerieExtraMinutes} min`,
          }
        : null,
      fixedMinutes > 0
        ? {
            label: "Temps fixe",
            value: `+${fixedMinutes} min`,
          }
        : null,
      aspiratorServiceMinutes > 0
        ? {
            label: "Temps aspirateur",
            value: `+${aspiratorServiceMinutes} min`,
          }
        : null,
    ].filter(Boolean),
    note: quantityLabel && serviceRateLabel
      ? "Le temps affiché est calculé à partir du tonnage de cette étape et du débit camion actif."
      : "Le temps affiché correspond au service de cette étape.",
    total: totalLabel,
    totalLabel: totalCardLabel,
  }, {
    serviceLabel: resolveFleetServiceRateRestrictedLabel(stopType),
    serviceMinutes: calculatedServiceMinutes,
  });
}

function buildSequenceSegmentServiceDurationChip(
  chip,
  segment,
  { assignment = null, compactLabel = compactFleetSequenceDurationLabel } = {},
) {
  const meunerieExtraMinutes = resolveFleetSequenceMeunerieExtraMinutes(segment);
  const aspiratorServiceMinutes = resolveFleetSequenceAspiratorServiceMinutes(segment);
  const displayedServiceMinutes = resolveFleetSequenceDisplayedServiceDurationMinutes(
    normalizeFleetSegmentDurationMinutes(segment?.serviceDurationMinutes),
    meunerieExtraMinutes,
    aspiratorServiceMinutes,
  );
  const displayLabel = displayedServiceMinutes != null
    ? formatFleetSequenceDurationChipLabel(displayedServiceMinutes)
    : typeof chip?.label === "string"
      ? compactLabel(chip.label)
      : "0 min";
  const hoverCardTitle = resolveFleetSequenceServiceHoverCardTitle(segment, chip?.hoverCardTitle || "Temps de service");

  return {
    ...chip,
    hoverCardDetails: buildSequenceSegmentServiceDurationHoverCard(segment, { assignment }),
    hoverCardTitle,
    label: displayLabel,
    title: `${hoverCardTitle} : ${displayLabel}`,
  };
}

function orderEmbeddedHoverCardDurationChips(chips = []) {
  const resolvedChips = Array.isArray(chips) ? chips.filter(Boolean) : [];
  const meunerieChips = resolvedChips.filter((chip) => chip?.kind === "meunerie" || chip?.key === "meunerie");
  const aspiratorChips = resolvedChips.filter((chip) => chip?.kind === "aspirator_time" || chip?.key === "aspirator_time");
  const manualBufferChips = resolvedChips.filter((chip) => chip?.kind === "manual_buffer" || chip?.key === "manual_buffer");
  const serviceChips = resolvedChips.filter((chip) => chip?.kind === "svc" || chip?.key === "svc");
  const travelChips = resolvedChips.filter((chip) => chip?.kind === "dur" || chip?.key === "dur");
  const otherChips = resolvedChips.filter((chip) => (
    !meunerieChips.includes(chip)
    && !aspiratorChips.includes(chip)
    && !manualBufferChips.includes(chip)
    && !serviceChips.includes(chip)
    && !travelChips.includes(chip)
  ));

  return [...otherChips, ...serviceChips, ...meunerieChips, ...travelChips, ...manualBufferChips, ...aspiratorChips];
}

function buildSequenceGroupDurationChips(groupItem, displayOverrides, { assignment = null, hovercardContext = null } = {}) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  if (!memberSegments.length) {
    return [];
  }

  if (
    !isInformationalEndOfDayLoadItem(groupItem)
    && !isInformationalEndOfDayLoadItem(memberSegments)
    && (
      isInformationalInitialPreloadItem(groupItem)
      || isInformationalInitialPreloadItem(memberSegments)
    )
  ) {
    return [];
  }

  const displayedTravelDurations = memberSegments.map((segment, memberIndex) => {
    return resolveSameSiteSequenceDisplayedTravelDuration(segment, displayOverrides, memberIndex, hovercardContext);
  });
  const totalTravelDurationMinutes = displayedTravelDurations.reduce((total, durationEntry) => total + durationEntry.minutes, 0);
  const totalServiceDurationMinutes = resolveSequenceGroupServiceDurationMinutes(memberSegments, { assignment });
  const displayedServiceDurationMinutes = resolveFleetSequenceGroupDisplayedServiceDurationMinutes(
    memberSegments,
    totalServiceDurationMinutes,
    { assignment, stopType: groupItem?.stopType },
  ) ?? totalServiceDurationMinutes;
  const hasServiceChip = displayedServiceDurationMinutes > 0 || memberSegments.some((segment) => segment?.serviceDurationLabel != null);
  const displayedTravelUsesHereTraffic = displayedTravelDurations.some((durationEntry) => durationEntry?.usesHereTraffic === true);
  const totalTravelDurationLabel = formatFleetSequenceDurationChipLabel(totalTravelDurationMinutes, { approximate: true });
  const totalServiceDurationLabel = formatFleetSequenceDurationChipLabel(displayedServiceDurationMinutes);

  return [
    hasServiceChip
      ? {
          key: `${groupItem?.groupKey || "group"}:svc`,
          kind: "svc",
          hoverCardDetails: buildSequenceGroupServiceDurationHoverCard(memberSegments, totalServiceDurationMinutes, {
            assignment,
          }),
          hoverCardTitle: "Temps de service",
          label: totalServiceDurationLabel,
          title: `Temps de service cumulé : ${totalServiceDurationLabel}`,
        }
      : null,
    groupItem?.stopType === "loading"
      ? buildFleetSequenceMeunerieChip(
        resolveFleetSequenceGroupMeunerieExtraMinutes(memberSegments),
        groupItem?.groupKey || groupItem?.key || "group",
      )
      : null,
    resolveFleetSequenceGroupAspiratorServiceMinutes(memberSegments) > 0
      ? buildFleetSequenceAspiratorTimeChip(
        resolveFleetSequenceGroupAspiratorServiceMinutes(memberSegments),
        groupItem?.groupKey || groupItem?.key || "group",
      )
      : null,
    buildFleetSequenceGroupManualBufferChip(memberSegments, groupItem?.groupKey || groupItem?.key || "group"),
    {
      key: `${groupItem?.groupKey || "group"}:dur`,
      kind: "dur",
      hoverCardDetails: buildGroupedRouteSequenceDurationHoverCard(
        groupItem,
        memberSegments,
        buildRouteSequenceGroupDistanceLabel(memberSegments, displayOverrides),
        totalTravelDurationMinutes,
        { showsHereTrafficDuration: displayedTravelUsesHereTraffic },
      ),
      hoverCardTitle: resolveRouteSequenceDisplayedTravelLabel(displayedTravelUsesHereTraffic),
      label: totalTravelDurationLabel,
      title: buildRouteSequenceDisplayedTravelTitle(totalTravelDurationLabel, displayedTravelUsesHereTraffic),
    },
  ].filter(Boolean);
}

function normalizeFleetSegmentDurationMinutes(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
}

function sumFleetSegmentDurationMinutes(values = []) {
  const resolvedValues = Array.isArray(values) ? values : [values];
  let hasValue = false;
  let totalMinutes = 0;

  resolvedValues.forEach((value) => {
    const normalizedValue = normalizeFleetSegmentDurationMinutes(value);
    if (normalizedValue == null) {
      return;
    }

    hasValue = true;
    totalMinutes += normalizedValue;
  });

  return hasValue ? totalMinutes : null;
}

function resolveFleetSegmentVisibleOpeningTime(segment, sequenceRenderItems = [], sequenceOpeningVisibilityByItemKey = null) {
  const resolvedOpeningTime = typeof segment?.client_loading_opening_time === "string"
    ? segment.client_loading_opening_time.trim()
    : "";

  if (!resolvedOpeningTime) {
    return "";
  }

  if (!(sequenceOpeningVisibilityByItemKey instanceof Map)) {
    return resolvedOpeningTime;
  }

  return shouldDisplayRouteSequenceOpeningForSegment(segment, sequenceRenderItems, sequenceOpeningVisibilityByItemKey)
    ? resolvedOpeningTime
    : "";
}

function resolveFleetSegmentVisibleClosingTime(segment, sequenceRenderItems = [], sequenceClosingVisibilityByItemKey = null) {
  const resolvedClosingTime = typeof segment?.client_loading_closing_time === "string"
    ? segment.client_loading_closing_time.trim()
    : "";

  if (!resolvedClosingTime) {
    return "";
  }

  if (!(sequenceClosingVisibilityByItemKey instanceof Map)) {
    return "";
  }

  return shouldDisplayRouteSequenceClosingForSegment(segment, sequenceRenderItems, sequenceClosingVisibilityByItemKey)
    ? resolvedClosingTime
    : "";
}

function resolveFleetGroupVisibleOpeningTime(memberSegments = [], sequenceRenderItems = [], sequenceOpeningVisibilityByItemKey = null) {
  const firstMemberSegment = (Array.isArray(memberSegments) ? memberSegments : [])[0] || null;

  if (!(sequenceOpeningVisibilityByItemKey instanceof Map)) {
    return resolveFleetSequenceGroupOpeningTime(memberSegments);
  }

  if (!shouldDisplayRouteSequenceOpeningForSegment(firstMemberSegment, sequenceRenderItems, sequenceOpeningVisibilityByItemKey)) {
    return "";
  }

  return resolveFleetSequenceGroupOpeningTime(memberSegments);
}

function resolveFleetGroupVisibleClosingTime(memberSegments = [], sequenceRenderItems = [], sequenceClosingVisibilityByItemKey = null) {
  const firstMemberSegment = (Array.isArray(memberSegments) ? memberSegments : [])[0] || null;

  if (!(sequenceClosingVisibilityByItemKey instanceof Map)) {
    return "";
  }

  if (!shouldDisplayRouteSequenceClosingForSegment(firstMemberSegment, sequenceRenderItems, sequenceClosingVisibilityByItemKey)) {
    return "";
  }

  return resolveFleetSequenceGroupClosingTime(memberSegments);
}

function parseGroupedExtraDurationChipMinutes(chip = null) {
  const label = getRouteMetaChipDisplayLabel(chip);
  if (typeof label !== "string" || !label.trim()) {
    return 0;
  }

  const match = label.match(/\+?\s*(\d+)\s*min/i);
  return match ? Number(match[1]) : 0;
}

function buildGroupedSegmentExtraDurationChip(extraChips = [], groupKey = "group") {
  const resolvedChips = Array.isArray(extraChips) ? extraChips.filter(Boolean) : [];
  if (!resolvedChips.length) {
    return null;
  }

  let totalMinutes = 0;
  const lines = [];

  resolvedChips.forEach((chip) => {
    const minutes = parseGroupedExtraDurationChipMinutes(chip);
    if (minutes > 0) {
      totalMinutes += minutes;
    }

    const lineLabel = typeof chip?.hoverCardTitle === "string" && chip.hoverCardTitle.trim()
      ? chip.hoverCardTitle.trim()
      : typeof chip?.kind === "string" && chip.kind.trim()
        ? chip.kind.trim()
        : "Temps";
    const lineValue = getRouteMetaChipDisplayLabel(chip) || "—";

    lines.push({
      label: lineLabel,
      value: lineValue,
    });
  });

  if (totalMinutes <= 0) {
    return null;
  }

  return {
    key: `${groupKey}:extra_duration`,
    kind: "manual_buffer",
    label: `+${totalMinutes} min`,
    tone: "buffer",
    hoverCardDetails: {
      type: "formula",
      lines,
      note: "Somme des temps ci-dessus.",
      total: `+${totalMinutes} min`,
      totalLabel: "Total additionnel",
    },
    hoverCardTitle: "Temps additionnels",
    title: `Temps additionnels : +${totalMinutes} min`,
  };
}

function GroupedSegmentOrdersHoverIcon({ memberSegments = [], allowSingleOrder = false }) {
  const orderEntries = React.useMemo(
    () => buildGroupedSegmentOrderHoverEntries(memberSegments),
    [memberSegments],
  );
  const orderCount = orderEntries.length;
  const hoverCardTitle = orderCount === 1 ? "Commande" : "Commandes";
  const hoverCardContent = React.useMemo(() => {
    const hoverCardDetails = buildGroupedSegmentOrdersHoverCardDetails(memberSegments);
    if (!hoverCardDetails) {
      return null;
    }

    return (
      <div className="grid min-w-0 gap-1 leading-tight">
        {hoverCardDetails.lines.map((line, index) => (
          <div key={`${line.label}:${index}`} className="flex min-w-0 items-start gap-2">
            <div className="grid shrink-0 gap-0.5 font-semibold text-content-ink">
              <span className="break-words" title={line.label}>{line.label}</span>
              {line.secondary ? <span className="break-words text-content-copy">{line.secondary}</span> : null}
            </div>
            <p className="m-0 min-w-0 flex-1 whitespace-normal break-words text-content-muted">{line.value}</p>
          </div>
        ))}
        {hoverCardDetails.note ? (
          <p className="m-0 border-t border-slate-200 pt-1 text-content-muted">{hoverCardDetails.note}</p>
        ) : null}
      </div>
    );
  }, [memberSegments]);

  if (!hoverCardContent || !orderCount || (orderCount === 1 && (!allowSingleOrder || orderEntries[0].orderLabel === "—"))) {
    return null;
  }

  return (
    <HoverCard
      title={hoverCardTitle}
      content={hoverCardContent}
      placement="top"
      delay={120}
      {...FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS}
      maxWidth={FLEET_SEGMENT_HOVER_CARD_MAX_WIDTH}
      width="max-content"
      accentColor="var(--hovercard-accent-neutral)"
    >
      <button
        type="button"
        aria-label={orderCount === 1 ? "Consulter la commande" : `${orderCount} commandes regroupées`}
        title={`${orderCount} commande${orderCount > 1 ? "s" : ""}`}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2"
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <ClipboardList aria-hidden="true" className="h-3 w-3" strokeWidth={2.05} />
      </button>
    </HoverCard>
  );
}

function FleetHoverCardOrderLocationColumn({ items = [] }) {
  const visibleItems = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!visibleItems.length) {
    return null;
  }

  return (
    <div className="grid min-w-0 grid-rows-2 gap-1">
      {visibleItems.map((item) => (
        <Pill
          key={item.key}
          tone="neutral"
          className={joinClasses([
            "min-h-5 w-full min-w-0 justify-start gap-1 rounded-[4px]! px-1.5 py-px text-[0.58rem] font-semibold leading-none shadow-none",
            item.key === "building" ? "row-start-2" : "row-start-1",
          ])}
          title={item.title || item.value}
        >
          <span className="shrink-0 font-extrabold uppercase tracking-[0.08em] text-content-faint">{item.label}</span>
          <span className="min-w-0 truncate text-content-ink">{item.value}</span>
        </Pill>
      ))}
    </div>
  );
}

function FleetHoverCardMetricsRow({
  assignment = null,
  compartmentChip = null,
  compartmentProfile = [],
  compartmentSegments = [],
  extraDurationChip = null,
  incidentDurationChip = null,
  orderLocationItems = [],
  ordersHoverIcon = null,
  primaryChips = [],
}) {
  // Camion après le segment, sous les pastilles ; à défaut (camion sans compartiments ni étapes), les carrés.
  const truckLoadMatch = useTruckLoadMatch(compartmentChip ? assignment : null, compartmentSegments);
  const visiblePrimaryChips = Array.isArray(primaryChips) ? primaryChips.filter(Boolean) : [];
  const visibleOrderLocationItems = Array.isArray(orderLocationItems) ? orderLocationItems.filter(Boolean) : [];
  const primaryMetricsGridClassName = resolveFleetHoverCardPrimaryMetricsGridClassName(visiblePrimaryChips.length);
  const useCompactPrimaryChipsLayout = visiblePrimaryChips.length === 1;
  const trailingMetricChips = [extraDurationChip, incidentDurationChip].filter(Boolean);
  const trailingMetricChipsContent = trailingMetricChips.length ? (
    <div className="flex shrink-0 items-center justify-end gap-1">
      {trailingMetricChips.map((chip) => (
        <RouteMetaChipRow
          key={chip?.key || chip?.kind || chip?.label}
          chips={[chip]}
          chipHoverCardMaxWidth={FLEET_SEGMENT_HOVER_CARD_MAX_WIDTH}
          chipLabelClassName="whitespace-nowrap"
          className="gap-1"
          gridClassName=""
          useTightHoverCard
        />
      ))}
    </div>
  ) : null;

  if (!compartmentChip && !visiblePrimaryChips.length && !visibleOrderLocationItems.length && !trailingMetricChips.length && !ordersHoverIcon) {
    return null;
  }

  return (
    <div className="grid min-w-0 w-full gap-1 overflow-hidden rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-white/92 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.78)]">
      <div className="grid min-w-0 gap-0.75">
        {!truckLoadMatch && (compartmentChip || ordersHoverIcon) ? (
          <div className="flex min-w-0 w-full items-center gap-2">
            {compartmentChip ? (
              <div className="min-w-0 flex-1 overflow-hidden">
                <FleetSequenceCompartmentPill chip={compartmentChip} compartmentProfile={compartmentProfile} segments={compartmentSegments} />
              </div>
            ) : null}
            {ordersHoverIcon ? (
              <div className="ml-auto shrink-0">{ordersHoverIcon}</div>
            ) : null}
          </div>
        ) : null}
        {visiblePrimaryChips.length || visibleOrderLocationItems.length || trailingMetricChips.length ? (
          <div className={joinClasses([
            "flex min-w-0 w-full items-center gap-2 overflow-hidden",
            useCompactPrimaryChipsLayout ? "justify-start" : "",
          ])}>
            {visiblePrimaryChips.length ? (
              <div className={joinClasses([
                useCompactPrimaryChipsLayout ? "w-fit max-w-full shrink-0" : "min-w-0 flex-1 overflow-hidden",
              ])}>
                <RouteMetaChipRow
                  chips={visiblePrimaryChips}
                  chipColumnClassNameMap={GROUPED_SEGMENT_METRICS_PRIMARY_CHIP_COLUMN_CLASS_NAME_MAP}
                  compactLayout={useCompactPrimaryChipsLayout}
                  chipHoverCardMaxWidth={FLEET_SEGMENT_HOVER_CARD_MAX_WIDTH}
                  chipLabelClassName="whitespace-nowrap"
                  className="gap-1"
                  gridClassName={useCompactPrimaryChipsLayout ? "" : primaryMetricsGridClassName}
                  useTightHoverCard
                />
              </div>
            ) : null}
            {visibleOrderLocationItems.length ? (
              <div className="w-28 shrink-0">
                <FleetHoverCardOrderLocationColumn items={visibleOrderLocationItems} />
              </div>
            ) : null}
            {!visibleOrderLocationItems.length ? trailingMetricChipsContent : null}
          </div>
        ) : null}
        {visibleOrderLocationItems.length ? trailingMetricChipsContent : null}
        {truckLoadMatch ? (
          <TruckLoadPreview match={truckLoadMatch} truckLabel={assignment?.truck_name || ""} trailing={ordersHoverIcon} />
        ) : null}
      </div>
    </div>
  );
}

function resolveGroupedSegmentBusinessWindowMinutes(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  if (!resolvedMemberSegments.length) {
    return { endMinutes: null, startMinutes: null };
  }

  const startMinutes = resolveSegmentBusinessStartMinutes(resolvedMemberSegments[0]);
  const endMinutes = resolvedMemberSegments.reduce((latestEndMinutes, segment) => {
    const segmentEndMinutes = resolveSegmentBusinessEndMinutes(segment);
    if (segmentEndMinutes == null) {
      return latestEndMinutes;
    }
    if (latestEndMinutes == null || segmentEndMinutes >= latestEndMinutes) {
      return segmentEndMinutes;
    }
    return latestEndMinutes;
  }, null);

  return { endMinutes, startMinutes };
}

function GroupedSegmentMetricsRow({
  assignment = null,
  memberSegments = [],
  compartmentProfile = [],
  groupDurationChips = [],
  displayOverrides = null,
  groupKey = "group",
  incidentDurationChip = null,
  ordersHoverIcon = null,
  hideDurationMetrics = false,
}) {
  const firstMemberSegment = memberSegments[0] || null;
  const compartmentLabel = getSequenceGroupCompartmentLabel(memberSegments);
  const totalQuantityTons = resolveGroupedSegmentQuantityTons(memberSegments);
  const totalQuantityLabel = Number.isFinite(totalQuantityTons) && totalQuantityTons > 0
    ? `${formatQuantityTons(totalQuantityTons)} t`
    : "";
  const compartmentChip = compartmentLabel && compartmentLabel !== "—"
    ? {
        key: `${groupKey}:compartment`,
        kind: "compartment",
        stopType: firstMemberSegment?.stopType,
        label: compartmentLabel,
        title: `Compartiment(s) : ${compartmentLabel}`,
      }
    : null;
  const firstMemberChips = firstMemberSegment
    ? buildSameSiteSequenceHoverMetaChips(firstMemberSegment, displayOverrides, {
        fallbackIndex: 0,
        hideFollowerDurationChips: true,
      })
    : [];
  const firstDistanceChip = firstMemberChips.find((chip) => chip?.kind === "dist" || chip?.key === "dist") || null;
  const groupDistanceLabel = buildRouteSequenceGroupDistanceLabel(memberSegments, displayOverrides);
  const distanceChip = groupDistanceLabel
    ? {
        ...(firstDistanceChip || {}),
        key: `${groupKey}:dist`,
        kind: "dist",
        label: groupDistanceLabel,
        title: `Distance affichée : ${groupDistanceLabel}`,
      }
    : null;
  const visibleDistanceChip = distanceChip && (() => {
    const distanceKm = parseSequenceDistanceKm(getRouteMetaChipDisplayLabel(distanceChip));
    return distanceKm == null || distanceKm > 0;
  })()
    ? distanceChip
    : null;
  const quantityChip = totalQuantityLabel
    ? {
        key: `${groupKey}:qty`,
        kind: "qty",
        hoverCardDetails: buildGroupedSegmentQuantityHoverCardDetails(memberSegments),
        hoverCardTitle: "Quantité",
        label: totalQuantityLabel,
        title: `Quantité cumulée : ${totalQuantityLabel}`,
      }
    : null;
  const serviceChip = hideDurationMetrics
    ? null
    : groupDurationChips.find((chip) => chip?.kind === "svc" || chip?.key === "svc") || null;
  const travelChip = hideDurationMetrics
    ? null
    : groupDurationChips.find((chip) => chip?.kind === "dur" || chip?.key === "dur") || null;
  const extraDurationChip = hideDurationMetrics
    ? null
    : buildGroupedSegmentExtraDurationChip(
      groupDurationChips.filter((chip) => {
        const chipKind = typeof chip?.kind === "string" ? chip.kind.trim() : "";
        return chipKind === "meunerie" || chipKind === "aspirator_time" || chipKind === "manual_buffer";
      }),
      groupKey,
    );
  const primaryChips = [quantityChip, visibleDistanceChip, serviceChip, travelChip].filter(Boolean);
  const orderLocationItems = buildDeliveryOrderLocationInfoItems(memberSegments);

  return (
    <FleetHoverCardMetricsRow
      assignment={assignment}
      compartmentChip={compartmentChip}
      compartmentProfile={compartmentProfile}
      compartmentSegments={memberSegments}
      extraDurationChip={extraDurationChip}
      incidentDurationChip={hideDurationMetrics ? null : incidentDurationChip}
      orderLocationItems={orderLocationItems}
      ordersHoverIcon={ordersHoverIcon}
      primaryChips={primaryChips}
    />
  );
}

function GroupedSegmentHoverCardContent({
  row,
  groupItem,
  displayOverrides = null,
  hovercardContext = null,
  comparisonPayloadByKey = null,
  dispatchDate = "",
  sequenceOpeningVisibilityByItemKey = null,
  sequenceClosingVisibilityByItemKey = null,
  sequenceRenderItems = [],
  truckLabelById = null,
}) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  const groupedHoverCardProductLabels = resolveGroupedSegmentProductLabels(memberSegments);
  const groupedCompartmentFill = summarizeCompartmentFillSegments(memberSegments);
  const firstMemberSegment = memberSegments[0] || null;
  const isInformationalEndOfDayLoadGroup = Boolean(groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload);
  const isInformationalInitialPreloadGroup = Boolean(groupItem?.isInitialPreload);
  const isInformationalSequenceLoadGroup = isInformationalEndOfDayLoadGroup || isInformationalInitialPreloadGroup;
  const comparisonEnabled = hovercardContext?.fleet_segment_hovercard_comparison_enabled === true;
  const groupedOrdersHoverIcon = memberSegments.length > 1
    ? <GroupedSegmentOrdersHoverIcon memberSegments={memberSegments} />
    : null;
  const groupedEndOfDayLoadHoverCardDetails = React.useMemo(() => {
    if (!isInformationalEndOfDayLoadGroup || !firstMemberSegment) {
      return null;
    }

    return resolveEndOfDayLoadHoverCardDetailsFromSegment(firstMemberSegment, {
      row,
      truckLabelById,
    });
  }, [firstMemberSegment, isInformationalEndOfDayLoadGroup, row, truckLabelById]);
  const groupedHeaderLabel = isInformationalEndOfDayLoadGroup ? "Chargé en PM" : "";
  const groupDurationChips = buildSequenceGroupDurationChips(groupItem, displayOverrides, {
    assignment: row?.assignment || null,
    hovercardContext,
  });
  const groupedIncidentDurationChip = React.useMemo(() => {
    if (isInformationalSequenceLoadGroup) {
      return null;
    }

    const { startMinutes, endMinutes } = resolveGroupedSegmentBusinessWindowMinutes(memberSegments);
    return buildFleetSequenceTruckIncidentChip(
      resolveFleetRowTruckIncidents(row),
      startMinutes,
      endMinutes,
      groupItem?.groupKey || groupItem?.key || "group",
    );
  }, [groupItem?.groupKey, groupItem?.key, isInformationalSequenceLoadGroup, memberSegments, row]);
  const groupedComparisonSummary = React.useMemo(() => {
    return buildFleetGroupedComparisonSummary(memberSegments, {
      comparisonPayloadByKey,
      dispatchDate,
      displayOverrides,
    });
  }, [comparisonPayloadByKey, dispatchDate, displayOverrides, memberSegments]);
  const comparisonSourceSegment = memberSegments.find((segment) => {
    const routeSegmentIndex = Number.parseInt(segment?.routeSegmentIndex, 10);
    return Number.isInteger(routeSegmentIndex) && routeSegmentIndex > 0;
  }) || firstMemberSegment;
  const comparisonCurrentDurationMinutes = groupedComparisonSummary.currentDurationMinutes
    ?? resolveFleetSegmentCurrentTravelDurationMinutes(comparisonSourceSegment);
  const comparisonServiceDurationMinutes = resolveSequenceGroupServiceDurationMinutes(memberSegments, {
    assignment: row?.assignment || null,
  });
  const comparisonManualBufferMinutes = sumFleetSegmentDurationMinutes(
    memberSegments.map((segment) => segment?.manualBufferMinutes),
  );
  const comparisonLegacyDurationMinutes = groupedComparisonSummary.legacyDurationMinutes
    ?? normalizeFleetSegmentDurationMinutes(resolveFleetSegmentLegacyTravelDurationMinutes(comparisonSourceSegment));
  const comparisonCurrentHereTrafficDurationMinutes = groupedComparisonSummary.currentHereTrafficDurationMinutes;
  const comparisonPayloadOverride = React.useMemo(() => {
    if (groupedComparisonSummary.comparisonSegmentCount <= 0) {
      return null;
    }

    if (groupedComparisonSummary.comparisonScopeCovered) {
      return {
        comparison_duration_minutes: groupedComparisonSummary.comparisonDurationMinutes,
        current_duration_minutes: comparisonCurrentDurationMinutes,
        here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
        status: "available",
      };
    }

    return {
      comparison_duration_minutes: null,
      current_duration_minutes: comparisonCurrentDurationMinutes,
      here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
      message: "Comparatif indisponible pour certaines étapes du groupe.",
      status: "unavailable",
    };
  }, [comparisonCurrentDurationMinutes, groupedComparisonSummary, hovercardContext?.here_time_aware_routing_enabled]);
  const groupedAddress = resolveSegmentPrimaryAddress(firstMemberSegment) || groupItem?.toAddress || firstMemberSegment?.fromAddress || "";
  const groupedDestinationAddress = resolveGroupedSegmentDestinationAddress(groupItem);
  const groupedDirectionsUrl = buildGroupedRouteDirectionsUrl(groupItem);
  const showGroupedDestinationAddressLine = Boolean(groupedDestinationAddress);
  const groupedPrimaryMapsUrl = showGroupedDestinationAddressLine
    ? ""
    : (groupedDirectionsUrl || buildGoogleMapsSearchUrl(groupedAddress));
  const groupedDestinationMapsUrl = groupedDirectionsUrl || buildGoogleMapsSearchUrl(groupedDestinationAddress);
  const groupedRouteSummary = resolveGroupedSegmentRouteSummary(groupItem);
  const groupedCounterpartyItems = buildSequenceCounterpartyInfoItems(memberSegments, firstMemberSegment);
  const isInlineCounterpartyGroup = shouldInlineGroupedCounterpartyByOrder(groupItem) || shouldInlineGroupedCounterpartyByOrder(firstMemberSegment);
  const groupTargetStartLabel = formatSegmentBusinessStartLabel(firstMemberSegment);
  const groupTargetEndLabel = formatGroupedSegmentBusinessEndLabel(groupItem, memberSegments);
  const groupOpeningTime = resolveFleetGroupVisibleOpeningTime(
    memberSegments,
    sequenceRenderItems,
    sequenceOpeningVisibilityByItemKey,
  );
  const groupClosingTime = resolveFleetGroupVisibleClosingTime(
    memberSegments,
    sequenceRenderItems,
    sequenceClosingVisibilityByItemKey,
  );

  if (!memberSegments.length) {
    return null;
  }

  return (
    <div style={{ "--fleet-context-accent": getSegmentHoverCardAccentColor(groupItem?.stopType, groupItem?.isInitialPreload, isInformationalEndOfDayLoadGroup, resolveEndOfDayLoadVisualMode(groupItem)) }} className={joinClasses([
      FLEET_SEGMENT_HOVER_CARD_SURFACE_CLASS_NAME,
      isInformationalEndOfDayLoadGroup ? "max-h-[calc(82dvh-4rem)] overflow-y-auto" : "",
      getGroupedSegmentSurfaceClassName(groupItem?.stopType || "mixed", groupItem?.isInitialPreload, groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload, resolveEndOfDayLoadVisualMode(groupItem)),
    ])}>
      <div className="grid min-w-0 gap-0.5">
        {row?.truckLabel ? <span data-fleet-context-identity>{row.truckLabel}</span> : null}
        <div className="flex min-w-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {groupedHeaderLabel ? (
              <div className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-content-muted">{groupedHeaderLabel}</div>
            ) : null}
            {groupedHoverCardProductLabels.map((label) => (
              <FleetProductPill
                key={label}
                label={label}
                maxLength={Infinity}
                className="max-w-full whitespace-normal break-words"
                title={label}
              />
            ))}
            <FleetCompartmentFillPill
              percent={groupedCompartmentFill.percent}
              segments={memberSegments}
              assignment={row?.assignment}
            />
            <FleetSegmentOpeningHeaderPill openingTime={groupOpeningTime} />
            <FleetSegmentClosingHeaderPill closingTime={groupClosingTime} />
          </div>
          {groupTargetStartLabel || groupTargetEndLabel ? (
            <div className="flex shrink-0 items-center gap-1.25">
              <SegmentInfoPill
                className="border-[rgba(59,130,246,0.16)] bg-[rgba(239,246,255,0.96)]"
                label={getSegmentStartLabel(firstMemberSegment)}
                value={groupTargetStartLabel}
              />
              <SegmentInfoPill
                className="border-[rgba(15,92,87,0.16)] bg-[rgba(238,247,245,0.96)]"
                label="Fin"
                value={groupTargetEndLabel}
              />
            </div>
          ) : null}
        </div>
        <FleetSegmentHoverCardSummary
          title={groupItem?.toLabel || memberSegments[0]?.toLabel || "Arrêt"}
          trailingInfoItems={groupedCounterpartyItems.length && !isInlineCounterpartyGroup && !groupedOrdersHoverIcon ? groupedCounterpartyItems : []}
          primaryAddress={groupedAddress}
          destinationAddress={showGroupedDestinationAddressLine ? groupedDestinationAddress : ""}
          primaryMapsUrl={groupedPrimaryMapsUrl}
          destinationMapsUrl={groupedDestinationMapsUrl}
          routeSummary={groupedRouteSummary}
        />
      </div>

      <div className="grid gap-0.75">
        {comparisonSourceSegment && !isInformationalSequenceLoadGroup && comparisonEnabled ? (
          <FleetSegmentComparisonSection
            segment={comparisonSourceSegment}
            comparisonPayloadOverride={comparisonPayloadOverride}
            currentDurationMinutesOverride={comparisonCurrentDurationMinutes}
            currentHereTrafficDurationMinutesOverride={comparisonCurrentHereTrafficDurationMinutes}
            displayOverrides={displayOverrides}
            durationChips={[]}
            hovercardContext={hovercardContext}
            legacyDurationMinutesOverride={comparisonLegacyDurationMinutes}
            manualBufferMinutesOverride={comparisonManualBufferMinutes}
            serviceDurationMinutesOverride={comparisonServiceDurationMinutes}
          />
        ) : null}
        <GroupedSegmentMetricsRow
          assignment={row?.assignment}
          memberSegments={memberSegments}
          compartmentProfile={withVirtualTruckCompartment(row?.assignment?.truck_compartments, row?.assignment?.capacity_tons)}
          groupDurationChips={groupDurationChips}
          displayOverrides={displayOverrides}
          groupKey={groupItem?.groupKey || groupItem?.key || "group"}
          incidentDurationChip={groupedIncidentDurationChip}
          ordersHoverIcon={groupedOrdersHoverIcon}
          hideDurationMetrics={isInformationalSequenceLoadGroup}
        />
      </div>

      {groupedEndOfDayLoadHoverCardDetails ? (
        <div className="border-t border-[rgba(148,163,184,0.16)] pt-1.75">
          <EndOfDayLoadHoverCardDetails details={groupedEndOfDayLoadHoverCardDetails} layout="inline" />
        </div>
      ) : null}
    </div>
  );
}

function resolveGroupedSegmentDragCandidate(groupElement, groupItem, clientX) {
  const rawMemberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments : [];
  const safeGroupWidthPercent = Number(groupItem?.widthPercent) > 0 ? Number(groupItem.widthPercent) : 1;
  const draggableSegments = buildExpandedTimelineGroupMemberSegments(rawMemberSegments, {
    leftPercent: Number(groupItem?.leftPercent) || 0,
    widthPercent: safeGroupWidthPercent,
  }).filter((segment) => segment?.reassignmentDrag || segment?.segmentReorder);

  if (!draggableSegments.length) {
    return null;
  }

  if (!(groupElement instanceof HTMLElement)) {
    return draggableSegments[0] || null;
  }

  const bounds = groupElement.getBoundingClientRect();
  const safeWidth = bounds.width > 0 ? bounds.width : 1;
  const relativeClientX = Number.isFinite(Number(clientX)) ? Number(clientX) : bounds.left;
  const relativeX = Math.min(Math.max(relativeClientX - bounds.left, 0), safeWidth);
  let nearestSegment = draggableSegments[0] || null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const segment of draggableSegments) {
    const segmentLeftPercent = Number(segment?.leftPercent) || 0;
    const segmentWidthPercent = Number(segment?.widthPercent) || 0;
    const segmentStartRatio = (segmentLeftPercent - (Number(groupItem?.leftPercent) || 0)) / safeGroupWidthPercent;
    const segmentEndRatio = (segmentLeftPercent + segmentWidthPercent - (Number(groupItem?.leftPercent) || 0)) / safeGroupWidthPercent;
    const segmentStartX = segmentStartRatio * safeWidth;
    const segmentEndX = segmentEndRatio * safeWidth;

    if (relativeX >= segmentStartX && relativeX <= segmentEndX) {
      return segment;
    }

    const segmentCenterX = (segmentStartX + segmentEndX) / 2;
    const distance = Math.abs(segmentCenterX - relativeX);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestSegment = segment;
    }
  }

  return nearestSegment;
}

function renderSegmentStatusIcon(iconName, className = "h-2.75 w-2.75") {
  // ShieldCheck a plus de marge interne que ArrowRightLeft : on booste pour égaliser le poids visuel.
  const protectedIconClassName = className === "h-2.75 w-2.75"
    ? "h-3.5 w-3.5"
    : className === "h-3 w-3"
      ? "h-4 w-4"
      : className === "h-3.5 w-3.5"
        ? "h-4.5 w-4.5"
        : className;

  if (iconName === "alert") {
    return <AlertTriangle aria-hidden="true" className={className} strokeWidth={2.15} />;
  }

  if (iconName === "pause") {
    return <Pause aria-hidden="true" className={className} strokeWidth={2.25} />;
  }

  if (iconName === "smartphone") {
    return <Smartphone aria-hidden="true" className={`${className} ${DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME}`} strokeWidth={2.15} />;
  }

  if (iconName === "rotate-ccw") {
    return <RotateCcw aria-hidden="true" className={className} strokeWidth={2.15} />;
  }

  if (iconName === "shuffle") {
    return <ArrowRightLeft aria-hidden="true" className={className} strokeWidth={2.65} />;
  }

  if (iconName === "sunset") {
    return <EndOfDayLoadSunsetIcon className={`${className} fleet-pm-sunset-icon`} strokeWidth={2.15} />;
  }

  if (iconName === "fuel") {
    return <Fuel aria-hidden="true" className={className} strokeWidth={2.15} />;
  }

  if (iconName === "moon") {
    return <Moon aria-hidden="true" className={className} strokeWidth={2.15} />;
  }

  if (iconName === "sun") {
    return <Sun aria-hidden="true" className={className} strokeWidth={2.15} />;
  }

  if (iconName === "lock") {
    return <Lock aria-hidden="true" className={protectedIconClassName} strokeWidth={2.75} />;
  }

  if (iconName === "shield") {
    return <ShieldCheck aria-hidden="true" className={protectedIconClassName} strokeWidth={2.25} />;
  }

  // « Livrée » : coche verte, distincte du bouclier de « Protégée ».
  if (iconName === "check") {
    return <CircleCheck aria-hidden="true" className={protectedIconClassName} strokeWidth={2.25} />;
  }

  return null;
}

function TimelineSegmentStatusMarkers({ markers = [], maxVisible = 3, size = "timeline" }) {
  if (!Array.isArray(markers) || !markers.length) {
    return null;
  }

  const visibleMarkers = markers.slice(0, maxVisible);
  const remainingCount = Math.max(0, markers.length - visibleMarkers.length);
  const isManualAssignmentMarker = (marker) => (
    ["reassigned", "protected", "protected-reordered", "end-of-day-load"].includes(marker?.key)
  );
  const isEnlargedStatusIcon = (marker) => (
    marker?.icon === "shuffle" || marker?.icon === "shield" || marker?.icon === "check" || marker?.icon === "lock" || marker?.icon === "sunset"
  );
  // Gantt à plat : carré Réaffectée / Protégée plus compact, symbole à sa taille d'origine pour rester net.
  const isFlatGanttSize = size === "timeline" || size === "compact";

  const resolveBadgeClassName = (marker) => {
    if (size === "relief") {
      return "inline-flex h-6 w-6 items-center justify-center rounded-[4px] border-2";
    }
    if (isManualAssignmentMarker(marker)) {
      return isFlatGanttSize
        ? "inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-[4px] border"
        : "inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border";
    }
    if (marker?.icon === "shield" || marker?.icon === "check" || marker?.icon === "lock" || marker?.icon === "sunset") {
      // Même taille en compact et timeline : les barres étroites gardent overflow-visible.
      if (size === "table") {
        return "inline-flex h-5.5 min-w-5.5 items-center justify-center rounded-[4px] border-2";
      }

      return "inline-flex h-5.5 min-w-5.5 items-center justify-center rounded-[4px] border-2";
    }

    if (marker?.icon === "shuffle") {
      if (size === "table") {
        return "inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border-2";
      }

      return "inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-[4px] border-2";
    }

    if (size === "table") {
      return "inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-[4px] border";
    }

    if (size === "compact") {
      return "inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-[4px] border";
    }

    return "inline-flex h-4 min-w-4 items-center justify-center rounded-[4px] border";
  };

  const resolveIconClassName = (marker) => {
    // Le soleil couchant n'a pas l'agrandissement du bouclier : il remplit le carré lui-même.
    if (marker?.icon === "sunset") {
      return "h-4.5 w-4.5";
    }

    if (isManualAssignmentMarker(marker) && marker?.icon === "shuffle" && size !== "table") {
      return "h-3.5 w-3.5";
    }

    if (isManualAssignmentMarker(marker) && (marker?.icon === "shield" || marker?.icon === "check" || marker?.icon === "lock")) {
      return "h-3 w-3";
    }

    if (isEnlargedStatusIcon(marker)) {
      return "h-3.5 w-3.5";
    }

    return "h-2.75 w-2.75";
  };
  const overflowBadgeClassName = size === "table"
    ? "inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-[4px] border border-[rgba(148,163,184,0.2)] bg-white text-[0.58rem] font-bold text-content-copy"
    : size === "compact"
      ? "inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-[4px] border border-[rgba(148,163,184,0.2)] bg-white text-[0.5rem] font-bold text-content-copy"
      : "inline-flex h-4 min-w-4 items-center justify-center rounded-[4px] border border-[rgba(148,163,184,0.2)] bg-white text-[0.54rem] font-bold text-content-copy";

  return (
    <span data-fleet-status-markers="true" className="flex shrink-0 items-center gap-1" aria-hidden="true">
      {visibleMarkers.map((marker) => (
        marker.icon === "smartphone" ? (
          <span key={marker.key}>
            {renderSegmentStatusIcon(marker.icon, size === "table" ? "h-4.5 w-4.5" : size === "compact" ? "h-4 w-4" : "h-4.5 w-4.5")}
          </span>
        ) : (
          <span
            key={marker.key}
            className={joinClasses([
              resolveBadgeClassName(marker),
              size === "relief" || isEnlargedStatusIcon(marker)
                ? (SEGMENT_STATUS_TONE_EMPHASIS_CLASS_NAMES[marker.tone] || SEGMENT_STATUS_TONE_EMPHASIS_CLASS_NAMES.success)
                : (SEGMENT_STATUS_TONE_CLASS_NAMES[marker.tone] || SEGMENT_STATUS_TONE_CLASS_NAMES.success),
            ])}
            style={size === "relief" || marker?.key === "protected" ? { borderColor: "white" } : undefined}
          >
            {renderSegmentStatusIcon(marker.icon, resolveIconClassName(marker))}
          </span>
        )
      ))}
      {remainingCount > 0 ? (
        <span className={joinClasses([overflowBadgeClassName])}>
          +{remainingCount}
        </span>
      ) : null}
    </span>
  );
}

function TimelineLegendItem({ className = "", label = "" }) {
  return (
    <span className="inline-flex items-center gap-1.25 text-[0.76rem] font-bold text-content-copy">
      <span className={joinClasses(["h-2.25 w-2.25 rounded-none border", className])} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function TimelineSequenceBadge({ className = "", compact = false, label = "", tone = "default" }) {
  return (
    <span
      aria-hidden="true"
      className={joinClasses([
        `inline-flex shrink-0 items-center justify-center border px-1 text-[10px] font-bold leading-none tracking-normal tabular-nums shadow-none [font-synthesis:none] ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`,
        compact ? "h-4.25 min-w-4.25" : "h-4 min-w-4",
        className || TIMELINE_SEQUENCE_BADGE_TONE_CLASS_NAMES[tone] || TIMELINE_SEQUENCE_BADGE_TONE_CLASS_NAMES.default,
      ])}
    >
      {label}
    </span>
  );
}

function TimelineProductLabelBadge({ className = "", label = "", title }) {
  return (
    <span data-fleet-product-badge="true" aria-hidden="true">
      <FleetProductPill
        className={joinClasses(["shadow-[0_2px_6px_rgba(15,23,42,0.08)]", className])}
        label={label}
        maxLength={7}
        title={title}
      />
    </span>
  );
}

function resolveInitialPreloadFleetRibbonClassName(anchor = "timeline", compact = false, tone = "veille") {
  const sharedClassName = `pointer-events-none absolute z-10 inline-flex items-center justify-center border uppercase shadow-[0_2px_6px_rgba(15,23,42,0.05)] ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`;

  if (anchor === "sequence") {
    return joinClasses([
      sharedClassName,
      "bg-[rgba(247,249,252,0.84)] px-0.75 py-0 font-bold tracking-[0.08em] text-[rgb(95,110,142)] border-[rgba(88,106,145,0.16)]",
      compact ? "w-10 text-[0.33rem]" : "w-12 text-[0.36rem]",
    ]);
  }

  return joinClasses([
    sharedClassName,
    "min-w-11 px-1 py-px font-black leading-none tracking-[0.04em]",
    // PM : la date doit rester lisible, d'où un texte plus grand que « Veille ».
    tone === "pm"
      ? "border-gray-800 bg-gray-800 text-[0.7rem] text-yellow-400 shadow-[0_2px_8px_rgba(15,23,42,0.18)]"
      : "border-[rgba(88,106,145,0.3)] bg-white/97 text-[0.48rem] text-[rgb(64,78,108)] shadow-[0_2px_8px_rgba(15,23,42,0.1)]",
  ]);
}

function resolveInitialPreloadFleetRibbonStyle(anchor = "timeline", sequenceVariant = "group") {
  if (anchor === "sequence") {
    return {
      left: "-1rem",
      top: sequenceVariant === "segment" ? "-0.15rem" : "-0.1rem",
      transform: "rotate(-32deg)",
    };
  }

  return {
    left: "-0.95rem",
    top: "-1.15rem",
    transform: "rotate(-18deg)",
    transformOrigin: "bottom right",
  };
}

function InitialPreloadFleetRibbon({
  anchor = "timeline",
  className = "",
  compact = false,
  compartmentFillBadge = null,
  sequenceVariant = "group",
  style = undefined,
  title = INITIAL_PRELOAD_FLEET_RIBBON_TITLE,
}) {
  // Case Veille trop étroite pour une pastille : le repère « EXP » suit l'étiquette.
  return (
    <span
      aria-hidden="true"
      title={title}
      className={joinClasses([
        resolveInitialPreloadFleetRibbonClassName(anchor, compact),
        className,
      ])}
      style={{ ...resolveInitialPreloadFleetRibbonStyle(anchor, sequenceVariant), ...style }}
    >
      {INITIAL_PRELOAD_FLEET_RIBBON_LABEL}
      {compartmentFillBadge ? (
        <span data-fleet-preload-compartment-fill="true" className="ml-0.5 whitespace-nowrap text-[rgb(162,28,175)]">
          · {compartmentFillBadge.label}
        </span>
      ) : null}
    </span>
  );
}

function EndOfDayLoadFleetRibbon({
  anchor = "timeline",
  className = "",
  compact = false,
  showChargedPmLabel = false,
  sourceDispatchDate = "",
  style = undefined,
  targetDispatchDate = "",
  title = "",
  visualMode = "next_day",
}) {
  const safeVisualMode = normalizeEndOfDayLoadModeForDisplay(visualMode);
  const labelOptions = { sourceDispatchDate, targetDispatchDate };
  const label = getEndOfDayLoadModeShortLabel(safeVisualMode, labelOptions);
  const isCompactKeyword = label === "Demain" || label === "Soir";
  const sizeClassName = isCompactKeyword
    ? (compact ? "h-4 min-w-8 px-1 text-[0.58rem]" : "h-4.5 min-w-10 px-1.25 text-[0.68rem]")
    : label.length > 8
      ? (compact ? "h-4.5 min-w-[3.95rem] px-1 text-[0.66rem]" : "h-5 min-w-[4.2rem] px-1.25 text-[0.76rem]")
      : (compact ? "h-4.5 min-w-[3.1rem] px-1.25 text-[0.72rem]" : "h-5 min-w-[3.3rem] px-1.5 text-[0.82rem]");
  const anchorClassName = anchor === "sequence"
    ? (compact ? "-left-4 top-[-0.1rem]" : "-left-4 top-[-0.15rem]")
    : isCompactKeyword
      ? (compact ? "-left-3 top-[-0.34rem]" : "-left-3.5 top-[-0.38rem]")
      : (compact ? "-left-4 top-[-0.42rem]" : "-left-4 top-[-0.46rem]");

  return (
    <span
      aria-hidden="true"
      title={title || getEndOfDayLoadFleetRibbonTitle(safeVisualMode, labelOptions)}
      className={joinClasses([
        `pointer-events-none absolute z-20 inline-flex items-center justify-center whitespace-nowrap border py-0 font-black uppercase leading-none tracking-[0.03em] shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`,
        // Vue inclinée : même bannière foncée « PM · date » que la vue à plat.
        showChargedPmLabel ? "border-gray-800 bg-gray-800 text-yellow-400" : "border-white/90 bg-white text-[rgb(220,38,38)]",
        sizeClassName,
        anchorClassName,
        className,
      ])}
      style={{ transform: "none", ...style }}
    >
      {showChargedPmLabel ? (
        <span>PM · <span data-fleet-pm-date="true">{label}</span></span>
      ) : <span>{label}</span>}
    </span>
  );
}

function TimelinePriorityBadge({ compact = false, label = "", title = "" }) {
  if (!label) {
    return null;
  }

  return (
    <span
      data-fleet-priority-badge="true"
      aria-hidden="true"
      title={title || undefined}
      className={joinClasses([
        `inline-flex shrink-0 items-center justify-center border font-black leading-none shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`,
        compact ? "h-3.5 min-w-3.5 px-1 text-[0.48rem]" : "h-4 min-w-4 px-1.25 text-[0.54rem]",
        TIMELINE_PRIORITY_BADGE_TONE_CLASS_NAMES[label] || TIMELINE_PRIORITY_BADGE_TONE_CLASS_NAMES.P3,
      ])}
    >
      {label}
    </span>
  );
}

function TimelineBridgeIcon({ className = "", strokeWidth = 2.5 }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={strokeWidth}
      viewBox="0 0 24 24"
    >
      <path d="M6 20V4" />
      <path d="M18 20V4" />
      <path d="m22 11-4-4A7.5 7.5 0 0 1 6 7l-4 4" />
      <path d="M22 16H2" />
      <path d="M10 9.728V16" />
      <path d="M14 9.728V16" />
    </svg>
  );
}

function TimelineContextBadge({ className = "", compact = false, icon = "", label = "", style = undefined, title = "", tone = "neutral" }) {
  const IconComponent = icon === "sailboat" || icon === "ship"
    ? Sailboat
    : icon === "bridge"
      ? TimelineBridgeIcon
      : null;
  const isIconOnlyBadge = Boolean(IconComponent && !label);
  if (!label && !IconComponent) {
    return null;
  }

  return (
    <span
      data-fleet-context-badge="true"
      data-fleet-context-icon={IconComponent ? "true" : undefined}
      aria-hidden="true"
      title={title || undefined}
      className={joinClasses([
        `inline-flex shrink-0 items-center justify-center border font-bold leading-none shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`,
        IconComponent
          ? isIconOnlyBadge
            ? (compact ? "h-5 w-5 p-0" : "h-5.5 w-5.5 p-0")
            : (compact ? "h-3.5 w-3.5 p-0" : "h-4 w-4 p-0")
          : (compact ? "h-3.5 min-w-3.5 px-1 text-[0.46rem]" : "h-4 min-w-4 px-1.5 text-[0.52rem]"),
        className || TIMELINE_CONTEXT_BADGE_TONE_CLASS_NAMES[tone] || TIMELINE_CONTEXT_BADGE_TONE_CLASS_NAMES.neutral,
      ])}
      style={style}
    >
      {IconComponent ? (
        <IconComponent
          aria-hidden="true"
          className={isIconOnlyBadge ? (compact ? "h-3.5 w-3.5" : "h-4 w-4") : (compact ? "h-2.5 w-2.5" : "h-3 w-3")}
          strokeWidth={isIconOnlyBadge ? 2.5 : 2.25}
        />
      ) : label}
    </span>
  );
}

function TimelineInlineIndicators({
  compact = false,
  contextBadges = [],
  markers = [],
  maxVisible = 3,
  priorityLabel = "",
  priorityTitle = "",
}) {
  const resolvedContextBadges = Array.isArray(contextBadges) ? contextBadges.filter(Boolean) : [];

  if (!priorityLabel && !resolvedContextBadges.length && (!Array.isArray(markers) || !markers.length)) {
    return null;
  }

  return (
    <span className="flex shrink-0 items-center gap-1">
      {priorityLabel ? <TimelinePriorityBadge compact={compact} label={priorityLabel} title={priorityTitle} /> : null}
      {resolvedContextBadges.map((badge) => (
        <TimelineContextBadge
          key={badge.key || badge.label || badge.icon}
          className={badge.className}
          compact={compact}
          icon={badge.icon}
          label={badge.label}
          style={badge.style}
          title={badge.title}
          tone={badge.tone}
        />
      ))}
      {Array.isArray(markers) && markers.length ? (
        <TimelineSegmentStatusMarkers
          markers={markers}
          maxVisible={maxVisible}
          size={compact ? "compact" : "timeline"}
        />
      ) : null}
    </span>
  );
}

export function TimelinePerspectiveIndicators({
  markers = [], contextBadges = [], productLabel = "", productLabels = [], productTitle = "",
  priorityLabel = "", priorityTitle = "", ribbon = null,
}) {
  const icons = buildFleetPerspectiveIcons(markers, contextBadges, { productLabel, productLabels, productTitle, priorityLabel, priorityTitle });
  if (!icons.length && !ribbon) return null;
  return (
    <span className="fleet-perspective-stack" data-fleet-icon-stack={icons.length}>
      <span className="fleet-perspective-panels">
        {icons.map(({ kind, value }, index) => (
          <span
            key={`${kind}:${value.key || value.icon}:${index}`}
            className="fleet-perspective-panel"
            data-fleet-perspective-kind={kind}
            data-fleet-perspective-row={kind === "priority" || (kind === "context" && value.tone === "schedule") ? "schedule" : undefined}
            title={value.title || value.label || undefined}
            aria-label={value.title || value.label || value.icon}
          >
            {kind === "status"
              ? <TimelineSegmentStatusMarkers markers={[value]} maxVisible={1} size="relief" />
              : kind === "product"
                ? <TimelineProductLabelBadge {...value} />
                : kind === "priority"
                  ? <TimelinePriorityBadge {...value} compact />
                  : <TimelineContextBadge {...value} compact />}
          </span>
        ))}
        {ribbon ? <span className="fleet-perspective-ribbon">{ribbon}</span> : null}
      </span>
    </span>
  );
}

function FleetTimelineSearchField({ onQueryChange = null, query = "" }) {
  const inputId = React.useId();
  const inputRef = React.useRef(null);
  const hasQuery = typeof query === "string" && query.trim().length > 0;

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "/" && !isShortcutTypingTarget(event)) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="w-full min-[720px]:w-[15rem]">
      <div className="flex min-h-9 min-w-0 items-center gap-2 rounded-[7px] border border-[rgba(148,163,184,0.18)] bg-white px-3 shadow-none transition duration-150 focus-within:border-border-accent-muted focus-within:ring-2 focus-within:ring-accent/20 focus-within:ring-offset-1">
        <input
          id={inputId}
          ref={inputRef}
          type="search"
          title="Raccourci : /"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              onQueryChange?.("");
              event.currentTarget.blur();
            }
          }}
          aria-label="Rechercher un camion, une commande ou un bon de travail dans la vue flotte"
          className="min-w-0 flex-1 border-0 bg-transparent py-0 text-[0.72rem] leading-4 text-text outline-hidden placeholder:text-content-copy"
          placeholder="Camion, chauffeur, commande, bon de travail..."
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => {
            onQueryChange?.(event.target.value);
          }}
        />
        <button
          type="button"
          className={joinClasses([
            "inline-flex shrink-0 items-center rounded-[6px] border border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.92)] px-2 py-0.5 text-[0.62rem] font-semibold leading-none text-content-copy transition duration-150 hover:border-border-accent-muted hover:bg-white hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
            hasQuery ? "opacity-100" : "pointer-events-none opacity-0",
          ])}
          aria-hidden={hasQuery ? undefined : "true"}
          disabled={!hasQuery}
          onClick={() => {
            onQueryChange?.("");
          }}
        >
          Effacer
        </button>
      </div>
    </div>
  );
}

const FLEET_TIMELINE_LEGEND_HINTS_TITLE = "Pastille = numéro d'étape; pour un segment C+D fusionné, elle affiche la plage des commandes. Partie ombrée = temps de service sur l'arrêt, hors séquences compactées au même site.";

function FleetTimelineLegend({ className = "", showHints = true }) {
  return (
    <div className={joinClasses([
      "flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[0.72rem] font-bold leading-4 text-content-copy",
      className,
    ])} title={showHints ? undefined : FLEET_TIMELINE_LEGEND_HINTS_TITLE}>
      <TimelineLegendItem className="border-[var(--dispatch-legend-loading-border)] bg-[var(--dispatch-segment-loading-fill)]" label="Chargement" />
      <TimelineLegendItem className="border-[var(--dispatch-legend-delivery-border)] bg-[var(--dispatch-segment-delivery-fill)]" label="Livraison" />
      <TimelineLegendItem className="border-[var(--dispatch-legend-return-border)] bg-[var(--dispatch-legend-return-fill)]" label="Retour dépôt" />
      {showHints ? (
        <>
          <span className="whitespace-nowrap" title="Pastille = numéro d'étape; pour un segment C+D fusionné, elle affiche la plage des commandes.">Pastille = séquence</span>
          <span className="whitespace-nowrap" title="Partie ombrée = temps de service sur l'arrêt, hors séquences compactées au même site">Ombre = temps de service</span>
        </>
      ) : null}
    </div>
  );
}

function FleetTimelineToolbar({ productLabels = [] }) {
  if (!productLabels.length) {
    return null;
  }

  return (
    <div
      data-fleet-timeline-toolbar="true"
      data-fleet-timeline-legend-products="true"
      className="flex min-w-0 flex-wrap items-center gap-1.25 rounded-[4px] border border-[rgba(15,92,87,0.12)] bg-[rgba(248,250,252,0.82)] px-2.5 py-0.75"
    >
      <span className="text-[0.72rem] font-bold text-content-copy">Produits</span>
      {productLabels.map((label) => (
        <FleetProductPill
          key={label}
          className="font-extrabold! uppercase tracking-[0.07em]"
          label={label}
          maxLength={Infinity}
          paddingClassName="px-1.5 py-0.5 text-[0.6rem]"
        />
      ))}
    </div>
  );
}

const FLEET_TOOLBAR_BUTTON_CLASS_NAME = "inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] border border-slate-300 bg-white px-2.75 py-1.5 text-[0.68rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2";

function FleetTimelineZoomSelect({ onZoomChange = null, zoomKey = DEFAULT_FLEET_TIMELINE_ZOOM_KEY }) {
  return (
    <select
      aria-label="Zoom temporel"
      title="Zoom temporel"
      value={zoomKey}
      onChange={(event) => onZoomChange?.(event.target.value)}
      className={joinClasses([FLEET_TOOLBAR_BUTTON_CLASS_NAME, "cursor-pointer py-0 pl-2! pr-6"])}
    >
      {FLEET_TIMELINE_ZOOM_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
    </select>
  );
}

const FLEET_TIMELINE_SHORTCUTS = [
  ["← / →", "Jour précédent / suivant"],
  ["/", "Rechercher dans le Gantt"],
  ["Échap", "Effacer la recherche"],
];

function FleetTimelineShortcutsHelp() {
  return (
    <HoverCard
      title="Raccourcis clavier"
      content={(
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[0.72rem]">
          {FLEET_TIMELINE_SHORTCUTS.map(([keys, label]) => (
            <React.Fragment key={keys}>
              <dt className="font-bold text-text"><kbd className="rounded-[4px] border border-slate-300 bg-slate-50 px-1.5 font-sans">{keys}</kbd></dt>
              <dd className="m-0 text-content-copy">{label}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      placement="bottom-end"
      delay={120}
      referenceAsChild
      flat
      compactTitle
    >
      <button type="button" aria-label="Raccourcis clavier" className={FLEET_TOOLBAR_BUTTON_CLASS_NAME}>
        <Keyboard aria-hidden="true" className="h-4 w-4" strokeWidth={2.15} />
      </button>
    </HoverCard>
  );
}

// Trait rouge à l'heure courante, seulement quand le Gantt affiche la journée d'aujourd'hui.
function FleetTimelineNowLine({ dispatchDate = "", endMinutes = 0, leadingPaddingPercent = 0, startMinutes = 0, trailingPaddingPercent = 0 }) {
  const [now, setNow] = React.useState(() => new Date());
  const isToday = dispatchDate === getLocalTodayDispatchDate();

  React.useEffect(() => {
    if (!isToday) {
      return undefined;
    }
    setNow(new Date());
    const intervalId = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(intervalId);
  }, [isToday]);

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (!isToday || !(endMinutes > startMinutes) || nowMinutes < startMinutes || nowMinutes > endMinutes) {
    return null;
  }

  const leading = Math.min(Math.max(Number(leadingPaddingPercent) || 0, 0), 40);
  const trailing = Math.min(Math.max(Number(trailingPaddingPercent) || 0, 0), 40);
  const left = `${leading + ((nowMinutes - startMinutes) / (endMinutes - startMinutes)) * (100 - leading - trailing)}%`;

  return (
    <div aria-hidden="true" data-fleet-now-line="true" className={joinClasses(["grid gap-px border-x border-transparent px-1", FLEET_TIMELINE_TRUCK_COLUMN_GRID])}>
      <div className="hidden min-[1120px]:block" />
      <div className="relative border-x border-transparent">
        <span className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-red-600" style={{ left }} />
        <span className="absolute top-0 -translate-x-1/2 rounded-[4px] bg-red-600 px-1.25 text-[0.66rem] font-bold leading-4 text-white" style={{ left }}>
          {formatTimelineClockLabel(nowMinutes)}
        </span>
      </div>
    </div>
  );
}

function FleetTimelineScale({ startMinutes = 0, endMinutes = 0, leadingPaddingPercent = 0, trailingPaddingPercent = 0 }) {
  const hourTicks = resolveTimelineHourTicks(startMinutes, endMinutes, { leadingPaddingPercent, trailingPaddingPercent });

  return (
    <div className={joinClasses(["grid gap-px border-x border-transparent px-1 min-[1120px]:items-center", FLEET_TIMELINE_TRUCK_COLUMN_GRID])}>
      <div className="hidden min-[1120px]:block" aria-hidden="true" />

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] items-center">
        <div
          data-fleet-timeline-scale="true"
          className="grid gap-1 overflow-visible rounded-[4px] border border-[rgba(15,92,87,0.1)] bg-[rgba(248,250,252,0.82)] py-1.4 text-[0.76rem] font-bold uppercase tracking-[0.08em] text-text"
        >
          <div data-fleet-scale-track="true" className="relative h-8 overflow-visible">
            <span className="absolute inset-x-0 top-[1.3rem] h-px bg-[rgba(148,163,184,0.28)]" />
            {hourTicks.map((tick) => (
              <React.Fragment key={tick.minutes}>
                <span
                  data-fleet-scale-tick={tick.label}
                  className="absolute top-0 whitespace-nowrap text-[0.74rem] leading-none normal-case tracking-normal text-content-ink"
                  style={getTimelineTickLabelStyle(tick)}
                >
                  {tick.label}
                </span>
                <span
                  aria-hidden="true"
                  className="absolute top-[1.05rem] h-3.5 w-px bg-[rgba(148,163,184,0.4)]"
                  style={{
                    left: tick.percent <= 0 ? 0 : tick.percent >= 100 ? "100%" : `${tick.percent}%`,
                    transform: tick.percent <= 0 ? "none" : tick.percent >= 100 ? "translateX(-100%)" : "translateX(-50%)",
                  }}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FleetTruckFilterControls({
  hideUnusedTrucks = false,
  isOpen = false,
  onSelectAll = null,
  onToggleHideUnusedTrucks = null,
  onToggleOpen = null,
  onToggleTruck = null,
  panelRef = null,
  selectedTruckCount = 0,
  selectedTruckIdSet = null,
  totalTruckCount = 0,
  truckOptions = [],
  triggerRef = null,
}) {
  const resolvedSelectedTruckIdSet = selectedTruckIdSet instanceof Set ? selectedTruckIdSet : new Set();
  const hasCustomSelection = totalTruckCount > 0 && selectedTruckCount < totalTruckCount;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={isOpen ? "true" : "false"}
          aria-haspopup="dialog"
          onClick={() => {
            onToggleOpen?.();
          }}
          className={joinClasses([
            "inline-flex min-h-9 items-center gap-2 rounded-[7px] border bg-white px-3 py-1.5 text-[0.72rem] font-semibold text-text transition hover:border-border-accent-muted hover:text-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
            isOpen ? "border-border-accent-strong shadow-[0_10px_24px_rgba(15,92,87,0.08)]" : "border-[rgba(148,163,184,0.18)]",
          ])}
        >
          <span>Camions</span>
          <span className={joinClasses([
            "inline-flex min-w-10 items-center justify-center rounded-[6px] border px-2 py-0.75 text-[0.66rem] font-extrabold leading-none",
            hasCustomSelection
              ? "border-border-accent-muted bg-[rgba(238,247,245,0.92)] text-(--color-legacy-teal)"
              : "border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.92)] text-content-copy",
          ])}>
            {selectedTruckCount}/{totalTruckCount}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={joinClasses(["h-4 w-4 transition-transform", isOpen ? "rotate-180" : ""])}
            strokeWidth={2.15}
          />
        </button>

        {isOpen ? (
          <div
            ref={panelRef}
            className="absolute right-0 top-[calc(100%+0.45rem)] z-30 grid w-[min(22rem,calc(100vw-2rem))] gap-1.5 rounded-[8px] border border-[rgba(148,163,184,0.2)] bg-white/98 p-2.5 shadow-[0_22px_44px_rgba(15,23,42,0.14)] backdrop-blur-md"
            role="dialog"
            aria-label="Filtrer les camions affichés"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="grid gap-0.5 min-w-0">
                <p className="m-0 text-[0.62rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">Filtrer les lignes</p>
                <p className="m-0 text-[0.68rem] leading-4.5 text-content-copy">Affichez un ou plusieurs camions dans le Gantt.</p>
              </div>

              <button
                type="button"
                onClick={() => {
                  onSelectAll?.();
                }}
                className="inline-flex shrink-0 items-center rounded-[6px] border border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.92)] px-2.25 py-0.85 text-[0.62rem] font-semibold text-content-copy transition hover:border-border-accent-muted hover:bg-white hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2"
              >
                Tout afficher
              </button>
            </div>

            <div className="grid max-h-[min(26rem,calc(100vh-10rem))] gap-0.75 overflow-y-auto pr-0.5">
              {truckOptions.map((option) => {
                const isChecked = resolvedSelectedTruckIdSet.has(option.assignmentId);
                const isLocked = isChecked && selectedTruckCount <= 1;

                return (
                  <label
                    key={option.assignmentId}
                    className={joinClasses([
                      "grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1.5 rounded-[4px] border px-2.5 py-1.5 transition",
                      isChecked
                        ? "border-slate-400 bg-slate-100"
                        : "border-[rgba(148,163,184,0.14)] bg-[rgba(248,250,252,0.78)] hover:border-[rgba(148,163,184,0.24)] hover:bg-white",
                      isLocked ? "opacity-70" : "",
                    ])}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isLocked}
                      onChange={() => {
                        onToggleTruck?.(option.assignmentId);
                      }}
                      className="h-3.25 w-3.25 rounded border-[rgba(148,163,184,0.4)] text-(--color-legacy-teal) focus:ring-border-accent-strong"
                    />

                    <span className="flex min-w-0 items-center justify-between gap-2">
                      <span className="truncate text-[0.72rem] font-semibold leading-4 text-text">{option.label}</span>
                      {option.meta && option.meta !== "Aucune étape planifiée" ? (
                        <span className="text-[0.6rem] leading-4 text-content-copy">{option.meta}</span>
                      ) : null}
                    </span>

                    {!option.hasTrips ? (
                      <span className="inline-flex shrink-0 items-center rounded-full border border-[rgba(148,163,184,0.18)] bg-white px-1.75 py-0.6 text-[0.58rem] font-semibold text-content-copy">
                        Inactif
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      <label className="inline-flex min-h-9 items-center gap-2 px-1.5 py-1.5 text-[0.72rem] font-medium text-white">
        <input
          type="checkbox"
          checked={hideUnusedTrucks}
          onChange={(event) => {
            onToggleHideUnusedTrucks?.(event.target.checked);
          }}
          className="h-3.5 w-3.5 rounded border-[rgba(148,163,184,0.4)] text-(--color-legacy-teal) focus:ring-border-accent-strong"
        />
        <span className="whitespace-nowrap" title="Masquer les camions non utilisés">Masquer inutilisés</span>
      </label>
    </div>
  );
}

function FleetReassignmentDragNotice({ dragState = null }) {
  if (!dragState) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  if (dragState.status === "blocked") {
    const pointerClientX = Number.isFinite(Number(dragState?.pointerClientX)) ? Number(dragState.pointerClientX) : 24;
    const pointerClientY = Number.isFinite(Number(dragState?.pointerClientY)) ? Number(dragState.pointerClientY) : 24;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;
    const popoverWidth = 388;
    const popoverHeight = 140;
    const left = Math.min(
      Math.max(pointerClientX + 18, 16),
      Math.max(16, viewportWidth - popoverWidth - 16),
    );
    const top = Math.min(
      Math.max(pointerClientY - 18, 16),
      Math.max(16, viewportHeight - popoverHeight - 16),
    );

    return createPortal(
      <div
        role="status"
        aria-atomic="true"
        aria-live="polite"
        data-fleet-reassignment-blocked="true"
        data-fleet-reassignment-drag-mode="true"
        data-fleet-reassignment-drag-popover="true"
        className="pointer-events-none fixed z-50 w-fit max-w-[calc(100vw-2rem)] rounded-[4px] border border-border-danger-strong bg-danger-soft px-3.5 py-3 text-[0.74rem] leading-5 text-danger shadow-card backdrop-blur-[10px]"
        style={{ left: `${left}px`, top: `${top}px` }}
      >
        <div className="flex items-start gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-border-danger-muted bg-white text-danger shadow-none">
            <Ban aria-hidden="true" className="h-4.5 w-4.5" strokeWidth={2.1} />
          </span>
          <div className="grid min-w-0 gap-0.5">
            <p className="m-0 text-[0.84rem] font-semibold leading-5 text-danger">Déplacement temporairement bloqué</p>
            <p className="m-0 text-[0.74rem] leading-5 text-danger">
              {getPlanningMutationActivityMessage() || "Une mise à jour du planning est déjà en cours."}
              <br />
              Attendez sa fin avant de déplacer une autre commande.
            </p>
          </div>
        </div>
      </div>,
      getReactPortalRoot(),
    );
  }

  const hasActionableCompatibilityIndex = Boolean(
    dragState?.actionableCompatibilityByTruckId
    && typeof dragState.actionableCompatibilityByTruckId === "object",
  );
  const compatibleTruckCount = (hasActionableCompatibilityIndex
    ? Object.keys(dragState.actionableCompatibilityByTruckId).length
    : (Array.isArray(dragState.compatibleTruckIds) ? dragState.compatibleTruckIds.length : 0))
    + Object.keys(dragState.eveningActionableCompatibilityByTruckId || {}).length;
  const hiddenTruckCount = Number.isFinite(Number(dragState.hiddenTruckCount))
    ? Math.max(Number(dragState.hiddenTruckCount), 0)
    : 0;
  const sourceLabel = typeof dragState?.sourceLabel === "string" ? dragState.sourceLabel.trim() : "";
  const sourcePreviewMeta = typeof dragState?.sourcePreviewMeta === "string" ? dragState.sourcePreviewMeta.trim() : "";
  const showDragPreview = dragState.dragKind === "assignment"
    && (dragState.status === "loading" || dragState.status === "ready");
  const isSuspendedDeliveryDrag = dragState?.dragKind === "suspended";
  const hiddenTruckMessage = hiddenTruckCount > 0
    ? (isSuspendedDeliveryDrag
      ? `${hiddenTruckCount} camion(s) non éligible(s) restent visibles mais ne peuvent pas recevoir cette réactivation.`
      : `${hiddenTruckCount} camion(s) non éligible(s) restent affichés mais ne peuvent pas recevoir cette commande.`)
    : "";
  const hoveredBlockedTarget = dragState.status === "ready" ? dragState.hoveredBlockedTarget : null;
  const pointerClientX = Number.isFinite(Number(dragState?.pointerClientX)) ? Number(dragState.pointerClientX) : 24;
  const pointerClientY = Number.isFinite(Number(dragState?.pointerClientY)) ? Number(dragState.pointerClientY) : 24;
  const headline = isSuspendedDeliveryDrag
    ? "Déposer sur un camion"
    : dragState.status === "ready"
    ? compatibleTruckCount > 0
      ? `${compatibleTruckCount} camion(s) éligibles`
      : "Déposer pour suspendre"
    : dragState.status === "loading"
      ? "Recherche des camions éligibles"
      : "Réaffectation indisponible";
  const detail = isSuspendedDeliveryDrag
    ? (sourceLabel
      ? `${sourceLabel}. Relâchez sur la ligne du camion qui doit reprendre cette commande.`
      : "Relâchez sur la ligne du camion qui doit reprendre cette commande.")
    : dragState.status === "ready"
    ? compatibleTruckCount > 0
      ? sourceLabel
        ? `${sourceLabel}. Seules les lignes marquées Compatible peuvent recevoir cette commande. Vous pouvez aussi la déposer dans Suspendues.`
        : "Seules les lignes marquées Compatible peuvent recevoir cette commande. Vous pouvez aussi la déposer dans Suspendues."
      : dragState.message || (sourceLabel
        ? `${sourceLabel}. Déposez la commande dans la zone Suspendues pour la retirer de la tournée.`
        : "Déposez la commande dans la zone Suspendues pour la retirer de la tournée.")
    : dragState.message || "Préparation en cours.";
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;
  const popoverWidth = 320;
  const popoverHeight = hiddenTruckCount > 0 || hoveredBlockedTarget ? 176 : 152;
  const left = Math.min(
    Math.max(pointerClientX + 18, 16),
    Math.max(16, viewportWidth - popoverWidth - 16),
  );
  const top = Math.min(
    Math.max(pointerClientY - 18, 16),
    Math.max(16, viewportHeight - popoverHeight - 16),
  );

  return createPortal(
    <div
      className="pointer-events-none [&_*]:pointer-events-none"
      ref={showDragPreview ? (node) => {
        if (node) {
          const preview = node.firstElementChild;
          const previewBounds = preview.getBoundingClientRect();
          const centeredLeft = Math.min(
            Math.max(pointerClientX - previewBounds.width / 2, 16),
            Math.max(16, viewportWidth - previewBounds.width - 16),
          );
          const centeredTop = Math.min(
            Math.max(pointerClientY - previewBounds.height / 2, 16),
            Math.max(16, viewportHeight - previewBounds.height - 16),
          );
          // Use viewport bounds to account for any offset of the portal's containing block.
          preview.style.left = `${Number.parseFloat(preview.style.left) + centeredLeft - previewBounds.left}px`;
          preview.style.top = `${Number.parseFloat(preview.style.top) + centeredTop - previewBounds.top}px`;
        }
      } : null}
    >
      {showDragPreview ? (
        <div
          aria-hidden="true"
          data-fleet-reassignment-drag-preview="true"
          className="pointer-events-none fixed z-[60] flex h-7.5 w-60 max-w-[calc(100vw-2rem)] items-center gap-2 overflow-hidden rounded-[4px] border border-accent-strong bg-accent px-2 text-[0.7rem] font-semibold text-white opacity-50 shadow-card"
          style={{ left: `${pointerClientX}px`, top: `${pointerClientY}px` }}
        >
          <span className="min-w-0 flex-1 truncate">{sourceLabel || (dragState.isGroupDrag ? "Groupe" : "Commande")}</span>
          {sourcePreviewMeta ? <span className="max-w-[40%] shrink-0 truncate text-[0.62rem] font-medium text-white/85">{sourcePreviewMeta}</span> : null}
        </div>
      ) : null}
      <div
        data-fleet-reassignment-drag-mode="true"
        data-fleet-reassignment-drag-popover="true"
        className="pointer-events-none fixed z-50 w-[20rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-[rgba(217,119,6,0.22)] bg-[linear-gradient(180deg,rgba(255,251,235,0.98),rgba(255,247,237,0.95))] px-3.5 py-3 text-[0.74rem] leading-5 text-[rgb(154,52,18)] shadow-[0_24px_48px_rgba(217,119,6,0.14)] backdrop-blur-[10px]"
        style={{ left: `${left}px`, top: `${top}px` }}
      >
        <div className="grid gap-2">
          <div className="flex items-start gap-2.5">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(217,119,6,0.18)] bg-white text-[rgb(154,52,18)] shadow-[0_8px_18px_rgba(217,119,6,0.08)]">
              <ArrowRightLeft aria-hidden="true" className="h-4.5 w-4.5" strokeWidth={2.1} />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <p className="m-0 text-[0.69rem] font-extrabold uppercase tracking-[0.12em] text-[rgba(154,52,18,0.92)]">{isSuspendedDeliveryDrag ? "Mode réactivation" : "Mode réaffectation"}</p>
              <p className="m-0 text-[0.84rem] font-semibold leading-5 text-[rgb(124,45,18)]">{headline}</p>
              <p className="m-0 text-[0.74rem] leading-5 text-[rgb(146,64,14)]">{detail}</p>
            </div>
          </div>

          {hoveredBlockedTarget ? (
            <div data-fleet-reassignment-blocked-reason="true" className="rounded-[8px] border border-[rgba(124,45,18,0.18)] border-l-[3px] border-l-[#c5524a] bg-white px-3 py-2 text-[0.72rem] leading-5">
              <p className="m-0 font-bold text-[rgb(127,29,29)]">{hoveredBlockedTarget.truckName} · non éligible</p>
              <p className="m-0 text-[rgb(124,45,18)]">{hoveredBlockedTarget.reason}</p>
            </div>
          ) : hiddenTruckMessage ? (
            <div className="rounded-[8px] border border-[rgba(217,119,6,0.14)] bg-white/72 px-3 py-2 text-[0.72rem] leading-5 text-[rgb(124,45,18)]">
              {hiddenTruckMessage}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    getReactPortalRoot(),
  );
}

function FleetReassignmentUndoNotice({ onUndo = null, undoState = null }) {
  if (!undoState) {
    return null;
  }

  const sourceTruckName = typeof undoState.sourceTruckName === "string" && undoState.sourceTruckName.trim()
    ? undoState.sourceTruckName.trim()
    : undoState.sourceTruckId || "le camion précédent";
  const targetTruckName = typeof undoState.targetTruckName === "string" && undoState.targetTruckName.trim()
    ? undoState.targetTruckName.trim()
    : undoState.targetTruckId || "le camion actuel";
  const sourceLabel = typeof undoState.sourceLabel === "string" ? undoState.sourceLabel.trim() : "";

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-[8px] border border-[rgba(15,92,87,0.16)] bg-[linear-gradient(180deg,rgba(240,249,246,0.98),rgba(232,245,241,0.96))] px-3 py-2 shadow-[0_12px_26px_rgba(15,92,87,0.08)]">
      <div className="grid min-w-0 gap-0.5">
        <p className="m-0 text-[0.66rem] font-black uppercase tracking-[0.14em] text-[rgba(15,92,87,0.74)]">Dernier déplacement</p>
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <p className="m-0 min-w-0 flex-1 truncate text-[0.82rem] font-semibold leading-5 text-[rgb(17,94,89)]" title={sourceLabel || `Commande déplacée vers ${targetTruckName}`}>
            {sourceLabel ? `${sourceLabel} vers ${targetTruckName}` : `Commande déplacée vers ${targetTruckName}`}
          </p>
          <p className="m-0 text-[0.72rem] leading-5 text-[rgba(17,94,89,0.84)]">
            Si le segment a été déposé sur le mauvais camion, revenez vers {sourceTruckName}.
          </p>
        </div>
      </div>

    </div>
  );
}

function normalizeTruckProductLabel(productLabel = "") {
  return typeof productLabel === "string" ? productLabel.trim() : "";
}

function truncateTruckProductLabel(productLabel = "", maxLength = 7) {
  const normalizedLabel = normalizeTruckProductLabel(productLabel);
  if (!normalizedLabel) {
    return "";
  }

  return Array.from(normalizedLabel).slice(0, maxLength).join("");
}

function truncateTimelineCompactLabel(label = "", maxLength = 1) {
  const normalizedLabel = typeof label === "string" ? label.trim() : "";
  if (!normalizedLabel) {
    return "";
  }

  return Array.from(normalizedLabel).slice(0, Math.max(1, maxLength)).join("");
}

function getCompactTimelineLabelMaxLength(widthPercent = 0) {
  if (widthPercent < 3.6) {
    return 1;
  }

  if (widthPercent < 5) {
    return 2;
  }

  if (widthPercent < 6.5) {
    return 4;
  }

  return 10;
}

function getTruckProductTone(productLabel = "") {
  return getFleetProductTone(normalizeTruckProductLabel(productLabel));
}

const FLEET_PRODUCT_PILL_BASE_CLASS_NAME = `inline-flex shrink-0 items-center border font-semibold leading-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`;

const FLEET_PRODUCT_PILL_DEFAULT_PADDING_CLASS_NAME = "px-1.25 py-0.5 text-[0.62rem]";

// Détail « Produit en expansion » : limite par compartiment utilisé, sans infobulle native.
export function CompartmentFillHoverCard({ children, segments = [], truckCompartments = [], truckCapacityTons = null }) {
  const impact = buildCompartmentFillImpact(segments, { truckCompartments, truckCapacityTons });
  if (!impact) {
    return children;
  }

  const columns = [impact.showOrder ? "Commande" : "", "Compartiment", "Calcul", "Max", "Chargé"].filter(Boolean);
  const content = (
    <div className="grid gap-1 text-[0.68rem] leading-4 text-content-copy">
      <p className="m-0">{impact.note}</p>
      {impact.rows.length ? (
        <div className="grid gap-x-2.5 gap-y-0.5 tabular-nums" style={{ gridTemplateColumns: `repeat(${columns.length}, auto)` }}>
          {columns.map((column) => (
            <span key={column} className="text-[0.6rem] font-semibold uppercase tracking-[0.04em] text-content-muted">{column}</span>
          ))}
          {impact.rows.map((row, index) => (
            <React.Fragment key={index}>
              {impact.showOrder ? <span className="whitespace-nowrap">{row.orderLabel}</span> : null}
              <span>{row.compartmentLabel}</span>
              <span className="whitespace-nowrap">{row.calculationLabel}</span>
              <span className="whitespace-nowrap font-semibold">{row.maxLabel}</span>
              <span className={joinClasses(["whitespace-nowrap", row.exceedsMax ? "font-semibold text-amber-700" : ""])}>{row.loadedLabel}</span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <p className="m-0 text-content-muted">Répartition par compartiment indisponible pour cette étape.</p>
      )}
      {impact.exceedsNote ? <p className="m-0 text-amber-700">{impact.exceedsNote}</p> : null}
    </div>
  );

  return (
    <HoverCard title="Produit en expansion" content={content} placement="top" delay={120} accentColor="rgb(162,28,175)" referenceAsChild flat compactTitle tight>
      {children}
    </HoverCard>
  );
}

function FleetCompartmentFillPill({ percent = null, segments = [], assignment = null }) {
  const label = buildCompartmentFillIndicatorLabel(normalizeCompartmentFillPercent(percent));
  if (!label) {
    return null;
  }

  return (
    <CompartmentFillHoverCard segments={segments} truckCompartments={assignment?.truck_compartments} truckCapacityTons={assignment?.capacity_tons}>
      <span
        className={joinClasses([
          FLEET_PRODUCT_PILL_BASE_CLASS_NAME,
          FLEET_PRODUCT_PILL_DEFAULT_PADDING_CLASS_NAME,
          TIMELINE_CONTEXT_BADGE_TONE_CLASS_NAMES.expansion,
        ])}
      >
        {label}
      </span>
    </CompartmentFillHoverCard>
  );
}

function FleetProductPill({
  className = "",
  label = "",
  maxLength = 7,
  paddingClassName = FLEET_PRODUCT_PILL_DEFAULT_PADDING_CLASS_NAME,
  title,
}) {
  const normalizedLabel = normalizeTruckProductLabel(label);
  const displayLabel = truncateTruckProductLabel(normalizedLabel, maxLength);
  const { accentColor } = getTruckProductTone(normalizedLabel);

  if (!displayLabel) {
    return null;
  }

  return (
    <span
      className={joinClasses([
        FLEET_PRODUCT_PILL_BASE_CLASS_NAME,
        paddingClassName,
        "text-white",
        className,
      ])}
      style={{ backgroundColor: accentColor, borderColor: accentColor }}
      title={title ?? normalizedLabel}
    >
      {displayLabel}
    </span>
  );
}

const TIMELINE_SEGMENT_TAB_CLASS_NAME = "inline-flex h-[0.93rem] items-center whitespace-nowrap rounded-t-[4px] px-1.5 text-[0.6rem] font-extrabold uppercase leading-none tracking-[0.07em]";
// Bannière en travers de l'onglet produit : part du bord du segment (sans déborder sur la colonne camion)
// et penche peu, pour laisser le produit lisible.
const TIMELINE_SEGMENT_BANNER_STYLE = { left: "0.15rem", top: "-1.45rem", transform: "rotate(-10deg)", transformOrigin: "center" };

// Haut d'un segment : onglet du produit qui ouvre une suite d'étapes, avec par-dessus une bannière
// en diagonale « Veille » (préchargement) ou « PM · jour » (foncée). Sur le jour de livraison, le PM
// chargé la veille affiche « PM · Veille ».
// L'onglet produit ne dépasse pas l'onglet produit suivant (roomPercent) : à l'étroit, il se resserre,
// puis son nom est coupé, le « +N » restant visible.
export function TimelineSegmentTabs({ endOfDayLoad = null, initialPreload = null, itemKey = "", leftPercent = 0, productLabel = "", productLabels = [], roomPercent = null }) {
  const normalizedProductLabel = normalizeTruckProductLabel(productLabel);
  const hasMultipleProducts = productLabels.length > 1;
  const hasProductTabRoom = Boolean(normalizedProductLabel) && Number.isFinite(roomPercent);
  const endOfDayLoadLabel = endOfDayLoad
    ? (endOfDayLoad.isSourcePreload
      ? (normalizeEndOfDayLoadModeForDisplay(endOfDayLoad.visualMode) === "evening" ? "Jour" : INITIAL_PRELOAD_FLEET_RIBBON_LABEL)
      : getEndOfDayLoadModeShortLabel(normalizeEndOfDayLoadModeForDisplay(endOfDayLoad.visualMode), endOfDayLoad))
    : "";
  if (!normalizedProductLabel && !endOfDayLoadLabel && !initialPreload) {
    return null;
  }

  return (
    <span
      aria-hidden="true"
      className={joinClasses(["pointer-events-none absolute top-0.5 z-30", hasProductTabRoom ? "@container" : ""])}
      data-fleet-segment-tabs-item-key={itemKey || undefined}
      style={{ left: `${leftPercent}%`, width: hasProductTabRoom ? `${roomPercent}%` : undefined }}
    >
      {normalizedProductLabel ? (
        <span
          data-fleet-product-run-tab="true"
          // Seul l'onglet à plusieurs produits réagit au survol, pour montrer sa liste de produits.
          className={joinClasses([TIMELINE_SEGMENT_TAB_CLASS_NAME, "absolute bottom-[-1px] left-0 max-w-full text-white @max-[4rem]:px-0.5 @max-[4rem]:text-[0.55rem] @max-[4rem]:tracking-normal", hasMultipleProducts ? "pl-0.5! pr-0! pointer-events-auto" : ""])}
          style={{ backgroundColor: getTruckProductTone(normalizedProductLabel).accentColor }}
          title={hasMultipleProducts ? `${productLabels.length} produits : ${productLabels.join(", ")}` : undefined}
        >
          <span className="min-w-0 overflow-hidden">{normalizedProductLabel}</span>
          {/* Le « +N » prend la couleur des autres produits livrés au même arrêt. */}
          {hasMultipleProducts ? <span data-fleet-product-count="true" className="ml-0.5 inline-flex shrink-0 items-center self-stretch rounded-tr-[4px] px-[0.2rem] font-black tracking-normal" style={{ background: getFleetProductBandsBackground(productLabels.slice(1), "90deg") }}>+{productLabels.length - 1}</span> : null}
        </span>
      ) : null}
      {endOfDayLoadLabel ? (
        <span
          data-fleet-pm-banner="true"
          className={joinClasses([resolveInitialPreloadFleetRibbonClassName("timeline", false, "pm"), "whitespace-nowrap"])}
          // Décalé à gauche pour dégager l'onglet produit. En début de Gantt, il déborderait sur la colonne
          // camion : il monte et penche moins à la place.
          style={{ ...TIMELINE_SEGMENT_BANNER_STYLE, ...(leftPercent < 4 ? { top: "-1.85rem", transform: "rotate(-5deg)" } : { left: "-2.6rem", top: "-1.55rem" }) }}
          title={getEndOfDayLoadFleetRibbonTitle(normalizeEndOfDayLoadModeForDisplay(endOfDayLoad.visualMode), endOfDayLoad)}
        >
          PM · {endOfDayLoadLabel}
        </span>
      ) : initialPreload ? (
        <InitialPreloadFleetRibbon className="whitespace-nowrap" compartmentFillBadge={initialPreload.compartmentFillBadge} style={TIMELINE_SEGMENT_BANNER_STYLE} />
      ) : null}
    </span>
  );
}

// Trait de la couleur du produit sur le bord gauche du même segment : l'onglet et le trait forment un « L ».
// Livraison de plusieurs produits : le trait est coupé en une bande par produit.
export function TimelineProductRunStripe({ label = "", labels = [] }) {
  const normalizedLabel = normalizeTruckProductLabel(label);
  if (!normalizedLabel) {
    return null;
  }

  return (
    <span
      aria-hidden="true"
      data-fleet-product-run-stripe="true"
      className="pointer-events-none absolute inset-y-0 left-0 z-5 w-[3px] rounded-l-[3px]"
      style={{ background: getFleetProductBandsBackground(labels.length > 1 ? labels : [normalizedLabel]) }}
    />
  );
}

function isLoadingOrDeliveryStopType(stopType = "") {
  const normalizedStopType = typeof stopType === "string" ? stopType.trim().toLowerCase() : "";
  return normalizedStopType === "loading" || normalizedStopType === "delivery";
}

function resolveSegmentHoverCardProductLabel(segment) {
  if (!segment || !isLoadingOrDeliveryStopType(segment?.stopType)) {
    return "";
  }

  return normalizeTruckProductLabel(
    segment?.productLabel
    || (segment?.assignmentIsManualMix ? segment?.manualMixCurrentProductLabel : "")
    || "",
  );
}

function TruckHoverCardMetricTile({ item }) {
  const isEstimatedCostItem = item?.key === "estimated-cost" || item?.label === "Coût estimé";

  return (
    <div className="flex min-w-0 items-center justify-between gap-2 rounded-[8px] border border-[rgba(121,108,90,0.14)] bg-[rgba(250,246,239,0.92)] px-2.5 py-1.5">
      <span className={joinClasses([
        "truncate text-[0.58rem] font-black uppercase tracking-[0.12em] text-content-faint",
        isEstimatedCostItem ? "text-[#94636c]" : "",
      ])}
      >
        {item.label}
      </span>
      <span className={joinClasses([
        "shrink-0 whitespace-nowrap text-[0.84rem] font-semibold leading-none",
        isEstimatedCostItem
          ? "text-[#7a2732]"
          : item?.isAccent
            ? "text-(--color-legacy-teal)"
            : "text-content-ink",
      ])}
      >
        {item.value}
      </span>
    </div>
  );
}

function TruckHoverCardContent({ row, shell, trip, truckSourceChanges = null }) {
  const kpiItems = getTruckHoverCardKpiItems(withPendingTruckSourceValues(row?.assignment || null, truckSourceChanges), trip || null);

  return (
    <div className="grid min-w-88 gap-1.75">
      <div className="grid gap-0.5">
        <div className="text-[0.62rem] font-black uppercase tracking-[0.16em] text-content-faint">Camion</div>
        <div className="text-[0.9rem] font-semibold leading-tight text-content-ink">{shell.truckTitle}</div>
      </div>

      {kpiItems.length ? (
        <div className="grid gap-1.25 sm:grid-cols-2">
          {kpiItems.map((item) => <TruckHoverCardMetricTile key={item.key || item.label} item={item} />)}
        </div>
      ) : (
        <p className="m-0 text-[0.71rem] leading-[1.45] text-content-muted">Aucune tournée active pour ce camion dans la lecture courante.</p>
      )}
    </div>
  );
}

function resolveFleetHoverCardInfoPillLabel(label = "") {
  const normalized = typeof label === "string" ? label.trim() : "";
  if (normalized === "Fournisseur" || normalized === "Fournisseurs") {
    return "Four.";
  }
  return normalized;
}

function FleetSegmentHoverCardSummary({
  title = "",
  trailingInfoItems = [],
  primaryAddress = "",
  destinationAddress = "",
  primaryMapsUrl = "",
  destinationMapsUrl = "",
  routeSummary = "",
}) {
  const normalizedTitle = typeof title === "string" ? title.trim() : "";
  const normalizedRouteSummary = typeof routeSummary === "string" ? routeSummary.trim() : "";
  const showDestinationAddressLine = Boolean(destinationAddress);

  if (
    !normalizedTitle
    && !trailingInfoItems.length
    && !primaryAddress
    && !showDestinationAddressLine
    && !normalizedRouteSummary
  ) {
    return null;
  }

  return (
    <div className="grid min-w-0 gap-0.5">
      {normalizedTitle || trailingInfoItems.length ? (
        <div className="flex flex-wrap items-start justify-between gap-2">
          {normalizedTitle ? (
            <div className="min-w-0 flex-1 text-[12px] font-semibold leading-[1.45] text-content-ink">{normalizedTitle}</div>
          ) : null}
          {trailingInfoItems.length ? (
            <div className="flex max-w-full flex-wrap justify-end gap-1">
              {trailingInfoItems.map((item) => (
                <SegmentInfoPill
                  key={item.key}
                  label={resolveFleetHoverCardInfoPillLabel(item.label)}
                  title={item.title || item.value}
                  value={item.value}
                  className={joinClasses(["max-w-full bg-white/90 px-2 py-0.65 text-[0.64rem]", item.className || ""])}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {primaryAddress ? (
        <HoverCardAddressLine
          value={primaryAddress}
          className="text-[11px] leading-[1.4] text-content-muted"
          directionsUrl={primaryMapsUrl}
        />
      ) : null}
      {showDestinationAddressLine ? (
        <HoverCardAddressLine
          value={destinationAddress}
          variant="destination"
          className="text-[11px] leading-[1.4] text-content-muted"
          directionsUrl={destinationMapsUrl}
        />
      ) : null}
      {normalizedRouteSummary && normalizedRouteSummary !== normalizedTitle ? (
        <div className="text-[11px] leading-normal text-content-muted">{normalizedRouteSummary}</div>
      ) : null}
    </div>
  );
}

function resolveSegmentOrderInfoItem(segment = null, row = null) {
  const segmentInfoItems = buildSegmentInfoItems({
    businessStep: segment?.businessStep || null,
    row,
    segment,
  });

  return segmentInfoItems.find((item) => item?.key === "order") || null;
}

function renderSegmentHoverCardOrderTitleTrailing(segment = null, row = null) {
  if (
    isMergedOperationalMissionSegment(segment)
    && buildGroupedSegmentOrderHoverEntries(segment?.mergedMemberSegments).length > 1
  ) {
    return null;
  }

  const orderInfoItem = resolveSegmentOrderInfoItem(segment, row);
  const workOrderLabel = resolveSegmentWorkOrderLabel({
    businessStep: segment?.businessStep || null,
    segment,
  });

  if (!orderInfoItem?.value && !workOrderLabel) {
    return null;
  }

  return (
    <div className="flex min-w-0 items-center gap-1">
      {orderInfoItem?.value ? (
        <SegmentInfoPill
          key={orderInfoItem.key}
          label={orderInfoItem.label}
          title={orderInfoItem.title || orderInfoItem.value}
          value={orderInfoItem.value}
          className="border-[rgba(148,163,184,0.22)] bg-white px-2 py-0.5 text-[0.62rem]"
        />
      ) : null}
      {workOrderLabel ? (
        <SegmentInfoPill
          key="work-order"
          label="BT"
          title={`Bon de travail ${workOrderLabel}`}
          value={workOrderLabel}
          className="border-[rgba(148,163,184,0.22)] bg-white px-2 py-0.5 text-[0.62rem]"
        />
      ) : null}
    </div>
  );
}

function buildFleetSegmentHoverCardTrailingInfoItems({
  segment = null,
  row = null,
  showContextualPartyInfo = true,
  showInlineCounterpartyByOrder = false,
} = {}) {
  const segmentInfoItems = buildSegmentInfoItems({
    businessStep: segment?.businessStep || null,
    row,
    segment,
  });
  const inlineCounterpartyLabel = showInlineCounterpartyByOrder
    ? resolveSequenceInlineCounterpartyLabel(segment)
    : "";
  const contextualItems = showInlineCounterpartyByOrder
    ? []
    : showContextualPartyInfo
      ? segmentInfoItems.filter((item) => item?.key === "client" || item?.key === "supplier")
      : [];

  const trailingInfoItems = [];

  contextualItems.forEach((item) => {
    trailingInfoItems.push({
      key: item.key,
      label: item.label,
      title: item.title || item.value,
      value: item.value,
      className: item.className,
    });
  });

  if (showInlineCounterpartyByOrder && inlineCounterpartyLabel) {
    trailingInfoItems.push({
      key: "inline-counterparty",
      label: "Client",
      title: inlineCounterpartyLabel,
      value: inlineCounterpartyLabel,
    });
  }

  return trailingInfoItems;
}

function SegmentSummaryBlock({
  row = null,
  segment,
  headingClassName = "text-[0.95rem] font-semibold leading-tight text-content-ink",
  addressClassName = "text-[0.72rem] leading-[1.45] text-content-muted",
  routeClassName = "text-[0.72rem] leading-[1.45] text-content-muted",
  infoPillClassName = "max-w-full px-2.25 py-0.85 text-[0.66rem]",
  showHeading = true,
  showAddress = true,
  showContextualPartyInfo = true,
  showInfoPills = true,
  showInlineCounterpartyByOrder = false,
  showRouteSummary = true,
}) {
  const segmentInfoItems = buildSegmentInfoItems({
    businessStep: segment?.businessStep || null,
    row,
    segment,
  });
  const orderInfoItem = segmentInfoItems.find((item) => item?.key === "order") || null;
  const inlineCounterpartyLabel = showInlineCounterpartyByOrder
    ? resolveSequenceInlineCounterpartyLabel(segment)
    : "";
  const visibleSegmentInfoItems = showInlineCounterpartyByOrder
    ? []
    : showContextualPartyInfo
      ? segmentInfoItems
      : segmentInfoItems.filter((item) => item?.key !== "client" && item?.key !== "supplier");
  const primaryTitle = resolveSegmentPrimaryTitle(segment);
  const primaryAddress = resolveSegmentPrimaryAddress(segment);
  const destinationAddress = resolveSegmentDestinationAddress(segment);
  const showDestinationAddressLine = showAddress && destinationAddress;
  const primaryMapsUrl = showDestinationAddressLine ? "" : resolveSegmentAddressMapsUrl(segment, primaryAddress);
  const destinationMapsUrl = resolveSegmentAddressMapsUrl(segment, destinationAddress || primaryAddress);
  const routeSummary = resolveSegmentRouteSummary(segment);

  return (
    <div className="grid gap-1">
      {showInfoPills && showInlineCounterpartyByOrder && orderInfoItem ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1.25">
          <SegmentInfoPill
            key={orderInfoItem.key}
            label={orderInfoItem.label}
            title={orderInfoItem.title || orderInfoItem.value}
            value={orderInfoItem.value}
            className={joinClasses([infoPillClassName, orderInfoItem.className || ""])}
          />
          {inlineCounterpartyLabel ? (
            <span
              className="min-w-0 truncate text-[0.68rem] font-semibold leading-none text-content-copy"
              title={inlineCounterpartyLabel}
            >
              {inlineCounterpartyLabel}
            </span>
          ) : null}
        </div>
      ) : showInfoPills && visibleSegmentInfoItems.length ? (
        <div className="flex flex-wrap items-center gap-1.25">
          {visibleSegmentInfoItems.map((item) => (
            <SegmentInfoPill
              key={item.key}
              label={item.label}
              title={item.title || item.value}
              value={item.value}
              className={joinClasses([infoPillClassName, item.className || ""])}
            />
          ))}
        </div>
      ) : null}
      {showHeading || showAddress || showRouteSummary ? (
        <div className="grid gap-0.5">
          {showHeading ? <div className={headingClassName}>{primaryTitle}</div> : null}
          {showAddress && primaryAddress ? (
            <HoverCardAddressLine
              value={primaryAddress}
              className={addressClassName}
              directionsUrl={primaryMapsUrl}
            />
          ) : null}
          {showDestinationAddressLine ? (
            <HoverCardAddressLine
              value={destinationAddress}
              variant="destination"
              className={addressClassName}
              directionsUrl={destinationMapsUrl}
            />
          ) : null}
          {showRouteSummary && routeSummary && routeSummary !== primaryTitle ? <div className={routeClassName}>{routeSummary}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function HoverCardAddressLine({ className = "", directionsUrl = "", value = "", variant = "current" }) {
  if (!value) {
    return null;
  }

  const displayValue = formatSegmentHoverCardAddress(value) || value;

  const Icon = variant === "destination" ? ArrowRightLeft : MapPin;
  const iconClassName = variant === "destination"
    ? "mt-[0.08rem] h-3 w-3 shrink-0 text-[rgba(56,99,142,0.72)]"
    : "mt-[0.08rem] h-3 w-3 shrink-0 text-[rgba(100,116,139,0.8)]";

  return (
    <div
      className={joinClasses([
        "grid w-full min-w-0 max-w-full items-start gap-x-1.5",
        directionsUrl ? "grid-cols-[auto_minmax(0,1fr)_auto]" : "grid-cols-[auto_minmax(0,1fr)]",
        className,
      ])}
    >
      <Icon aria-hidden="true" className={joinClasses([iconClassName, "col-start-1 row-start-1"])} strokeWidth={2.05} />
      <span className="col-start-2 min-w-0 truncate whitespace-nowrap" title={value}>{displayValue}</span>
      {directionsUrl ? (
        <a
          href={directionsUrl}
          target="_blank"
          rel="noreferrer noopener"
          aria-label="Ouvrir l’itinéraire dans Google Maps"
          title="Ouvrir l’itinéraire dans Google Maps"
          className="col-start-3 row-start-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[rgba(148,163,184,0.24)] bg-white/78 text-[rgba(56,99,142,0.86)] shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition hover:border-[rgba(56,99,142,0.28)] hover:bg-white hover:text-[rgba(37,99,235,0.92)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[rgba(59,130,246,0.32)]"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Route aria-hidden="true" className="h-3.15 w-3.15" strokeWidth={2.05} />
        </a>
      ) : null}
    </div>
  );
}

function resolveFleetSegmentComparisonErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  return fallbackMessage;
}

async function fetchFleetSegmentComparison({ routeSegmentIndex, signal, tripId }) {
  const searchParams = new URLSearchParams({
    trip_id: tripId,
    route_segment_index: String(routeSegmentIndex),
  });
  const dispatchDate = readCurrentDispatchDate();
  if (dispatchDate) {
    searchParams.set("dispatch_date", dispatchDate);
  }
  const response = await fetchWithSupabaseAuth(`/api/results/latest/segment-comparison?${searchParams.toString()}`, { signal });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(resolveFleetSegmentComparisonErrorMessage(payload, "Impossible de charger le comparatif du segment."));
  }

  return payload;
}

async function fetchRouteAlternatives({ routeSegmentIndex, signal, tripId }) {
  const searchParams = new URLSearchParams({
    trip_id: tripId,
    route_segment_index: String(routeSegmentIndex),
  });
  const dispatchDate = readCurrentDispatchDate();
  if (dispatchDate) {
    searchParams.set("dispatch_date", dispatchDate);
  }
  const response = await fetchWithSupabaseAuth(`/api/results/latest/route-alternatives?${searchParams.toString()}`, { signal });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(resolveFleetSegmentComparisonErrorMessage(payload, "Impossible de charger l'alternative de route."));
  }

  return payload;
}

function normalizeFleetRouteSegmentIndexes(routeSegmentIndex, routeSegmentIndexes = []) {
  const candidates = Array.isArray(routeSegmentIndexes) && routeSegmentIndexes.length
    ? [...routeSegmentIndexes]
    : [routeSegmentIndex];
  const indexes = [];
  const seen = new Set();

  candidates.forEach((value) => {
    const parsedIndex = Number.parseInt(String(value ?? ""), 10);
    if (!Number.isInteger(parsedIndex) || parsedIndex <= 0 || seen.has(parsedIndex)) {
      return;
    }

    seen.add(parsedIndex);
    indexes.push(parsedIndex);
  });

  return indexes;
}

function resolveFleetSegmentCurrentTravelDurationMinutes(segment) {
  const effectiveDisplayTravelDurationMinutes = Number(segment?.displayTravelDurationMinutes);
  if (Number.isFinite(effectiveDisplayTravelDurationMinutes) && effectiveDisplayTravelDurationMinutes >= 0) {
    return effectiveDisplayTravelDurationMinutes;
  }

  const fleetDisplayedTravelDurationMinutes = Number(segment?.fleetDisplayedTravelDurationMinutes);
  if (Number.isFinite(fleetDisplayedTravelDurationMinutes) && fleetDisplayedTravelDurationMinutes >= 0) {
    return fleetDisplayedTravelDurationMinutes;
  }

  const travelDurationMinutes = Number(segment?.travelDurationMinutes);
  if (Number.isFinite(travelDurationMinutes) && travelDurationMinutes >= 0) {
    return travelDurationMinutes;
  }

  return 0;
}

function resolveFleetSegmentLegacyTravelDurationMinutes(segment) {
  const legacyDisplayTravelDurationMinutes = Number(segment?.legacyDisplayTravelDurationMinutes);
  if (Number.isFinite(legacyDisplayTravelDurationMinutes) && legacyDisplayTravelDurationMinutes >= 0) {
    return legacyDisplayTravelDurationMinutes;
  }

  const displayTravelDurationMinutes = Number(segment?.displayTravelDurationMinutes);
  if (Number.isFinite(displayTravelDurationMinutes) && displayTravelDurationMinutes >= 0) {
    return displayTravelDurationMinutes;
  }

  const travelDurationMinutes = Number(segment?.travelDurationMinutes);
  if (Number.isFinite(travelDurationMinutes) && travelDurationMinutes >= 0) {
    return travelDurationMinutes;
  }

  return 0;
}

function resolveFleetSegmentCurrentHereTrafficDurationMinutes(segment) {
  const scheduledTravelDurationMinutes = Number(segment?.routeScheduledTravelDurationMinutes);
  if (shouldUseFleetSegmentComparisonCurrentDuration(segment, scheduledTravelDurationMinutes)) {
    return scheduledTravelDurationMinutes;
  }

  return null;
}

function buildFleetDisplayedTravelSegment(segment, hovercardContext = null) {
  if (!segment || typeof segment !== "object") {
    return segment;
  }

  const legacyDisplayTravelDurationMinutes = resolveFleetSegmentLegacyTravelDurationMinutes(segment);
  const hereTravelDurationMinutes = resolveFleetSegmentCurrentHereTrafficDurationMinutes(segment);
  const useHereTraffic = hovercardContext?.here_time_aware_routing_enabled === true
    && Number.isFinite(hereTravelDurationMinutes)
    && hereTravelDurationMinutes >= 0;

  return {
    ...segment,
    displayTravelDurationMinutes: useHereTraffic ? hereTravelDurationMinutes : legacyDisplayTravelDurationMinutes,
    fleetDisplayedTravelDurationMinutes: useHereTraffic ? hereTravelDurationMinutes : legacyDisplayTravelDurationMinutes,
    fleetDisplayedTravelUsesHereTraffic: useHereTraffic,
    legacyDisplayTravelDurationMinutes,
  };
}

function buildFleetDisplayedTravelSegments(segments = [], hovercardContext = null) {
  return (Array.isArray(segments) ? segments : []).filter(Boolean).map((segment) => buildFleetDisplayedTravelSegment(segment, hovercardContext));
}

function buildFleetTimelineSourceSegments(row, {
  activeDispatchDate = "",
  fleetSegmentComparisonByKey = null,
  fleetSegmentHovercardContext = null,
} = {}) {
  return buildFleetDisplayedSegmentTimings(
    buildAspiratorMissionDisplaySegments(applyFleetSegmentComparisonDurations(
      buildFleetDisplayedTravelSegments(Array.isArray(row?.segments) ? row.segments : [], fleetSegmentHovercardContext),
      fleetSegmentComparisonByKey,
      fleetSegmentHovercardContext,
      activeDispatchDate,
    )),
    fleetSegmentHovercardContext,
  );
}

function resolveRowDisplayedTimelineEndMinutes(row, options = {}) {
  const displayedSegments = buildFleetTimelineDisplaySegments(
    buildFleetTimelineSourceSegments(row, options),
  );
  const endCandidates = displayedSegments
    .filter((segment) => segment && segment.isInitialPreload !== true && segment.isEndOfDayLoadSourcePreload !== true && segment.isEndOfDayLoad !== true)
    .map((segment) => resolveSegmentBusinessEndMinutes(segment))
    .filter((value) => value != null);

  if (!endCandidates.length) {
    return null;
  }

  const rowStartMinutes = Number(row?.timelineStartMinutes);
  const displayedEndMinutes = Math.max(...endCandidates);

  return Number.isFinite(rowStartMinutes)
    ? Math.max(rowStartMinutes, displayedEndMinutes)
    : displayedEndMinutes;
}

function hasDisplayableTimelineDuration(segment) {
  if (!segment || typeof segment !== "object") {
    return false;
  }

  return [
    segment.displayedSegmentTotalDurationMinutes,
    segment.visualTimelineDurationMinutes,
    segment.timelineDurationMinutes,
    segment.baseTimelineDurationMinutes,
    segment.displayTravelDurationMinutes,
    segment.travelDurationMinutes,
    segment.serviceDurationMinutes,
    segment.manualBufferMinutes,
  ].some((value) => {
    const durationMinutes = Number(value);
    return Number.isFinite(durationMinutes) && durationMinutes > 0;
  });
}

function resolveRowDisplayedTimelineStartMinutes(row, options = {}) {
  const displayedSegments = buildFleetTimelineDisplaySegments(
    buildFleetTimelineSourceSegments(row, options),
  );
  const startCandidates = displayedSegments
    .filter((segment) => segment && segment.isInitialPreload !== true && segment.isEndOfDayLoadSourcePreload !== true && segment.isEndOfDayLoad !== true && hasDisplayableTimelineDuration(segment))
    .map((segment) => resolveSegmentBusinessStartMinutes(segment))
    .filter((value) => value != null);

  return startCandidates.length ? Math.min(...startCandidates) : null;
}

function buildFleetTimelineBoundRows(rows = [], options = {}) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const displayedTimelineStartMinutes = resolveRowDisplayedTimelineStartMinutes(row, options);
    const displayedTimelineEndMinutes = resolveRowDisplayedTimelineEndMinutes(row, options);

    return {
      ...row,
      displayedTimelineEndMinutes,
      displayedTimelineStartMinutes,
    };
  });
}

function isFleetDurationChip(chip) {
  const chipKey = typeof chip?.key === "string" ? chip.key.trim() : "";
  const chipKind = typeof chip?.kind === "string" ? chip.kind.trim() : "";
  return chipKey === "svc"
    || chipKey === "dur"
    || chipKey === "manual_buffer"
    || chipKind === "svc"
    || chipKind === "dur"
    || chipKind === "manual_buffer";
}

function sortFleetDurationChips(chips = []) {
  const resolvedChips = Array.isArray(chips) ? chips.filter(Boolean) : [];

  return [...resolvedChips].sort((leftChip, rightChip) => {
    const leftKind = typeof leftChip?.kind === "string" ? leftChip.kind.trim() : typeof leftChip?.key === "string" ? leftChip.key.trim() : "";
    const rightKind = typeof rightChip?.kind === "string" ? rightChip.kind.trim() : typeof rightChip?.key === "string" ? rightChip.key.trim() : "";
    const leftRank = leftKind === "svc" ? 0 : leftKind === "dur" ? 1 : leftKind === "manual_buffer" ? 2 : 3;
    const rightRank = rightKind === "svc" ? 0 : rightKind === "dur" ? 1 : rightKind === "manual_buffer" ? 2 : 3;
    return leftRank - rightRank;
  });
}

function FleetSegmentComparisonSection({
  segment,
  comparisonPayloadOverride = null,
  displayOverrides = null,
  headerTrailingAccessory = null,
  hovercardContext = null,
  currentDurationMinutesOverride = null,
  currentHereTrafficDurationMinutesOverride = null,
  durationChips = [],
  legacyDurationMinutesOverride = null,
  manualBufferMinutesOverride = null,
  serviceDurationMinutesOverride = null,
}) {
  const comparisonEnabled = hovercardContext?.fleet_segment_hovercard_comparison_enabled === true;
  const comparisonPayloadProvided = comparisonPayloadOverride && typeof comparisonPayloadOverride === "object";
  const fallbackIndex = Math.max((Number(segment?.globalSequence) || 1) - 1, 0);
  const currentDurationMinutes = Number.isFinite(currentDurationMinutesOverride) && currentDurationMinutesOverride >= 0
    ? currentDurationMinutesOverride
    : getSameSiteSequenceDisplayTravelDurationMinutes(segment, displayOverrides, fallbackIndex);
  const tripId = typeof segment?.tripId === "string" ? segment.tripId.trim() : "";
  const routeSegmentIndex = Number.parseInt(segment?.routeSegmentIndex, 10);
  const comparisonRefreshSignature = [
    readCurrentDispatchDate() || "",
    tripId,
    Number.isInteger(routeSegmentIndex) ? routeSegmentIndex : "",
    segment?.globalSequence ?? "",
    segment?.distanceLabel ?? "",
    segment?.displayTravelDurationMinutes ?? "",
    segment?.travelDurationMinutes ?? "",
    segment?.segmentEndMinutes ?? "",
    segment?.visualSegmentEndMinutes ?? "",
    currentDurationMinutes ?? "",
  ].join(":");
  const [comparisonState, setComparisonState] = React.useState(() => {
    if (!comparisonEnabled) {
      return { payload: null, status: "idle" };
    }
    if (!tripId || !Number.isInteger(routeSegmentIndex) || routeSegmentIndex < 1) {
      return {
        payload: {
          here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
          message: "Comparatif indisponible pour ce segment.",
          status: "unavailable",
        },
        status: "unavailable",
      };
    }
    return { payload: null, status: "loading" };
  });

  React.useEffect(() => {
    if (!comparisonEnabled || comparisonPayloadProvided) {
      return undefined;
    }

    if (!tripId || !Number.isInteger(routeSegmentIndex) || routeSegmentIndex < 1) {
      setComparisonState({
        payload: {
          here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
          message: "Comparatif indisponible pour ce segment.",
          status: "unavailable",
        },
        status: "unavailable",
      });
      return undefined;
    }

    const abortController = new AbortController();
    setComparisonState({ payload: null, status: "loading" });
    fetchFleetSegmentComparison({
      tripId,
      routeSegmentIndex,
      signal: abortController.signal,
    })
      .then((payload) => {
        setComparisonState({
          payload,
          status: payload?.status === "available" ? "ready" : "unavailable",
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setComparisonState({
          payload: {
            here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
            message: error instanceof Error && error.message ? error.message : "Comparatif indisponible pour ce segment.",
            status: "unavailable",
          },
          status: "unavailable",
        });
      });

    return () => {
      abortController.abort();
    };
  }, [comparisonEnabled, comparisonPayloadProvided, hovercardContext?.here_time_aware_routing_enabled, comparisonRefreshSignature]);

  if (!comparisonEnabled) {
    return null;
  }

  const payload = comparisonPayloadProvided ? comparisonPayloadOverride : comparisonState.payload;
  const comparisonStatus = comparisonPayloadProvided
    ? (comparisonPayloadOverride?.status === "available" ? "ready" : "unavailable")
    : comparisonState.status;
  const effectiveHereTimeAwareRoutingEnabled = typeof payload?.here_time_aware_routing_enabled === "boolean"
    ? payload.here_time_aware_routing_enabled
    : hovercardContext?.here_time_aware_routing_enabled === true;
  const legacyDurationVisibleWhenHereEnabled = hovercardContext?.fleet_segment_hovercard_legacy_duration_visible_when_here_enabled === true;
  const manualDurationLabel = resolveFleetSequenceManualDurationLabel(segment);
  const comparisonDurationChips = sortFleetDurationChips(Array.isArray(durationChips) ? durationChips.filter(isFleetDurationChip) : []);
  const chipServiceDurationMinutes = resolveFleetSegmentDurationMinutesFromChips(comparisonDurationChips, "svc");
  const chipDisplayedTravelDurationMinutes = resolveFleetSegmentDurationMinutesFromChips(comparisonDurationChips, "dur");
  const chipDisplayedTravelDurationAvailable = shouldUseFleetSegmentComparisonCurrentDuration(
    segment,
    chipDisplayedTravelDurationMinutes,
  );
  const meunerieExtraMinutes = resolveFleetSequenceMeunerieExtraMinutes(segment);
  const aspiratorServiceMinutes = resolveFleetSequenceAspiratorServiceMinutes(segment);
  const serviceExtraMinutes = meunerieExtraMinutes + aspiratorServiceMinutes;
  const displayedServiceChipMinutes = chipServiceDurationMinutes;
  const displayedServiceDurationMinutes = serviceDurationMinutesOverride != null
    ? normalizeFleetSegmentDurationMinutes(serviceDurationMinutesOverride) ?? 0
    : displayedServiceChipMinutes != null && serviceExtraMinutes > 0
      ? displayedServiceChipMinutes + serviceExtraMinutes
      : normalizeFleetSegmentDurationMinutes(segment?.serviceDurationMinutes)
      ?? displayedServiceChipMinutes
      ?? 0;
  const legacyTravelDurationMinutes = Number.isFinite(legacyDurationMinutesOverride) && legacyDurationMinutesOverride >= 0
    ? legacyDurationMinutesOverride
    : getSameSiteSequenceLegacyTravelDurationMinutes(segment, displayOverrides, fallbackIndex);
  const payloadCurrentDurationMinutes = Number(payload?.current_duration_minutes);
  const payloadCurrentDurationAvailable = payload?.status === "available"
    && shouldUseFleetSegmentComparisonCurrentDuration(segment, payloadCurrentDurationMinutes);
  const currentHereTrafficDurationMinutes = resolveFleetSegmentCurrentHereDurationMinutes({
    overrideMinutes: currentHereTrafficDurationMinutesOverride,
    payloadAvailable: payloadCurrentDurationAvailable,
    payloadMinutes: payloadCurrentDurationMinutes,
    fallbackMinutes: resolveFleetSegmentCurrentHereTrafficDurationMinutes(segment),
  });
  const currentHereTrafficDurationAvailable = Number.isFinite(currentHereTrafficDurationMinutes) && currentHereTrafficDurationMinutes >= 0;
  const displayedTravelUsesHereTraffic = effectiveHereTimeAwareRoutingEnabled === true && currentHereTrafficDurationAvailable;
  const displayedTravelDurationMinutes = displayedTravelUsesHereTraffic
    ? currentHereTrafficDurationMinutes
    : chipDisplayedTravelDurationAvailable
      ? chipDisplayedTravelDurationMinutes
      : currentDurationMinutes;
  const displayedTravelDurationChipLabel = formatFleetSequenceDurationChipLabel(displayedTravelDurationMinutes, { approximate: true });
  const comparisonDurationMinutes = Number(payload?.comparison_duration_minutes);
  const comparisonAvailable = payload?.status === "available" && Number.isFinite(comparisonDurationMinutes) && comparisonDurationMinutes >= 0;
  const effectiveManualBufferMinutes = normalizeFleetSegmentDurationMinutes(manualBufferMinutesOverride)
    ?? normalizeFleetSegmentDurationMinutes(segment?.manualBufferMinutes)
    ?? 0;
  const displayedTotalDurationMinutes = resolveFleetSegmentDisplayedTotalDurationMinutes({
    currentDurationMinutes,
    currentHereTrafficDurationAvailable,
    currentHereTrafficDurationMinutes,
    manualBufferMinutes: effectiveManualBufferMinutes,
    displayedServiceDurationMinutes,
    displayedTravelDurationMinutes: chipDisplayedTravelDurationAvailable ? chipDisplayedTravelDurationMinutes : null,
    hereTimeAwareRoutingEnabled: effectiveHereTimeAwareRoutingEnabled,
  });
  const displayedTotalDurationLabel = formatFleetSegmentComparisonDurationLabel(displayedTotalDurationMinutes);
  const legacyDurationAvailable = Number.isFinite(legacyTravelDurationMinutes) && legacyTravelDurationMinutes >= 0;
  const legacyDurationLabel = legacyDurationAvailable
    ? formatFleetSegmentComparisonDurationLabel(legacyTravelDurationMinutes)
    : "—";
  const currentHereTotalDurationLabel = currentHereTrafficDurationAvailable
    ? formatFleetSegmentComparisonDurationLabel(currentHereTrafficDurationMinutes)
    : "—";
  const comparisonTotalDurationLabel = comparisonAvailable
    ? formatFleetSegmentComparisonDurationLabel(comparisonDurationMinutes)
    : "—";
  const comparisonDeltaReferenceMinutes = currentHereTrafficDurationAvailable
    ? currentHereTrafficDurationMinutes
    : currentDurationMinutes;
  const comparisonDeltaAvailable = comparisonAvailable
    && Number.isFinite(comparisonDeltaReferenceMinutes)
    && comparisonDeltaReferenceMinutes >= 0;
  const delta = comparisonAvailable
    ? buildFleetSegmentComparisonDelta(comparisonDeltaReferenceMinutes, comparisonDurationMinutes)
    : { deltaLabel: "", deltaPercentLabel: "" };
  const deltaSummary = delta.deltaLabel && delta.deltaPercentLabel
    ? `${delta.deltaLabel} (${delta.deltaPercentLabel})`
    : delta.deltaLabel || "—";
  const comparisonLabel = resolveFleetSegmentComparisonLabel();
  const resolvedComparisonDurationChips = comparisonDurationChips.map((chip) => {
    const chipKind = typeof chip?.kind === "string"
      ? chip.kind.trim()
      : typeof chip?.key === "string"
        ? chip.key.trim()
        : "";

    if (chipKind === "svc") {
      return {
        ...chip,
        label: formatFleetSequenceDurationChipLabel(displayedServiceChipMinutes ?? displayedServiceDurationMinutes),
      };
    }

    if (chipKind === "dur") {
      return {
        ...chip,
        hoverCardTitle: displayedTravelUsesHereTraffic
          ? "Temps HERE avec trafic"
          : legacyDurationVisibleWhenHereEnabled
            ? manualDurationLabel
            : chip?.hoverCardTitle,
        label: displayedTravelDurationChipLabel,
        title: displayedTravelUsesHereTraffic
          ? `Temps HERE avec trafic affiché : ${displayedTravelDurationChipLabel}`
          : resolveFleetSequenceDurationChipTitle(segment, displayedTravelDurationChipLabel, {
            legacyDurationVisibleWhenHereEnabled,
          }),
      };
    }

    return chip;
  });
  const durationRowLabel = resolveFleetSequenceDisplayedDurationRowLabel();
  const legacyDurationRowLabel = manualDurationLabel;
  const comparisonHeaderDetailLabel = displayedTravelUsesHereTraffic
    ? "Calcul durée: HERE actif"
    : legacyDurationVisibleWhenHereEnabled
      ? manualDurationLabel
      : null;
  const currentHereTotalRowLabel = "Temps HERE avec trafic";
  const comparisonTotalRowLabel = comparisonLabel;
  const showCurrentHereTotalRow = currentHereTrafficDurationAvailable;
  const showLegacyDurationRow = effectiveHereTimeAwareRoutingEnabled === true && legacyDurationVisibleWhenHereEnabled === true;

  return (
    <div className="grid gap-1 rounded-[8px] border border-[rgba(148,163,184,0.18)] bg-white/84 px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.12em] text-content-muted">Comparatif</div>
          {headerTrailingAccessory}
        </div>
        {comparisonHeaderDetailLabel ? (
          <div className="shrink-0 whitespace-nowrap text-[0.58rem] font-medium tracking-[0.08em] text-slate-500">
            {comparisonHeaderDetailLabel}
          </div>
        ) : null}
      </div>

      <div className="grid gap-0.75 text-[0.76rem] leading-5 text-slate-700">
        {resolvedComparisonDurationChips.length ? (
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3">
            <span className="text-slate-600">{durationRowLabel}</span>
            <RouteMetaChipRow
              chips={resolvedComparisonDurationChips}
              chipClassName="min-h-5 max-w-full shrink-0 gap-0.75 overflow-hidden px-1.5 py-px text-[0.58rem] leading-none font-semibold shadow-none"
              chipLabelClassName="whitespace-nowrap"
              className="min-w-0 justify-end"
              gridClassName={FLEET_COMPARISON_DURATION_CHIP_GRID_CLASS_NAME}
            />
            <span className="whitespace-nowrap font-semibold text-slate-900">{displayedTotalDurationLabel}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-600">{durationRowLabel}</span>
            <span className="font-semibold text-slate-900">{displayedTotalDurationLabel}</span>
          </div>
        )}
        {showCurrentHereTotalRow ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-600">{currentHereTotalRowLabel}</span>
            <span className="font-semibold text-slate-900">{currentHereTotalDurationLabel}</span>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3">
          <span className="text-slate-600">{comparisonTotalRowLabel}</span>
          <span className="font-semibold text-slate-900">
            {comparisonStatus === "loading"
              ? "…"
              : comparisonTotalDurationLabel}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-slate-600">Écart</span>
          <span className="font-semibold text-slate-900">{comparisonDeltaAvailable ? deltaSummary : "—"}</span>
        </div>
        {showLegacyDurationRow ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-slate-600">{legacyDurationRowLabel}</span>
            <span className="font-semibold text-slate-900">{legacyDurationLabel}</span>
          </div>
        ) : null}
      </div>
      {!comparisonAvailable ? (
        <p className="m-0 text-[0.7rem] leading-4 text-slate-500">
          {comparisonStatus === "loading"
            ? "Calcul du comparatif…"
            : resolveFleetSegmentComparisonErrorMessage(payload, "Comparatif indisponible pour ce segment.")}
        </p>
      ) : null}
    </div>
  );
}

function SegmentHoverCardContent({
  row,
  segment: sourceSegment,
  embedded = false,
  displayOverrides = null,
  hovercardContext = null,
  comparisonPayloadByKey = null,
  dispatchDate = "",
  manualBufferChipOverride = null,
  showContextualPartyInfo = true,
  showInlineCounterpartyByOrder = false,
  suppressManualBufferChip = false,
  sequenceOpeningVisibilityByItemKey = null,
  sequenceClosingVisibilityByItemKey = null,
  sequenceRenderItems = [],
  truckLabelById = null,
}) {
  const segment = React.useMemo(() => resolvePmSegmentContext(sourceSegment), [sourceSegment]);
  const terminalMergedComparisonSourceSegments = React.useMemo(() => {
    return resolveTerminalMergedComparisonSourceSegments(
      Array.isArray(row?.segments) ? row.segments : [],
      segment,
    );
  }, [row?.segments, segment]);
  const terminalMergedComparisonSegment = React.useMemo(() => {
    if (segment?.isTerminalRouteMerged !== true) {
      return null;
    }

    for (let segmentIndex = terminalMergedComparisonSourceSegments.length - 1; segmentIndex >= 0; segmentIndex -= 1) {
      const candidateSegment = terminalMergedComparisonSourceSegments[segmentIndex] || null;
      if (candidateSegment?.stopType === "depot_return") {
        return candidateSegment;
      }
    }

    return null;
  }, [segment?.isTerminalRouteMerged, terminalMergedComparisonSourceSegments]);
  const terminalMergedComparisonSummary = React.useMemo(() => {
    if (segment?.isTerminalRouteMerged !== true || !terminalMergedComparisonSegment) {
      return null;
    }

    return buildFleetGroupedComparisonSummary([terminalMergedComparisonSegment], {
      comparisonPayloadByKey,
      dispatchDate,
    });
  }, [comparisonPayloadByKey, dispatchDate, segment?.isTerminalRouteMerged, terminalMergedComparisonSegment]);
  const terminalMergedCurrentDurationMinutes = segment?.isTerminalRouteMerged === true
    ? resolveFleetSegmentCurrentTravelDurationMinutes(segment)
    : null;
  const terminalMergedComparisonPayloadOverride = React.useMemo(() => {
    if (segment?.isTerminalRouteMerged !== true || !terminalMergedComparisonSummary) {
      return null;
    }

    if (terminalMergedComparisonSummary.comparisonSegmentCount <= 0) {
      return null;
    }

    if (terminalMergedComparisonSummary.comparisonScopeCovered) {
      return {
        comparison_duration_minutes: terminalMergedComparisonSummary.comparisonDurationMinutes,
        current_duration_minutes: terminalMergedCurrentDurationMinutes,
        here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
        status: "available",
      };
    }

    return {
      comparison_duration_minutes: null,
      current_duration_minutes: terminalMergedCurrentDurationMinutes,
      here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
      message: "Comparatif indisponible pour ce segment.",
      status: "unavailable",
    };
  }, [
    hovercardContext?.here_time_aware_routing_enabled,
    segment?.isTerminalRouteMerged,
    terminalMergedComparisonSummary,
    terminalMergedCurrentDurationMinutes,
  ]);
  const legacyDurationVisibleWhenHereEnabled = hovercardContext?.fleet_segment_hovercard_legacy_duration_visible_when_here_enabled === true;
  const mergedMemberSegments = React.useMemo(() => {
    return Array.isArray(segment?.mergedMemberSegments) ? segment.mergedMemberSegments.filter(Boolean) : [];
  }, [segment?.mergedMemberSegments]);
  const isAspiratorMissionSegment = segment?.isAspiratorMissionMerged === true;
  const isMergedSameAddressSegment = !embedded
    && isMergedOperationalMissionSegment(segment)
    && mergedMemberSegments.length > 0;
  const mergedSameAddressOrdersHoverIcon = isMergedSameAddressSegment
    ? <GroupedSegmentOrdersHoverIcon memberSegments={mergedMemberSegments} />
    : null;
  const mergedSameAddressGroupItem = React.useMemo(() => {
    if (!isMergedSameAddressSegment) {
      return null;
    }

    const sameSiteGroupKey = typeof segment?.aspiratorMissionGroupKey === "string" && segment.aspiratorMissionGroupKey.trim()
      ? segment.aspiratorMissionGroupKey.trim()
      : typeof segment?.sameSiteGroupKey === "string" && segment.sameSiteGroupKey.trim()
      ? segment.sameSiteGroupKey.trim()
      : `${segment?.assignmentId || "assignment"}::${segment?.tripId || "trip"}::merged-same-address`;

    return {
      groupKey: sameSiteGroupKey,
      memberSegments: mergedMemberSegments,
    };
  }, [isMergedSameAddressSegment, mergedMemberSegments, segment?.aspiratorMissionGroupKey, segment?.assignmentId, segment?.sameSiteGroupKey, segment?.tripId]);
  const mergedSameAddressComparisonSummary = React.useMemo(() => {
    if (!isMergedSameAddressSegment) {
      return null;
    }

    const comparisonSegments = isAspiratorMissionSegment
      && Array.isArray(segment?.aspiratorMissionTravelSegments)
      ? segment.aspiratorMissionTravelSegments.filter(Boolean)
      : mergedMemberSegments;

    return buildFleetGroupedComparisonSummary(comparisonSegments, {
      comparisonPayloadByKey,
      dispatchDate,
      displayOverrides,
    });
  }, [comparisonPayloadByKey, dispatchDate, displayOverrides, isAspiratorMissionSegment, isMergedSameAddressSegment, mergedMemberSegments, segment?.aspiratorMissionTravelSegments]);
  const mergedSameAddressDurationChips = React.useMemo(() => {
    if (!mergedSameAddressGroupItem) {
      return [];
    }

    return buildSequenceGroupDurationChips(mergedSameAddressGroupItem, displayOverrides, {
      assignment: row?.assignment || null,
      hovercardContext,
    });
  }, [displayOverrides, hovercardContext, mergedSameAddressGroupItem, row?.assignment]);
  const mergedSameAddressServiceDurationMinutes = React.useMemo(() => {
    if (!isMergedSameAddressSegment) {
      return null;
    }

    if (isAspiratorMissionSegment) {
      return Number(segment?.serviceDurationMinutes) || 0;
    }

    return resolveSequenceGroupServiceDurationMinutes(mergedMemberSegments, {
      assignment: row?.assignment || null,
    });
  }, [isAspiratorMissionSegment, isMergedSameAddressSegment, mergedMemberSegments, row?.assignment, segment?.serviceDurationMinutes]);
  const mergedSameAddressManualBufferMinutes = React.useMemo(() => {
    if (!isMergedSameAddressSegment) {
      return null;
    }

    return isAspiratorMissionSegment
      ? 0
      : sumFleetSegmentDurationMinutes(mergedMemberSegments.map((memberSegment) => memberSegment?.manualBufferMinutes));
  }, [isAspiratorMissionSegment, isMergedSameAddressSegment, mergedMemberSegments]);
  const mergedSameAddressCurrentDurationMinutes = mergedSameAddressComparisonSummary?.currentDurationMinutes ?? null;
  const mergedSameAddressCurrentHereTrafficDurationMinutes = mergedSameAddressComparisonSummary?.currentHereTrafficDurationMinutes ?? null;
  const mergedSameAddressLegacyDurationMinutes = mergedSameAddressComparisonSummary?.legacyDurationMinutes ?? null;
  const mergedSameAddressComparisonPayloadOverride = React.useMemo(() => {
    if (!isMergedSameAddressSegment) {
      return null;
    }

    const currentDurationMinutes = Number.isFinite(mergedSameAddressCurrentDurationMinutes) && mergedSameAddressCurrentDurationMinutes >= 0
      ? mergedSameAddressCurrentDurationMinutes
      : 0;
    const comparisonSegmentCount = Number(mergedSameAddressComparisonSummary?.comparisonSegmentCount) || 0;

    if (comparisonSegmentCount > 0 && mergedSameAddressComparisonSummary?.comparisonScopeCovered) {
      return {
        comparison_duration_minutes: mergedSameAddressComparisonSummary.comparisonDurationMinutes,
        current_duration_minutes: currentDurationMinutes,
        here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
        status: "available",
      };
    }

    return {
      comparison_duration_minutes: null,
      current_duration_minutes: currentDurationMinutes,
      here_time_aware_routing_enabled: hovercardContext?.here_time_aware_routing_enabled === true,
      message: comparisonSegmentCount > 0
        ? "Comparatif indisponible pour certaines étapes du segment."
        : "Comparatif indisponible pour ce segment.",
      status: "unavailable",
    };
  }, [
    hovercardContext?.here_time_aware_routing_enabled,
    isMergedSameAddressSegment,
    mergedSameAddressComparisonSummary,
    mergedSameAddressCurrentDurationMinutes,
  ]);
  const mergedSameAddressQuantityLabel = React.useMemo(() => {
    if (!isMergedSameAddressSegment) {
      return "";
    }

    const totalQuantityTons = resolveMergedSameAddressQuantityTons(mergedMemberSegments);

    return Number.isFinite(totalQuantityTons) && totalQuantityTons > 0
      ? `${formatQuantityTons(totalQuantityTons)} t`
      : "";
  }, [isMergedSameAddressSegment, mergedMemberSegments]);
  const segmentHoverCardProductLabel = resolveSegmentHoverCardProductLabel(segment);
  const segmentChips = buildSameSiteSequenceHoverMetaChips(segment, displayOverrides, {
    fallbackIndex: Math.max((Number(segment?.globalSequence) || 1) - 1, 0),
    hideFollowerDurationChips: true,
  });
  const shouldPromoteProductChip = !segmentHoverCardProductLabel
    && typeof segment?.productCodeLabel === "string"
    && segment.productCodeLabel.trim();
  const compartmentChip = segmentChips.find((chip) => chip?.kind === "compartment" || chip?.key === "compartment") || null;
  const nonCompartmentSegmentChips = compartmentChip
    ? segmentChips.filter((chip) => chip !== compartmentChip)
    : segmentChips;
  const promotedProductChip = shouldPromoteProductChip
    ? (nonCompartmentSegmentChips.find((chip) => chip?.kind === "grain" || chip?.key === "grain") || null)
    : null;
  const segmentHoverFallbackIndex = Math.max((Number(segment?.globalSequence) || 1) - 1, 0);
  const segmentHoverDisplayDistanceLabel = getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, segmentHoverFallbackIndex);
  const segmentHoverDisplayTravelDuration = resolveSameSiteSequenceDisplayedTravelDuration(
    segment,
    displayOverrides,
    segmentHoverFallbackIndex,
    hovercardContext,
  );
  const preparedHoverCardMetaChips = nonCompartmentSegmentChips
    .filter((chip) => chip !== promotedProductChip)
    .map((chip) => {
      if (chip?.kind === "svc") {
        return buildSequenceSegmentServiceDurationChip(chip, segment, {
          assignment: row?.assignment || null,
          compactLabel: compactHoverCardFleetSequenceDurationLabel,
        });
      }

      if (chip?.kind !== "dur" && chip?.key !== "dur") {
        return chip;
      }

      const displayLabel = formatFleetSequenceDurationChipLabel(
        segmentHoverDisplayTravelDuration.minutes,
        { approximate: true },
      );
      const compactLabel = compactHoverCardFleetSequenceDurationLabel(displayLabel);

      return {
        ...chip,
        ...enrichRouteSequenceDurationChipMetadata(
          segment,
          segmentHoverDisplayDistanceLabel,
          segmentHoverDisplayTravelDuration.minutes,
          [],
          { showsHereTrafficDuration: segmentHoverDisplayTravelDuration.usesHereTraffic === true },
        ),
        label: compactLabel,
      };
    });
  const meunerieChip = buildFleetSequenceMeunerieChip(
    resolveFleetSequenceMeunerieExtraMinutes(segment),
    getFleetSequenceDisplayKey(segment, segmentHoverFallbackIndex),
  );
  const aspiratorTimeChip = buildFleetSequenceAspiratorTimeChip(
    resolveFleetSequenceAspiratorServiceMinutes(segment),
    getFleetSequenceDisplayKey(segment, segmentHoverFallbackIndex),
  );
  const manualBufferChip = suppressManualBufferChip
    ? null
    : manualBufferChipOverride || buildFleetSequenceManualBufferChip(
      segment,
      getFleetSequenceDisplayKey(segment, segmentHoverFallbackIndex),
    );
  const preparedHoverCardMetaChipsWithExtras = (() => {
    const serviceChips = preparedHoverCardMetaChips.filter((chip) => chip?.kind === "svc" || chip?.key === "svc");
    const travelChips = preparedHoverCardMetaChips.filter((chip) => chip?.kind === "dur" || chip?.key === "dur");
    const otherChips = preparedHoverCardMetaChips.filter((chip) => !serviceChips.includes(chip) && !travelChips.includes(chip));
    const extraChips = [
      meunerieChip && !preparedHoverCardMetaChips.some((chip) => chip?.kind === "meunerie" || chip?.key === "meunerie") ? meunerieChip : null,
      aspiratorTimeChip && !preparedHoverCardMetaChips.some((chip) => chip?.kind === "aspirator_time" || chip?.key === "aspirator_time") ? aspiratorTimeChip : null,
      manualBufferChip && !preparedHoverCardMetaChips.some((chip) => chip?.kind === "manual_buffer" || chip?.key === "manual_buffer") ? manualBufferChip : null,
    ].filter(Boolean);

    if (!extraChips.length) {
      return orderEmbeddedHoverCardDurationChips(preparedHoverCardMetaChips);
    }

    const manualBufferExtraChips = extraChips.filter((chip) => chip?.kind === "manual_buffer" || chip?.key === "manual_buffer");
    const otherExtraChips = extraChips.filter((chip) => !manualBufferExtraChips.includes(chip));

    return orderEmbeddedHoverCardDurationChips([...otherChips, ...serviceChips, ...otherExtraChips, ...travelChips, ...manualBufferExtraChips]);
  })();
  const hideInformationalLoadDurationMetrics = shouldHideInformationalLoadDurationMetrics(segment);
  const moveDurationChipsIntoComparison = !embedded
    && !hideInformationalLoadDurationMetrics
    && hovercardContext?.fleet_segment_hovercard_comparison_enabled === true;
  const comparisonDurationChips = moveDurationChipsIntoComparison
    ? preparedHoverCardMetaChipsWithExtras.filter(isFleetDurationChip)
    : [];
  const hoverCardMetaChips = moveDurationChipsIntoComparison
    ? preparedHoverCardMetaChipsWithExtras.filter((chip) => !isFleetDurationChip(chip))
    : preparedHoverCardMetaChipsWithExtras;
  const hideEmbeddedDurationChips = embedded && (
    hovercardContext?.fleet_segment_hovercard_comparison_enabled === true
    || hideInformationalLoadDurationMetrics
  );
  const displayedHoverCardMetaChips = [promotedProductChip, ...hoverCardMetaChips]
    .filter(Boolean)
    .filter((chip) => {
      if (segmentHoverCardProductLabel && (chip?.kind === "grain" || chip?.key === "grain")) {
        return false;
      }

      if (!embedded) {
        return true;
      }

      if (hideEmbeddedDurationChips && isFleetDurationChip(chip)) {
        return false;
      }

      if (chip?.kind === "dist" || chip?.key === "dist") {
        const distanceKm = parseSequenceDistanceKm(getRouteMetaChipDisplayLabel(chip));
        return distanceKm == null || distanceKm > 0;
      }

      return true;
    });
  const mergedSameAddressHoverCardMetaChips = React.useMemo(() => {
    if (!isMergedSameAddressSegment || !mergedSameAddressQuantityLabel) {
      return displayedHoverCardMetaChips;
    }

    const quantityHoverCardDetails = buildGroupedSegmentOrderHoverEntries(mergedMemberSegments).length > 1
      ? buildGroupedSegmentQuantityHoverCardDetails(mergedMemberSegments)
      : buildMergedSameAddressQuantityHoverCard(
        mergedSameAddressQuantityLabel,
        { isSingleCount: true },
      );

    let hasQuantityChip = false;
    const nextChips = displayedHoverCardMetaChips.map((chip) => {
      const chipKind = typeof chip?.kind === "string"
        ? chip.kind.trim()
        : typeof chip?.key === "string"
          ? chip.key.trim()
          : "";

      if (chipKind !== "qty") {
        return chip;
      }

      hasQuantityChip = true;
      return {
        ...chip,
        hoverCardDetails: quantityHoverCardDetails,
        hoverCardTitle: "Quantité",
        label: mergedSameAddressQuantityLabel,
        title: `Quantité aspirateur : ${mergedSameAddressQuantityLabel}`,
      };
    });

    if (hasQuantityChip) {
      return nextChips;
    }

    return [
      ...nextChips,
      {
        key: "merged-same-address:qty",
        kind: "qty",
        hoverCardDetails: quantityHoverCardDetails,
        hoverCardTitle: "Quantité",
        label: mergedSameAddressQuantityLabel,
        title: `Quantité aspirateur : ${mergedSameAddressQuantityLabel}`,
      },
    ];
  }, [displayedHoverCardMetaChips, isMergedSameAddressSegment, mergedSameAddressQuantityLabel]);
  const segmentHoverCardMetricsKeyPrefix = getFleetSequenceDisplayKey(segment, segmentHoverFallbackIndex);
  const segmentHoverCardMetricSourceChips = preparedHoverCardMetaChipsWithExtras.filter((chip) => {
    if (segmentHoverCardProductLabel && (chip?.kind === "grain" || chip?.key === "grain")) {
      return false;
    }

    if (chip === promotedProductChip) {
      return false;
    }

    return true;
  });
  const segmentHoverCardQuantityChip = segmentHoverCardMetricSourceChips.find((chip) => chip?.kind === "qty" || chip?.key === "qty") || null;
  const segmentHoverCardDistanceChip = (() => {
    const chip = segmentHoverCardMetricSourceChips.find((item) => item?.kind === "dist" || item?.key === "dist");
    if (!chip) {
      return null;
    }

    const distanceKm = parseSequenceDistanceKm(getRouteMetaChipDisplayLabel(chip));
    return distanceKm == null || distanceKm > 0 ? chip : null;
  })();
  const segmentHoverCardServiceChip = segmentHoverCardMetricSourceChips.find((chip) => chip?.kind === "svc" || chip?.key === "svc") || null;
  const segmentHoverCardTravelChip = segmentHoverCardMetricSourceChips.find((chip) => chip?.kind === "dur" || chip?.key === "dur") || null;
  const segmentHoverCardExtraDurationChip = hideInformationalLoadDurationMetrics
    ? null
    : buildGroupedSegmentExtraDurationChip(
      segmentHoverCardMetricSourceChips.filter((chip) => {
        const chipKind = typeof chip?.kind === "string" ? chip.kind.trim() : "";
        return chipKind === "meunerie" || chipKind === "aspirator_time" || chipKind === "manual_buffer";
      }),
      segmentHoverCardMetricsKeyPrefix,
    );
  const segmentHoverCardIncidentDurationChip = hideInformationalLoadDurationMetrics
    ? null
    : buildFleetSequenceTruckIncidentChip(
      resolveFleetRowTruckIncidents(row),
      resolveSegmentBusinessStartMinutes(segment),
      resolveSegmentBusinessEndMinutes(segment),
      segmentHoverCardMetricsKeyPrefix,
    );
  const segmentHoverCardPrimaryChips = [
    segmentHoverCardQuantityChip,
    segmentHoverCardDistanceChip,
    hideInformationalLoadDurationMetrics ? null : segmentHoverCardServiceChip,
    hideInformationalLoadDurationMetrics ? null : segmentHoverCardTravelChip,
  ].filter(Boolean);
  const segmentOrderLocationItems = buildDeliveryOrderLocationInfoItems(
    isMergedSameAddressSegment ? mergedMemberSegments : [segment],
  );
  const targetStartLabel = formatSegmentBusinessStartLabel(segment);
  const targetEndLabel = formatSegmentBusinessEndLabel(segment);
  const segmentOpeningTime = resolveFleetSegmentVisibleOpeningTime(
    segment,
    sequenceRenderItems,
    sequenceOpeningVisibilityByItemKey,
  );
  const segmentClosingTime = resolveFleetSegmentVisibleClosingTime(
    segment,
    sequenceRenderItems,
    sequenceClosingVisibilityByItemKey,
  );
  const isInformationalEndOfDayLoadSegment = Boolean(segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload);
  const endOfDayLoadHoverCardDetails = React.useMemo(() => {
    if (!isInformationalEndOfDayLoadSegment || embedded) {
      return null;
    }

    return resolveEndOfDayLoadHoverCardDetailsFromSegment(segment, {
      row,
      truckLabelById,
    });
  }, [embedded, isInformationalEndOfDayLoadSegment, row, segment, truckLabelById]);
  const endOfDayLoadVisualMode = resolveEndOfDayLoadVisualMode(segment);
  const hoverSurfaceClassName = isMergedSameAddressSegment
    ? getMergedSameAddressHoverSurfaceClassName()
    : getSegmentHoverSurfaceClassName(segment?.stopType, segment?.isInitialPreload, isInformationalEndOfDayLoadSegment, endOfDayLoadVisualMode);
  const containerClassName = embedded
    ? "grid gap-1.75 rounded-[8px] border border-[rgba(148,163,184,0.16)] bg-white/92 px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.78)]"
    : joinClasses([
      FLEET_SEGMENT_HOVER_CARD_SURFACE_CLASS_NAME,
      isInformationalEndOfDayLoadSegment ? "max-h-[calc(82dvh-4rem)] overflow-y-auto" : "",
      hoverSurfaceClassName,
    ]);
  const segmentSummaryPrimaryAddress = resolveSegmentPrimaryAddress(segment);
  const segmentSummaryDestinationAddress = resolveSegmentDestinationAddress(segment);
  const segmentSummaryShowDestinationAddressLine = !embedded && Boolean(segmentSummaryDestinationAddress);
  const segmentSummaryPrimaryMapsUrl = segmentSummaryShowDestinationAddressLine
    ? ""
    : resolveSegmentAddressMapsUrl(segment, segmentSummaryPrimaryAddress);
  const segmentSummaryDestinationMapsUrl = resolveSegmentAddressMapsUrl(
    segment,
    segmentSummaryDestinationAddress || segmentSummaryPrimaryAddress,
  );
  const segmentSummaryTrailingInfoItems = buildFleetSegmentHoverCardTrailingInfoItems({
    row,
    segment,
    showContextualPartyInfo,
    showInlineCounterpartyByOrder,
  });
  return (
    <div className={containerClassName} style={embedded ? undefined : { "--fleet-context-accent": getSegmentHoverCardAccentColor(segment?.stopType, segment?.isInitialPreload, isInformationalEndOfDayLoadSegment, endOfDayLoadVisualMode) }}>
      {!embedded && row?.truckLabel ? <span data-fleet-context-identity>{row.truckLabel}</span> : null}
      <div className={embedded ? "grid gap-1.25" : "grid min-w-0 gap-0.5"}>
        <div className="flex min-w-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            {isInformationalEndOfDayLoadSegment ? (
              <div className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-content-muted">Chargé en PM</div>
            ) : null}
            {segmentHoverCardProductLabel ? (
              <FleetProductPill
                label={segmentHoverCardProductLabel}
                maxLength={48}
                title={segmentHoverCardProductLabel}
              />
            ) : null}
            <FleetCompartmentFillPill
              percent={segment?.compartmentFillPercent}
              segments={isMergedSameAddressSegment ? mergedMemberSegments : [segment]}
              assignment={row?.assignment}
            />
            {!embedded ? <FleetSegmentOpeningHeaderPill openingTime={segmentOpeningTime} /> : null}
            {!embedded ? <FleetSegmentClosingHeaderPill closingTime={segmentClosingTime} /> : null}
          </div>
          {!embedded && (targetStartLabel || targetEndLabel) ? (
            <div className="flex shrink-0 items-center gap-1.25">
              <SegmentInfoPill
                className="border-[rgba(59,130,246,0.16)] bg-[rgba(239,246,255,0.96)]"
                label={getSegmentStartLabel(segment)}
                value={targetStartLabel}
              />
              <SegmentInfoPill
                className="border-[rgba(15,92,87,0.16)] bg-[rgba(238,247,245,0.96)]"
                label="Fin"
                value={targetEndLabel}
              />
            </div>
          ) : null}
        </div>
        {!embedded ? (
          <>
            <FleetSegmentHoverCardSummary
              title={resolveSegmentPrimaryTitle(segment)}
              trailingInfoItems={segmentSummaryTrailingInfoItems}
              primaryAddress={segmentSummaryPrimaryAddress}
              destinationAddress={segmentSummaryShowDestinationAddressLine ? segmentSummaryDestinationAddress : ""}
              primaryMapsUrl={segmentSummaryPrimaryMapsUrl}
              destinationMapsUrl={segmentSummaryDestinationMapsUrl}
              routeSummary={resolveSegmentRouteSummary(segment)}
            />
            <PmOrderLines lines={segment?.endOfDayLoadOrderLines} />
          </>
        ) : (
          <SegmentSummaryBlock
            row={row}
            segment={segment}
            showHeading={false}
            showAddress
            showContextualPartyInfo={showContextualPartyInfo}
            showInlineCounterpartyByOrder={showInlineCounterpartyByOrder}
            showRouteSummary
          />
        )}
      </div>

      {embedded ? (
        compartmentChip || mergedSameAddressHoverCardMetaChips.length ? (
          <div className="grid gap-1">
            {compartmentChip ? (
              <div className="w-full max-w-[15.9rem] min-w-0">
                <FleetSequenceCompartmentPill chip={compartmentChip} compartmentProfile={withVirtualTruckCompartment(row?.assignment?.truck_compartments, row?.assignment?.capacity_tons)} segments={isMergedSameAddressSegment ? mergedMemberSegments : [segment]} />
              </div>
            ) : null}
            {mergedSameAddressHoverCardMetaChips.length ? (
              <div className="min-w-0 max-w-full overflow-hidden">
                <RouteMetaChipRow
                  chips={mergedSameAddressHoverCardMetaChips}
                  chipColumnClassNameMap={EMBEDDED_SEGMENT_META_CHIP_COLUMN_CLASS_NAME_MAP}
                  chipLabelClassName="whitespace-nowrap"
                  className="gap-1 lg:flex-nowrap"
                  gridClassName={EMBEDDED_SEGMENT_META_GRID_CLASS_NAME}
                />
              </div>
            ) : null}
          </div>
        ) : null
      ) : (
        <FleetHoverCardMetricsRow
          assignment={row?.assignment}
          compartmentChip={compartmentChip}
          compartmentProfile={withVirtualTruckCompartment(row?.assignment?.truck_compartments, row?.assignment?.capacity_tons)}
          compartmentSegments={isMergedSameAddressSegment ? mergedMemberSegments : [segment]}
          extraDurationChip={segmentHoverCardExtraDurationChip}
          incidentDurationChip={segmentHoverCardIncidentDurationChip}
          orderLocationItems={segmentOrderLocationItems}
          ordersHoverIcon={mergedSameAddressOrdersHoverIcon}
          primaryChips={segmentHoverCardPrimaryChips}
        />
      )}

      {!embedded && !hideInformationalLoadDurationMetrics ? (
        <FleetSegmentComparisonSection
          segment={segment}
          comparisonPayloadOverride={isMergedSameAddressSegment ? mergedSameAddressComparisonPayloadOverride : terminalMergedComparisonPayloadOverride}
          currentDurationMinutesOverride={isMergedSameAddressSegment ? mergedSameAddressCurrentDurationMinutes : terminalMergedCurrentDurationMinutes}
          currentHereTrafficDurationMinutesOverride={isMergedSameAddressSegment ? mergedSameAddressCurrentHereTrafficDurationMinutes : terminalMergedComparisonSummary?.currentHereTrafficDurationMinutes ?? null}
          displayOverrides={displayOverrides}
          durationChips={[]}
          hovercardContext={hovercardContext}
          legacyDurationMinutesOverride={isMergedSameAddressSegment ? mergedSameAddressLegacyDurationMinutes : null}
          manualBufferMinutesOverride={isMergedSameAddressSegment ? mergedSameAddressManualBufferMinutes : null}
          serviceDurationMinutesOverride={isMergedSameAddressSegment ? mergedSameAddressServiceDurationMinutes : null}
        />
      ) : null}

      {endOfDayLoadHoverCardDetails ? (
        <div className="border-t border-[rgba(148,163,184,0.16)] pt-1.75">
          <EndOfDayLoadHoverCardDetails details={endOfDayLoadHoverCardDetails} layout="inline" />
        </div>
      ) : null}
    </div>
  );
}

// Chargé PM de plusieurs commandes : une ligne par commande, le client du lendemain en titre s'il est unique.
function PmOrderLines({ lines = [] }) {
  if (!Array.isArray(lines) || lines.length < 2) {
    return null;
  }
  const clientNames = [...new Set(lines.map((line) => line.clientName).filter(Boolean))];

  return (
    <div className="mt-1 grid gap-0.5 rounded-[4px] border border-[rgba(148,163,184,0.35)] bg-white px-2 py-1.5 text-[11px] leading-[1.4] text-content-copy" data-pm-order-lines="true">
      <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-content-muted">
        {clientNames.length === 1 ? `Commandes · ${clientNames[0]}` : "Commandes"}
      </div>
      {lines.map((line) => (
        <div key={line.orderId} className="grid grid-cols-[1fr_auto_auto] gap-2.5">
          <span className="min-w-0 truncate font-semibold">
            {clientNames.length > 1 && line.clientName ? `${line.orderId} · ${line.clientName}` : line.orderId}
          </span>
          <span className="text-content-muted">{line.compartmentLabel}</span>
          <span className="text-right font-bold">{line.quantityLabel}</span>
        </div>
      ))}
    </div>
  );
}

function SegmentInfoPill({ className = "", label = "", title = "", value = "" }) {
  if (!value) {
    return null;
  }

  return (
    <span
      className={joinClasses(["inline-flex min-w-0 items-center gap-1.5 rounded-full border border-[rgba(148,163,184,0.16)] bg-white px-2.5 py-1 text-[0.68rem] leading-none text-content-copy", className])}
      title={title || value}
    >
      {label ? <span className="shrink-0 font-extrabold uppercase tracking-[0.12em] text-content-faint">{label}</span> : null}
      <span className="truncate font-semibold text-text">{value}</span>
    </span>
  );
}

function FleetSegmentOpeningHeaderPill({ openingTime = "" }) {
  const resolvedOpeningTime = typeof openingTime === "string" ? openingTime.trim() : "";
  if (!resolvedOpeningTime) {
    return null;
  }

  return (
    <SegmentInfoPill
      className="border-[rgba(37,99,235,0.22)] bg-white"
      label="O"
      title={`Heure d'ouverture du site : ${resolvedOpeningTime}. Le chargement ne peut pas débuter avant cette heure.`}
      value={formatRouteSequenceHoursIndicatorTime(resolvedOpeningTime)}
    />
  );
}

function FleetSegmentClosingHeaderPill({ closingTime = "" }) {
  const resolvedClosingTime = typeof closingTime === "string" ? closingTime.trim() : "";
  if (!resolvedClosingTime) {
    return null;
  }

  return (
    <SegmentInfoPill
      className="border-[rgba(180,83,9,0.24)] bg-white"
      label="F"
      title={`Heure de fermeture du site : ${resolvedClosingTime}. Le chargement doit commencer au plus tard à cette heure.`}
      value={formatRouteSequenceHoursIndicatorTime(resolvedClosingTime)}
    />
  );
}

export {
  FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS,
  FLEET_TIMELINE_ZOOM_OPTIONS,
  DEFAULT_FLEET_TIMELINE_ZOOM_KEY,
  resolveEndOfDayLoadLabelOptions,
  formatSegmentBoundaryLabel,
  formatPreviousDaySequenceStartLabel,
  resolveSegmentBusinessStartMinutes,
  formatDisplayedSegmentEndLabel,
  formatSegmentBusinessEndLabel,
  getSegmentStartLabel,
  normalizeFleetSearch,
  getFleetSequenceDisplayKey,
  getSequenceOperationLabel,
  buildSequenceCounterpartyInfoItems,
  FleetSequenceCompartmentPill,
  withVirtualTruckCompartment,
  resolveFleetSequenceMeunerieExtraMinutes,
  resolveFleetSequenceAspiratorServiceMinutes,
  resolveFleetSequenceGroupOpeningTime,
  buildFleetSequenceMeunerieChip,
  buildFleetSequenceAspiratorTimeChip,
  buildFleetSequenceManualBufferChip,
  getSequenceGroupCompartmentLabel,
  parseSequenceDistanceKm,
  compactFleetSequenceDurationLabel,
  resolveSegmentDestinationAddress,
  isInformationalSequenceLoadItem,
  formatFleetSequenceDurationChipLabel,
  buildSequenceSegmentServiceDurationChip,
  buildSequenceGroupDurationChips,
  normalizeFleetSegmentDurationMinutes,
  GroupedSegmentOrdersHoverIcon,
  GroupedSegmentHoverCardContent,
  resolveGroupedSegmentDragCandidate,
  renderSegmentStatusIcon,
  TimelineSegmentStatusMarkers,
  TimelineSequenceBadge,
  TimelineProductLabelBadge,
  InitialPreloadFleetRibbon,
  EndOfDayLoadFleetRibbon,
  TimelinePriorityBadge,
  TimelineContextBadge,
  TimelineInlineIndicators,
  FleetTimelineSearchField,
  FleetTimelineLegend,
  FleetTimelineToolbar,
  FleetTimelineZoomSelect,
  FleetTimelineShortcutsHelp,
  FleetTimelineNowLine,
  FleetTimelineScale,
  FleetTruckFilterControls,
  FleetReassignmentDragNotice,
  normalizeTruckProductLabel,
  truncateTimelineCompactLabel,
  getCompactTimelineLabelMaxLength,
  getTruckProductTone,
  FleetProductPill,
  TruckHoverCardContent,
  renderSegmentHoverCardOrderTitleTrailing,
  SegmentSummaryBlock,
  fetchFleetSegmentComparison,
  fetchRouteAlternatives,
  normalizeFleetRouteSegmentIndexes,
  buildFleetTimelineSourceSegments,
  buildFleetTimelineBoundRows,
  SegmentHoverCardContent,
};
